import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Car, LayoutDashboard, Package, TrendingUp, TrendingDown, Truck, Users, User, Settings, LogOut, Plus, Search, Edit2, Trash2, X, Check, Copy, RefreshCw, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, FileText, FileSignature, Paperclip, Upload, Download, Image as ImageIcon, File as FileIcon, CheckCircle2, Clock, DollarSign, LayoutGrid, List, Trophy, Medal, Sparkles, Calendar, Headphones, UserPlus, AlertTriangle, Crown, Wrench, ShieldCheck, History, Key, ListChecks, HandCoins, Printer, Flame, Timer, Activity, Star, ArrowUpRight, ArrowDownRight, Award, BarChart3, Gem, Hourglass, ClipboardList, Gauge, Sun, Moon, CheckSquare, Scale, Calculator, Gavel } from "lucide-react";
import { toast } from "sonner";
import api, { formatCurrency, PUBLIC_API_BASE, vehiclePhoto, setActiveDealershipId, getActiveDealershipId } from "@/lib/api";
import { openPdfBlob } from "@/lib/openPdf";
import VehicleScheduleModal from "@/components/VehicleScheduleModal";
import InlineSchedule from "@/components/InlineSchedule";
import DealershipProfileSection from "@/components/DealershipProfileSection";
import OtherStoresSection from "@/components/OtherStoresSection";
import SignatureSection from "@/components/SignatureSection";
import WeeklyPayrollModal from "@/components/WeeklyPayrollModal";
import DownPaymentDialog from "@/components/DownPaymentDialog";
import PasswordsVault from "@/components/PasswordsVault";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LANG_OPTIONS } from "@/lib/i18n.jsx";
import PhotoUploader from "@/components/PhotoUploader";
import ExpenseManager from "@/components/ExpenseManager";
import WhatsAppButton from "@/components/WhatsAppButton";
import Financial from "@/pages/Financial";
import LeadsPage from "@/pages/LeadsPage";
import TasksPage from "@/pages/TasksPage";
import PostSales from "@/pages/PostSales";
import CreditApplications from "@/pages/CreditApplications";
import Receivables from "@/pages/Receivables";
import ReceivablesAlertWidget from "@/components/ReceivablesAlertWidget";
import LemonLawWarrantyModal from "@/components/LemonLawWarrantyModal";
import ContractPrintModal from "@/components/ContractPrintModal";
import NotificationBell from "@/components/NotificationBell";
import ActivityLogPanel from "@/components/ActivityLogPanel";
import TeamDiagnoseModal from "@/components/TeamDiagnoseModal";
import { Stethoscope, Mail } from "lucide-react";

// Opens a stored file via the backend proxy — Cloudinary blocks public PDF
// delivery (401), so downloads must go through /api/files/download.
async function openStoredFile(f, t) {
  const src = f.url || f.data_url;
  if (!src) return;
  if (!f.url) {
    // Legacy base64 files open directly.
    const w = window.open();
    if (w) w.document.write(`<iframe src="${src}" style="width:100%;height:100%;border:0"></iframe>`);
    return;
  }
  try {
    const res = await api.get("/files/download", {
      params: { url: f.url, name: f.name || "" },
      responseType: "blob",
    });
    const blobData = res.data;
    const blobUrl = URL.createObjectURL(blobData);
    const w2 = window.open(blobUrl, "_blank");
    if (!w2) {
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = f.name || "documento";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch {
    toast.error(t ? t("error_generic") : "Erro ao abrir o arquivo");
  }
}
import LemonLawPage from "@/pages/LemonLawPage";
import PaymentAuthorizationPage from "@/pages/PaymentAuthorizationPage";
import EmployeesSection from "@/components/EmployeesSection";
import { Menu as MenuIcon } from "lucide-react";
import VehicleHistoryModal from "@/components/VehicleHistoryModal";
import ChatWidget from "@/components/ChatWidget";
import InstallPrompt from "@/components/InstallPrompt";
import NameWithAvatar, { useTeamPhotos } from "@/components/NameWithAvatar";
import ImportInventoryPageModal from "@/components/ImportInventoryPageModal";
import RmvTaskList from "@/components/RmvTaskList";
import AuctionPage from "@/pages/AuctionPage";
import FinancingSimPage from "@/pages/FinancingSimPage";
import Avatar from "@/components/Avatar";
import FinanceSimModal from "@/components/FinanceSimModal";
import { uploadProfilePhoto } from "@/lib/uploadPhoto";

const STATUS_COLUMNS = [
  { id: "in_stock", color: "border-blue-500" },
  { id: "reserved", color: "border-warning" },
  { id: "sold", color: "border-success" },
];

// Finance partners (banks/lenders) we work with — shown when the user picks
// "Finance" as the payment method on a sold vehicle. Order kept exactly as
// requested by the dealership. "Outro" gives the user a fallback for any
// less-common lender that isn't in the list yet.
const FINANCE_BANKS = [
  "Westlake Financial",
  "Western Funding",
  "United Auto Credit",
  "Credit Acceptance Corporation",
  "Lendbuzz Inc.",
  "First Help Financial",
  "Shoreham Bank",
  "Capital One",
  "Carvant Auto Finance",
  "DCU",
  "Outro",
];

function StoreSwitcher({ stores, activeId }) {
  // Compact dropdown listing every dealership in the owner_group. Clicking a
  // non-active store swaps the localStorage `intercar_active_dealership` and
  // hard-reloads the page — cleanest way to nuke every cached list without
  // sprinkling invalidation across dozens of useEffects.
  const [open, setOpen] = useState(false);
  const active = stores.find((s) => s.id === activeId) || stores[0];

  const pick = (id) => {
    if (id === activeId) { setOpen(false); return; }
    setActiveDealershipId(id);
    window.location.reload();
  };

  return (
    <div className="relative mt-3" data-testid="store-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="store-switcher-btn"
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded border border-border bg-bg-elev hover:border-brand/50 transition-colors group"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
          <div className="min-w-0 text-left">
            <p className="text-[9px] uppercase tracking-widest text-text-secondary">Loja ativa</p>
            <p className="text-xs font-display font-bold truncate">{active?.name || "—"}</p>
          </div>
        </div>
        <ChevronDown size={14} className={`shrink-0 text-text-secondary transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 z-30 rounded border border-border bg-background shadow-lg overflow-hidden">
          {stores.map((s) => {
            const isActive = s.id === activeId;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => pick(s.id)}
                data-testid={`store-opt-${s.id}`}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-brand/5 transition-colors ${
                  isActive ? "bg-brand/10" : ""
                }`}
              >
                <div className="min-w-0">
                  <p className="text-xs font-display font-bold truncate">{s.name}</p>
                  {s.address && <p className="text-[10px] text-text-secondary truncate">{s.address}</p>}
                </div>
                {isActive && <Check size={14} className="shrink-0 text-brand" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StoreTopBar({ stores, activeId }) {
  // Prominent store pill fixed at the top-center of EVERY page. Shows the
  // active store in its signature color and opens a dropdown to switch —
  // available to the whole team (backend honors the header for all roles).
  const [open, setOpen] = useState(false);
  const idx = Math.max(0, stores.findIndex((s) => s.id === activeId));
  const active = stores[idx] || stores[0];
  const clrs = ["bg-brand text-white", "bg-cyan-500 text-white", "bg-purple-500 text-white", "bg-amber-500 text-black"];
  const dots = ["bg-brand", "bg-cyan-500", "bg-purple-500", "bg-amber-500"];
  const label = (s) => (s?.short_name || s?.name || "").trim();
  const pick = (id) => {
    if (id === activeId) { setOpen(false); return; }
    setActiveDealershipId(id);
    window.location.reload();
  };
  return (
    <div className="fixed left-1/2 -translate-x-1/2 top-16 lg:top-3 z-40" data-testid="store-topbar">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="store-topbar-btn"
        title="Clique pra trocar de loja"
        className={`${clrs[idx] || clrs[0]} flex items-center gap-2 pl-4 pr-3 py-2 rounded-full shadow-xl font-display font-black text-sm uppercase tracking-[0.18em] hover:opacity-90 transition-opacity`}
      >
        <Package size={15} strokeWidth={2.5} />
        {label(active) || "Loja"}
        <ChevronDown size={15} strokeWidth={3} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0" onClick={() => setOpen(false)} />
          <div data-testid="store-topbar-menu" className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 w-64 rounded border border-border bg-background shadow-2xl overflow-hidden">
            <p className="px-3 py-1.5 text-[9px] uppercase tracking-widest text-text-secondary border-b border-border">Trocar de loja</p>
            {stores.map((s, i) => {
              const isActive = s.id === activeId;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => pick(s.id)}
                  data-testid={`store-topbar-opt-${s.id}`}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-bg-elev transition-colors ${isActive ? "bg-brand/10" : ""}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dots[i] || dots[0]}`} />
                    <div className="min-w-0">
                      <p className="text-xs font-display font-bold uppercase truncate">{label(s)}</p>
                      {s.address && <p className="text-[10px] text-text-secondary truncate">{s.address}</p>}
                    </div>
                  </div>
                  {isActive && <Check size={14} className="shrink-0 text-brand" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function RegistrationPromptModal({ row, onClose, onSaved, onSkip }) {
  // Inline mini-modal that lets the owner fill in the missing Emplacamento (RMV)
  // amount in a single click — instead of opening the full VehicleForm just to
  // find that one field. (MA tax auto-suggestion removed at the owner's request.)
  const sold = Number(row?.sold_price) || 0;
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const n = Number(value);
    if (!(n > 0)) { toast.error("Digite um valor válido de RMV"); return; }
    setSaving(true);
    try {
      await api.put(`/vehicles/${row.vehicle_id}`, { registration_cost: n });
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="rmv-modal">
      <div className="bg-background border border-border max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-5 py-3 flex items-center justify-between">
          <p className="label-eyebrow text-brand inline-flex items-center gap-2">
            🧾 Emplacamento (RMV) faltando
          </p>
          <button type="button" onClick={onClose} className="text-text-secondary hover:text-primary"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded border border-border bg-bg-elev p-3">
            <p className="text-[10px] uppercase tracking-widest text-text-secondary">Veículo</p>
            <p className="text-sm font-display font-bold mt-1">
              {row?.year} {row?.make} {row?.model} · {row?.buyer_name || "—"}
            </p>
            <p className="text-[10px] text-text-secondary mt-1">Sold: {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(sold)}</p>
          </div>
          <div>
            <label className="label-eyebrow mb-1 block">Quanto foi gasto de emplacamento (US$)</label>
            <input
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              data-testid="rmv-input"
              className="w-full px-3 py-3 bg-bg-elev border border-border rounded text-lg font-display font-bold focus:outline-none focus:border-brand"
            />
          </div>
          <div className="rounded border border-brand/30 bg-brand/[0.04] p-3">
            <p className="text-xs text-text-secondary leading-relaxed">
              Assim que você salvar aqui, o carro já é <b>marcado como FUNDED</b> automaticamente.
              Se a comissão precisa sair antes do registro, use <b>"Fundar sem emplacamento"</b> —
              o carro fica com o aviso "falta emplacamento" até você preencher o valor.
            </p>
          </div>
        </div>
        <div className="border-t border-border p-4 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-border rounded hover:bg-bg-elev">Cancelar</button>
          <button type="button" onClick={() => { if (!saving && onSkip) { setSaving(true); onSkip(); } }} disabled={saving} data-testid="btn-fund-without-rmv"
            className="px-4 py-2 text-sm border border-warning/60 text-warning rounded font-display font-bold hover:bg-warning/10 disabled:opacity-50">
            Fundar sem emplacamento
          </button>
          <button type="button" onClick={save} disabled={saving} data-testid="btn-save-rmv"
            className="px-4 py-2 text-sm bg-brand text-white rounded font-display font-bold hover:bg-brand/90 disabled:opacity-50 inline-flex items-center gap-1">
            <Check size={14} /> {saving ? "Salvando..." : "Salvar e marcar FUNDED"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function TransferVehicleModal({ vehicle, stores, activeDealershipId, onClose, onTransferred }) {
  // Owner picks a destination store (any store in the group except the current one)
  // and gives a reason. The transfer is logged and the vehicle disappears from
  // the current store's inventory (moved to the destination on the backend).
  const otherStores = stores.filter((s) => s.id !== activeDealershipId);
  const [targetId, setTargetId] = useState(otherStores[0]?.id || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!targetId) { toast.error("Selecione a loja destino"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/vehicles/${vehicle.id}/transfer`, {
        target_dealership_id: targetId,
        reason: reason.trim(),
      });
      toast.success(`Carro transferido para ${r.data.to_dealership_name}`);
      onTransferred && onTransferred();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao transferir");
      setSaving(false);
    }
  };

  const targetStore = otherStores.find((s) => s.id === targetId);
  const veh = vehicle || {};
  const vehLabel = `${veh.year || ""} ${veh.make || ""} ${veh.model || ""} · ${veh.vin ? veh.vin.slice(-6) : ""}`.trim();

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="transfer-modal">
      <div className="bg-background border border-border max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-5 py-3 flex items-center justify-between sticky top-0 bg-background z-10">
          <p className="label-eyebrow text-brand inline-flex items-center gap-2"><Truck size={14} /> Transferir para outra loja</p>
          <button type="button" onClick={onClose} className="text-text-secondary hover:text-primary"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded border border-border bg-bg-elev p-3">
            <p className="text-[10px] uppercase tracking-widest text-text-secondary">Veículo</p>
            <p className="text-sm font-display font-bold mt-1">{vehLabel || "—"}</p>
          </div>
          <div>
            <p className="label-eyebrow mb-2">Loja destino</p>
            <div className="space-y-2">
              {otherStores.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setTargetId(s.id)}
                  data-testid={`transfer-opt-${s.id}`}
                  className={`w-full flex items-start justify-between gap-2 px-3 py-3 border rounded transition-all text-left ${
                    targetId === s.id ? "border-brand bg-brand/10 shadow-[0_0_0_2px_rgba(217,45,32,0.15)]" : "border-border hover:border-brand/50"
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-display font-bold truncate">{s.name}</p>
                    {s.address && <p className="text-[10px] text-text-secondary truncate mt-0.5">{s.address}</p>}
                  </div>
                  {targetId === s.id && <Check size={16} className="shrink-0 text-brand mt-1" />}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="label-eyebrow mb-1">Motivo <span className="text-text-secondary lowercase font-normal">(opcional, mas ajuda no histórico)</span></p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Ex: Cliente da Peabody quer ver esse carro"
              data-testid="transfer-reason"
              className="w-full px-3 py-2 bg-bg-elev border border-border rounded text-sm focus:outline-none focus:border-brand"
            />
          </div>
          {targetStore && (
            <div className="rounded border border-amber-500/30 bg-amber-500/[0.06] p-3">
              <p className="text-xs text-text-secondary leading-relaxed">
                <b className="text-primary">Atenção:</b> a partir da transferência, este carro aparece só no
                estoque de <b>{targetStore.name}</b>. Se for vendido lá, o recibo usa
                o endereço de {targetStore.name} — e a comissão/lucro vai pro fechamento dessa loja.
              </p>
            </div>
          )}
        </div>
        <div className="border-t border-border p-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-border rounded hover:bg-bg-elev">Cancelar</button>
          <button type="button" onClick={submit} disabled={saving || !targetId} data-testid="btn-do-transfer"
            className="px-4 py-2 text-sm bg-brand text-white rounded font-display font-bold hover:bg-brand/90 disabled:opacity-50 inline-flex items-center gap-1">
            <Truck size={14} /> {saving ? "Transferindo..." : "Confirmar transferência"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function SalespersonTransferModal({ sp, stores, onClose, onSaved }) {
  // Owner moves the salesperson's HOME store — no delete/recreate needed.
  const primaryId = sp?.dealership_id;
  const [targetId, setTargetId] = useState("");
  const [saving, setSaving] = useState(false);
  const currentStore = stores.find((s) => s.id === primaryId);

  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/salespeople/${sp.id}/transfer`, { target_dealership_id: targetId });
      toast.success(`${sp.name} transferido(a) para ${r.data.to_dealership_name}${r.data.login_moved ? " (login movido junto)" : ""}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao transferir vendedor");
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="sp-transfer-modal">
      <div className="bg-background border border-border max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-5 py-3 flex items-center justify-between">
          <p className="label-eyebrow text-brand inline-flex items-center gap-2"><Truck size={14} /> Transferir de loja · {sp?.name}</p>
          <button type="button" onClick={onClose} className="text-text-secondary hover:text-primary"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="rounded border border-brand/30 bg-brand/[0.04] p-3">
            <p className="text-xs text-text-secondary leading-relaxed">
              <b>{sp?.name}</b> sai de <b>{currentStore?.short_name || currentStore?.name || "—"}</b> e passa a trabalhar
              na loja escolhida — sem precisar criar de novo. O login dele(a) muda junto e o
              histórico de vendas continua na loja onde cada carro foi vendido.
            </p>
          </div>
          {stores.filter((s) => s.id !== primaryId).map((s) => (
            <label key={s.id} className={`flex items-center gap-3 border p-3 cursor-pointer transition-colors ${targetId === s.id ? "border-brand bg-brand/[0.06]" : "border-border hover:border-brand/50"}`}>
              <input
                type="radio"
                name="sp-transfer-target"
                data-testid={`sp-transfer-target-${s.id}`}
                checked={targetId === s.id}
                onChange={() => setTargetId(s.id)}
              />
              <span className="font-display font-bold text-sm uppercase">{s.short_name || s.name}</span>
            </label>
          ))}
        </div>
        <div className="border-t border-border p-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-border rounded hover:bg-bg-elev">Cancelar</button>
          <button type="button" onClick={submit} disabled={saving || !targetId} data-testid="btn-do-sp-transfer"
            className="px-4 py-2 text-sm bg-brand text-white rounded font-display font-bold hover:bg-brand/90 disabled:opacity-50 inline-flex items-center gap-1">
            <Truck size={14} /> {saving ? "Transferindo..." : "Confirmar transferência"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function SalespersonAccessModal({ sp, stores, onClose, onSaved }) {
  // Owner picks which extra stores this salesperson also works in. The primary
  // dealership_id (where they were created) is always kept — the checkbox for
  // that store is checked and disabled.
  const primaryId = sp?.dealership_id;
  const initialExtra = (sp?.dealership_access_ids || []).filter((d) => d !== primaryId);
  const [selected, setSelected] = useState(new Set(initialExtra));
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    if (id === primaryId) return; // primary is locked
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/salespeople/${sp.id}/access`, { dealership_ids: Array.from(selected) });
      toast.success(`Acesso multi-loja atualizado para ${sp.name}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar acesso");
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="access-modal">
      <div className="bg-background border border-border max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-5 py-3 flex items-center justify-between">
          <p className="label-eyebrow text-brand inline-flex items-center gap-2"><Package size={14} /> Acesso multi-loja · {sp?.name}</p>
          <button type="button" onClick={onClose} className="text-text-secondary hover:text-primary"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="rounded border border-brand/30 bg-brand/[0.04] p-3">
            <p className="text-xs text-text-secondary leading-relaxed">
              Marque as lojas onde <b>{sp?.name}</b> também trabalha. Ela(e) aparece no
              roster de cada loja marcada e pode ser selecionada(o) como vendedor(a)
              quando um carro é vendido lá. As vendas contam pra loja onde o carro
              foi vendido (não a loja principal do vendedor).
            </p>
          </div>
          <div className="space-y-2">
            {stores.map((s) => {
              const isPrimary = s.id === primaryId;
              const isChecked = isPrimary || selected.has(s.id);
              return (
                <label
                  key={s.id}
                  data-testid={`access-store-${s.id}`}
                  className={`flex items-center gap-3 p-3 border rounded cursor-pointer transition-all ${
                    isChecked ? "border-brand bg-brand/10" : "border-border hover:border-brand/50"
                  } ${isPrimary ? "opacity-90 cursor-not-allowed" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isPrimary}
                    onChange={() => toggle(s.id)}
                    className="w-4 h-4 accent-brand"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-display font-bold truncate">
                      {s.name}
                      {isPrimary && <span className="ml-2 text-[10px] uppercase tracking-widest text-brand font-normal">principal</span>}
                    </p>
                    {s.address && <p className="text-[10px] text-text-secondary truncate mt-0.5">{s.address}</p>}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
        <div className="border-t border-border p-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-border rounded hover:bg-bg-elev">Cancelar</button>
          <button type="button" onClick={save} disabled={saving} data-testid="btn-save-access"
            className="px-4 py-2 text-sm bg-brand text-white rounded font-display font-bold hover:bg-brand/90 disabled:opacity-50">
            {saving ? "Salvando..." : "Salvar acesso"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AddStoreModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Nome da loja é obrigatório"); return; }
    setSaving(true);
    try {
      const r = await api.post("/dealerships/create", { name: name.trim(), address, phone, email });
      toast.success(`Loja "${r.data.name}" criada!`);
      // Auto-switch to the new store so the owner can start populating it.
      setActiveDealershipId(r.data.id);
      onCreated && onCreated(r.data);
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar loja");
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="add-store-modal">
      <div className="bg-background border border-border max-w-md w-full my-8 sm:my-0 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-5 py-3 flex items-center justify-between sticky top-0 bg-background z-10">
          <p className="label-eyebrow text-primary">Nova loja no grupo</p>
          <button type="button" onClick={onClose} className="text-text-secondary hover:text-primary"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded border border-brand/30 bg-brand/[0.04] p-3">
            <p className="text-xs text-text-secondary leading-relaxed">
              Toda venda feita nesta loja usará o <b>endereço abaixo no recibo</b>.
              Estoque, financeiro e equipe começam vazios — 100% isolados da outra loja.
            </p>
          </div>
          <div>
            <p className="label-eyebrow mb-1">Nome da loja *</p>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Intercar Peabody"
              data-testid="input-store-name"
              className="w-full px-3 py-2 bg-bg-elev border border-border rounded text-sm focus:outline-none focus:border-brand" />
          </div>
          <div>
            <p className="label-eyebrow mb-1">Endereço completo</p>
            <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="98 Foster St, Peabody, MA 01960"
              data-testid="input-store-address"
              className="w-full px-3 py-2 bg-bg-elev border border-border rounded text-sm focus:outline-none focus:border-brand" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="label-eyebrow mb-1">Telefone</p>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(978) 555-0101"
                data-testid="input-store-phone"
                className="w-full px-3 py-2 bg-bg-elev border border-border rounded text-sm focus:outline-none focus:border-brand" />
            </div>
            <div>
              <p className="label-eyebrow mb-1">Email</p>
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="peabody@intercar.com"
                data-testid="input-store-email"
                className="w-full px-3 py-2 bg-bg-elev border border-border rounded text-sm focus:outline-none focus:border-brand" />
            </div>
          </div>
        </div>
        <div className="border-t border-border p-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-border rounded hover:bg-bg-elev">Cancelar</button>
          <button type="button" onClick={save} disabled={saving} data-testid="btn-save-store"
            className="px-4 py-2 text-sm bg-brand text-white rounded font-display font-bold hover:bg-brand/90 disabled:opacity-50">
            {saving ? "Criando..." : "Criar loja"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function StorePickerScreen({ stores, user, onLogout }) {
  // Full-screen "clock in" experience shown right after login when the user
  // belongs to a group with 2+ stores. Nothing else in the app is accessible
  // until they pick a store — that keeps the mental model clean.
  const [picking, setPicking] = useState(null); // id being clicked (for spinner)

  const pick = (id) => {
    setPicking(id);
    setActiveDealershipId(id);
    // Hard reload so every axios call from now on carries the X-Active-Dealership-Id
    // header + every useEffect starts fresh with the new store's data.
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-[#070707] text-white flex items-center justify-center p-6" data-testid="store-picker-screen">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <img src="/intercar-logo.png" alt="Intercar" className="h-20 mx-auto mb-6 object-contain" />
          <p className="text-[11px] uppercase tracking-[0.24em] text-white/50 mb-2">
            Olá {(user?.full_name || user?.email || "").split(" ")[0]}
          </p>
          <h1 className="font-display font-black text-4xl uppercase tracking-tight mb-3">
            Em qual loja você tá trabalhando hoje?
          </h1>
          <p className="text-sm text-white/60 max-w-lg mx-auto leading-relaxed">
            O financeiro, vendedores e gastos vão ser separados por loja.
            Estoque, leads e o painel principal ficam compartilhados nos dois lados.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="store-picker-grid">
          {stores.map((s, idx) => {
            const isPicking = picking === s.id;
            const accent = idx === 0 ? "hover:border-brand" : "hover:border-cyan-500";
            const dot = idx === 0 ? "bg-brand" : "bg-cyan-500";
            return (
              <button
                key={s.id}
                type="button"
                disabled={picking !== null}
                onClick={() => pick(s.id)}
                data-testid={`pick-store-${s.id}`}
                className={`group text-left border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] ${accent} p-6 transition-all duration-200 disabled:opacity-50 ${isPicking ? "border-brand" : ""}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className={`w-3 h-3 rounded-full ${dot} shadow-[0_0_12px_rgba(217,45,32,0.5)]`} />
                  <span className="text-[9px] uppercase tracking-[0.24em] text-white/40">
                    {isPicking ? "Entrando..." : "Clique pra entrar"}
                  </span>
                </div>
                <p className="text-[10px] uppercase tracking-widest text-white/50 mb-1">
                  {s.short_name || "Loja"}
                </p>
                <p className="font-display font-black text-2xl uppercase tracking-tight mb-2 group-hover:text-white transition-colors">
                  {s.name}
                </p>
                {s.address && (
                  <p className="text-xs text-white/50 leading-snug">{s.address}</p>
                )}
              </button>
            );
          })}
        </div>
        <div className="text-center mt-8">
          <button
            type="button"
            onClick={onLogout}
            data-testid="picker-logout"
            className="text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white/70 transition-colors"
          >
            ← Sair e voltar pro login
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { t, lang, setLang } = useI18n();
  const { user, dealership, logout, refreshDealership, refreshUser } = useAuth();
  const isSalesperson = user?.role === "salesperson";
  const isBdc = user?.role === "bdc";
  const isOwner = user?.role === "owner";
  const isManager = user?.role === "gerente";
  const isStaff = isOwner || isManager;

  // Per-user day/night toggle. "auto" = follow the dealership preference,
  // "light" forces the showroom theme, "dark" forces the dark default.
  // Persisted in localStorage scoped by user id so different staff members
  // can pick their own preference without affecting each other.
  const themeStorageKey = user?.id ? `theme_mode_${user.id}` : "theme_mode_anon";
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window === "undefined") return "auto";
    return localStorage.getItem(themeStorageKey) || "auto";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(themeStorageKey, themeMode);
    }
  }, [themeMode, themeStorageKey]);

  // Apply theme based on user choice (falls back to the dealership preference).
  useEffect(() => {
    const root = document.documentElement;
    const dealershipTheme = (dealership?.theme || "").toLowerCase();
    let resolved = dealershipTheme; // when themeMode === "auto"
    if (themeMode === "light") resolved = "showroom";
    else if (themeMode === "dark") resolved = "";
    if (resolved === "showroom") {
      root.setAttribute("data-theme", "showroom");
    } else {
      root.removeAttribute("data-theme");
    }
    return () => root.removeAttribute("data-theme");
  }, [dealership?.theme, themeMode]);
  const [tab, setTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer state
  // Desktop: when the user collapses the sidebar it shows only icons. Hover
  // expands it back temporarily. Persisted in localStorage so the choice sticks
  // across reloads.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "1"; } catch { return false; }
  });
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const sidebarExpanded = !sidebarCollapsed || sidebarHovered; // visual expanded state on desktop
  // Multi-store: dealerships in the owner's group + currently active id.
  const [myDealerships, setMyDealerships] = useState([]);
  const [showAddStore, setShowAddStore] = useState(false);
  const activeDealershipId = getActiveDealershipId() || dealership?.id;
  useEffect(() => {
    // Refetch whenever the dealership object refreshes (e.g. after saving the
    // profile/etiqueta in Configurações) so the top pill + switcher update
    // without needing a full page reload.
    api.get("/dealerships/mine").then((r) => setMyDealerships(r.data || [])).catch(() => {});
  }, [dealership]);
  const toggleSidebarCollapse = () => {
    setSidebarCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem("sidebar_collapsed", next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  };
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  // Estoque é compartilhado entre as lojas, mas as ESTEIRAS (vendas/entrega)
  // são isoladas: cada deal pertence à loja onde o carro está.
  const storeVehicles = (myDealerships.length > 1 && activeDealershipId)
    ? vehicles.filter((v) => v.dealership_id === activeDealershipId)
    : vehicles;
  const [deliveries, setDeliveries] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  // Group-wide list (Everett + Peabody) — owner assigns ANY seller to a sale
  const [groupSalespeople, setGroupSalespeople] = useState([]);
  const [editing, setEditing] = useState(null);
  const [simVehicle, setSimVehicle] = useState(null); // simulação de financiamento
  const [importOpen, setImportOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [historyVid, setHistoryVid] = useState(null);
  const [importPageOpen, setImportPageOpen] = useState(false);
  const [fpAlerts, setFpAlerts] = useState({ overdue: [], today: [], tomorrow: [], total: 0 });
  const [recAlerts, setRecAlerts] = useState({ alert_count: 0, total_remaining: 0 });
  const [team, setTeam] = useState([]);  // members list for delivery-schedule assignees
  const [scheduleAlertCount, setScheduleAlertCount] = useState(0);

  // Auto-load Floor Plan alerts for owner+gerente every 5 minutes
  useEffect(() => {
    if (!isStaff) return;
    let alive = true;
    const fetchAlerts = async () => {
      try {
        const r = await api.get("/floor-plans/alerts");
        if (alive) setFpAlerts(r.data || { overdue: [], today: [], tomorrow: [], total: 0 });
      } catch {/* silent */}
    };
    fetchAlerts();
    const id = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, [isStaff]);

  // Auto-load Receivables summary (overdue + due today badge) every 5 minutes
  // Also refetch when switching to overview/receivables tab so the user always
  // sees fresh numbers right after creating/paying installments.
  useEffect(() => {
    let alive = true;
    const userPerms = user?.permissions || [];
    const canAccessRec = isOwner || userPerms.includes("receivables");
    if (!canAccessRec) return;
    const fetchRec = async () => {
      try {
        const r = await api.get("/receivables/summary");
        if (alive) setRecAlerts(r.data || { alert_count: 0, total_remaining: 0 });
      } catch {/* silent */}
    };
    fetchRec();
    const id = setInterval(fetchRec, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, [isOwner, user, tab]);

  const userPerms = user?.permissions || [];
  // Lemon Law is a public reference page — every authenticated user can see it
  // (it's the law text, not company-sensitive data). Payment Authorization is
  // a form-generation tool used by sales floor; same logic applies.
  const PUBLIC_TABS = new Set(["lemon_law", "payment_auth", "financing"]);
  const canAccess = (tabId) => isOwner || PUBLIC_TABS.has(tabId) || userPerms.includes(tabId);
  const stuckCount = isStaff ? (deliveries || []).filter(v => v.stuck_alert).length : 0;
  const allTabs = [
    { id: "overview", label: t("dashboard"), icon: LayoutDashboard },
    { id: "inventory", label: t("inventory"), icon: Package },
    { id: "pipeline", label: t("pipeline"), icon: TrendingUp },
    { id: "delivery", label: t("nav_delivery") || t("delivery"), icon: Truck },
    { id: "leads", label: t("leads_title"), icon: Headphones },
    { id: "tasks", label: t("tasks") || "Tarefas", icon: CheckSquare },
    { id: "salespeople", label: t("salespeople"), icon: Users },
    { id: "financial", label: t("financial"), icon: DollarSign },
    { id: "post_sales", label: t("post_sales_tab"), icon: ShieldCheck },
    { id: "applications", label: t("applications_tab"), icon: FileText },
    { id: "receivables", label: t("receivables_tab"), icon: HandCoins },
    { id: "auction", label: "Leilão", icon: Gavel, rolesOnly: ["owner", "gerente"] },
    { id: "financing", label: "Financiamento", icon: Calculator },

    { id: "payment_auth", label: "Payment Auth", icon: FileSignature },
    { id: "passwords", label: "Senhas", icon: Key, rolesOnly: ["owner", "gerente", "salesperson"] },
    { id: "activity", label: t("activity_tab") || "Atividades", icon: History, rolesOnly: ["owner", "gerente"] },
    { id: "lemon_law", label: "Lemon Law", icon: Scale },
    { id: "settings", label: t("settings"), icon: Settings, ownerOnly: true },
  ];
  const tabs = allTabs.filter(tb => tb.ownerOnly ? isOwner : tb.rolesOnly ? tb.rolesOnly.includes(user?.role) : canAccess(tb.id));

  // Auto-pick a valid initial tab once we know what the user can access
  useEffect(() => {
    if (!user) return;
    const tabIds = tabs.map(tb => tb.id);
    if (tabIds.length && !tabIds.includes(tab)) setTab(tabIds[0]);
    // eslint-disable-next-line
  }, [user, userPerms.join(",")]);

  const reload = async () => {
    try {
      // Build requests dynamically based on what user can access
      const calls = [];
      const labels = [];
      if (canAccess("overview")) { calls.push(api.get("/stats")); labels.push("stats"); }
      if (canAccess("inventory")) { calls.push(api.get("/vehicles", { params: { search: search || undefined } })); labels.push("vehicles"); }
      if (canAccess("delivery")) { calls.push(api.get("/delivery")); labels.push("deliveries"); }
      // Salespeople list is needed for leads form too
      if (canAccess("salespeople") || canAccess("leads")) { calls.push(api.get("/salespeople")); labels.push("salespeople"); }
      if (canAccess("salespeople") || canAccess("leads")) { calls.push(api.get("/salespeople", { params: { scope: "group" } }).catch(() => ({ data: [] }))); labels.push("groupSalespeople"); }
      // Team members (for delivery-schedule assignees) — anyone with delivery_schedule perm
      if (canAccess("delivery") || canAccess("delivery_schedule") || isOwner) { calls.push(api.get("/team").catch(() => ({ data: { members: [] } }))); labels.push("team"); }
      // Schedule alerts badge (for staff + yard/salesperson with delivery_schedule perm)
      if (canAccess("delivery") || isOwner) { calls.push(api.get("/delivery-schedules/alerts").catch(() => ({ data: { count: 0 } }))); labels.push("scheduleAlerts"); }
      const results = await Promise.allSettled(calls);
      results.forEach((r, i) => {
        if (r.status === "fulfilled") {
          if (labels[i] === "stats") setStats(r.value.data);
          else if (labels[i] === "vehicles") setVehicles(r.value.data);
          else if (labels[i] === "deliveries") setDeliveries(r.value.data);
          else if (labels[i] === "salespeople") setSalespeople(r.value.data);
          else if (labels[i] === "groupSalespeople") setGroupSalespeople(Array.isArray(r.value.data) ? r.value.data : []);
          else if (labels[i] === "team") setTeam(r.value.data?.members || []);
          else if (labels[i] === "scheduleAlerts") setScheduleAlertCount(r.value.data?.count || 0);
        }
      });
    } catch { toast.error(t("error_generic")); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [search]);

  const onDelete = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/vehicles/${id}`); toast.success(t("saved")); reload(); }
    catch { toast.error(t("error_generic")); }
  };

  const updateStatus = async (id, status, extra = {}) => {
    try {
      await api.put(`/vehicles/${id}`, { status, ...extra });
      toast.success(t("saved"));
      reload();
    } catch { toast.error(t("error_generic")); }
  };

  // Multi-store login: force the user to pick a store before letting them in.
  // Only prompts when (a) the group has more than one store AND (b) no active
  // store is set in localStorage yet (fresh login). Clock-in behaviour.
  const needsStorePick = myDealerships.length > 1 && !getActiveDealershipId();

  // Carros fundados sem emplacamento (RMV) — lista do Painel + aviso no Financeiro.
  const rmvPendingList = (user?.role === "owner" || user?.role === "gerente")
    ? vehicles.filter((v) => v.funded && !(Number(v.registration_cost) > 0)).map((v) => {
        const idx = myDealerships.length >= 2 ? myDealerships.findIndex((s) => s.id === v.dealership_id) : -1;
        const clrs = ["bg-brand text-white", "bg-cyan-500 text-white", "bg-purple-500 text-white", "bg-amber-500 text-black"];
        return { ...v, store_badge: idx >= 0 ? { label: (myDealerships[idx].short_name || myDealerships[idx].name || "").trim(), clr: clrs[idx] || clrs[0] } : null };
      })
    : null;
  if (needsStorePick) {
    return <StorePickerScreen stores={myDealerships} user={user} onLogout={logout} />;
  }

  return (
    <div data-testid="app-shell" className="min-h-screen text-white flex">
      {/* Big always-visible store pill at the top of every page */}
      {myDealerships.length > 1 && (
        <StoreTopBar stores={myDealerships} activeId={activeDealershipId} />
      )}
      {/* Unified notification bell — desktop (the mobile one lives in the topbar) */}
      <div className="hidden lg:block fixed top-6 right-5 z-40">
        <NotificationBell onNavigate={setTab} />
      </div>
      {/* Mobile top bar — hidden on desktop */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-14 bg-background/95 backdrop-blur-sm border-b border-border z-30 flex items-center justify-between px-3">
        <div className="flex items-center gap-2 min-w-0">
          <img src={dealership?.logo_url || "/intercar-logo.png"} alt="" className="w-8 h-8 object-contain" />
          <p className="font-display font-black uppercase text-sm truncate">{dealership?.name || "Intercar"}</p>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell onNavigate={(t2) => { setTab(t2); setSidebarOpen(false); }} />
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
            className="p-2 border border-border hover:border-primary"
            aria-label="Menu"
          >
            <MenuIcon size={20} />
          </button>
        </div>
      </div>

      {/* Mobile backdrop when sidebar is open */}
      {sidebarOpen && (
        <div
          data-testid="mobile-backdrop"
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside
        data-testid="sidebar"
        onMouseEnter={() => sidebarCollapsed && setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
        className={`group/sidebar border-r border-border min-h-screen flex flex-col bg-background backdrop-blur-sm
          fixed lg:relative inset-y-0 left-0 z-50 transition-[width,transform] duration-200 lg:flex-shrink-0
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
          ${sidebarExpanded ? "w-64" : "w-16"}`}
      >
        {/* Desktop-only collapse toggle (the arrow) */}
        <button
          type="button"
          data-testid="sidebar-collapse-toggle"
          onClick={toggleSidebarCollapse}
          title={sidebarCollapsed ? "Expandir menu" : "Recolher menu"}
          className="hidden lg:flex absolute top-6 -right-3 z-10 w-6 h-6 items-center justify-center bg-background border border-border hover:border-primary hover:text-primary text-text-secondary transition-colors"
        >
          {sidebarCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>

        <div className={`border-b border-border bg-stripes-overlay ${sidebarExpanded ? "p-6" : "p-3"}`}>
          <div className={`flex items-center gap-3 ${sidebarExpanded ? "" : "justify-center"}`}>
            <div className={`relative shrink-0 ${sidebarExpanded ? "w-12 h-12" : "w-10 h-10"}`}>
              <div className="absolute inset-0 bg-primary/30 blur-xl rounded-full" />
              <img
                src={dealership?.logo_url || "/intercar-logo.png"}
                alt={dealership?.name || "Logo"}
                className="relative w-full h-full object-contain drop-shadow-lg"
              />
            </div>
            {sidebarExpanded && (
              <div className="min-w-0">
                <p className="font-display font-black uppercase text-sm truncate tracking-tight">{dealership?.name || "..."}</p>
                <p className="label-eyebrow text-[10px] truncate">{user?.email}</p>
              </div>
            )}
          </div>
          {/* Multi-store switcher — every team member with 2+ stores sees it. */}
          {sidebarExpanded && myDealerships.length > 1 && (
            <StoreSwitcher stores={myDealerships} activeId={activeDealershipId} />
          )}
          {/* Prompt to add a second store — only owners, only when solo store. */}
          {sidebarExpanded && user?.role === "owner" && myDealerships.length === 1 && (
            <button
              type="button"
              onClick={() => setShowAddStore(true)}
              data-testid="btn-add-store"
              className="mt-3 w-full text-[10px] uppercase tracking-widest text-text-secondary hover:text-brand transition-colors flex items-center gap-1 border border-dashed border-border hover:border-brand/50 rounded px-2 py-1.5"
            >
              <Plus size={12} /> Adicionar loja
            </button>
          )}
        </div>

        {/* Logged-in user profile chip with editable avatar — only when expanded */}
        {sidebarExpanded && <UserProfileChip user={user} t={t} onPhotoChanged={refreshUser} />}

        <nav className={`flex-1 space-y-1 ${sidebarExpanded ? "p-3" : "p-2"}`}>
          {tabs.map((tb) => {
            const showStuckBadge = tb.id === "delivery" && isStaff && stuckCount > 0;
            const showFpBadge = tb.id === "financial" && isStaff && fpAlerts.total > 0;
            const showRecBadge = tb.id === "receivables" && recAlerts.alert_count > 0;
            const badgeCount = showStuckBadge ? stuckCount : showFpBadge ? fpAlerts.total : showRecBadge ? recAlerts.alert_count : 0;
            return (
              <button
                key={tb.id}
                data-testid={`nav-${tb.id}`}
                onClick={() => { setTab(tb.id); setSidebarOpen(false); }}
                title={tb.label}
                className={`w-full flex items-center font-display uppercase tracking-wider font-semibold transition-colors relative ${
                  sidebarExpanded ? "gap-3 px-4 py-3 text-sm" : "justify-center px-2 py-3 text-sm"
                } ${
                  tab === tb.id ? "bg-primary text-white" : "text-text-secondary hover:bg-surface hover:text-white"
                }`}
              >
                <span className="relative shrink-0">
                  <tb.icon size={16} />
                  {/* Mini badge over the icon when collapsed */}
                  {!sidebarExpanded && badgeCount > 0 && (
                    <span
                      data-testid={`nav-${tb.id}-mini-badge`}
                      className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 px-1 rounded-full bg-primary text-white text-[8px] font-black flex items-center justify-center shadow-[0_0_8px_rgba(217,45,32,0.6)]"
                    >
                      {badgeCount > 9 ? "9+" : badgeCount}
                    </span>
                  )}
                </span>
                {sidebarExpanded && <span className="flex-1 text-left whitespace-nowrap overflow-hidden">{tb.label}</span>}
                {sidebarExpanded && showStuckBadge && (
                  <span
                    data-testid="nav-delivery-stuck-badge"
                    className="min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center shadow-[0_0_10px_rgba(217,45,32,0.6)]"
                    title={`${stuckCount} ${stuckCount === 1 ? "carro" : "carros"}`}
                  >
                    {stuckCount}
                  </span>
                )}
                {sidebarExpanded && showFpBadge && (
                  <span
                    data-testid="nav-financial-fp-badge"
                    className="min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center shadow-[0_0_10px_rgba(217,45,32,0.6)] animate-pulse"
                    title={`${fpAlerts.total} pagamento(s) Floor Plan`}
                  >
                    {fpAlerts.total}
                  </span>
                )}
                {sidebarExpanded && showRecBadge && (
                  <span
                    data-testid="nav-receivables-badge"
                    className="min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center shadow-[0_0_10px_rgba(217,45,32,0.6)] animate-pulse"
                    title={`${recAlerts.alert_count} parcela(s) atrasada(s) ou vencendo hoje`}
                  >
                    {recAlerts.alert_count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className={`border-t border-border space-y-3 ${sidebarExpanded ? "p-3" : "p-2"}`}>
          {/* Day/Night theme toggle — overrides the dealership preference per user */}
          <button
            data-testid="theme-toggle"
            onClick={() => {
              setThemeMode((prev) => {
                // 3-state cycle: auto → light → dark → auto
                if (prev === "auto") return "light";
                if (prev === "light") return "dark";
                return "auto";
              });
            }}
            title={
              themeMode === "auto"
                ? (t("theme_auto") || "Automático (segue a loja)")
                : themeMode === "light"
                  ? (t("theme_day") || "Modo dia (claro)")
                  : (t("theme_night") || "Modo noite (escuro)")
            }
            className={`w-full flex items-center text-xs text-text-secondary hover:text-primary transition-colors ${
              sidebarExpanded ? "gap-2 px-4 py-2" : "justify-center px-2 py-2"
            }`}
          >
            {themeMode === "light" ? (
              <Sun size={14} className="text-warning" />
            ) : themeMode === "dark" ? (
              <Moon size={14} className="text-info" />
            ) : (
              <Sun size={14} className="opacity-60" />
            )}
            {sidebarExpanded && (
              <span className="font-display font-bold uppercase tracking-widest">
                {themeMode === "auto"
                  ? (t("theme_auto_short") || "Auto")
                  : themeMode === "light"
                    ? (t("theme_day_short") || "Dia")
                    : (t("theme_night_short") || "Noite")}
              </span>
            )}
          </button>
          {sidebarExpanded && (
            <div className="flex gap-1" data-testid="lang-switcher-shell">
              {LANG_OPTIONS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`shell-lang-${l.code}`}
                  className={`flex-1 h-8 text-[10px] font-display font-bold uppercase border transition-colors ${
                    lang === l.code ? "border-primary text-primary" : "border-border text-text-secondary hover:text-white"
                  }`}
                >
                  {l.flag}
                </button>
              ))}
            </div>
          )}
          <button
            data-testid="logout-btn"
            onClick={logout}
            title={t("sign_out")}
            className={`w-full flex items-center text-xs text-text-secondary hover:text-primary transition-colors ${
              sidebarExpanded ? "gap-2 px-4 py-2" : "justify-center px-2 py-2"
            }`}
          >
            <LogOut size={14} /> {sidebarExpanded && t("sign_out")}
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 p-4 lg:p-8 overflow-auto pt-20 lg:pt-8 min-w-0">
        {tab === "overview" && canAccess("overview") && <Overview stats={stats} dealership={dealership} t={t} isSalesperson={isSalesperson} isBdc={isBdc} fpAlerts={isStaff ? fpAlerts : null} recAlerts={canAccess("receivables") ? recAlerts : null} onGoToFinancial={() => setTab("financial")} onGoToReceivables={() => setTab("receivables")} myDealerships={myDealerships} />}
        {tab === "overview" && rmvPendingList && rmvPendingList.length > 0 && (
          <RmvTaskList cars={rmvPendingList} team={team} onFix={(v) => setEditing(v)} />
        )}
        {tab === "inventory" && canAccess("inventory") && (
          <Inventory
            vehicles={vehicles} t={t} search={search} setSearch={setSearch} isSalesperson={isSalesperson}
            onAdd={() => setEditing("new")} onImport={() => setImportOpen(true)} onImportPage={isStaff ? () => setImportPageOpen(true) : null}
            onEdit={(v) => setEditing(v)} onDelete={onDelete}
            onHistory={setHistoryVid}
            onSimulate={setSimVehicle}
            myDealerships={myDealerships}
            canTransfer={user?.role === "owner" || user?.role === "gerente"}
            onTransferred={reload}
          />
        )}
        {tab === "inventory" && canAccess("inventory") && simVehicle && (
          <FinanceSimModal vehicle={simVehicle} onClose={() => setSimVehicle(null)} />
        )}
        {tab === "pipeline" && canAccess("pipeline") && <Pipeline vehicles={storeVehicles} t={t} onMove={updateStatus} onEdit={(v) => setEditing(v)} onHistory={setHistoryVid} />}
        {tab === "delivery" && canAccess("delivery") && <Delivery deliveries={deliveries} vehicles={storeVehicles} team={team} currentUser={user} scheduleAlertCount={scheduleAlertCount} salespeople={salespeople} t={t} onReload={reload} isStaff={isStaff} onHistory={setHistoryVid} />}
        {tab === "leads" && canAccess("leads") && <LeadsPage t={t} role={user?.role || "owner"} currentSpId={user?.salesperson_id || ""} salespeople={salespeople} onOpenVehicle={canAccess("inventory") ? (vid) => {
          const v = vehicles.find((x) => x.id === vid);
          if (v) { setTab("inventory"); setEditing(v); }
          else toast.error("Esse carro não está mais no estoque");
        } : null} />}
        {tab === "auction" && (user?.role === "owner" || user?.role === "gerente") && <AuctionPage t={t} />}
        {tab === "financing" && <FinancingSimPage />}
        {tab === "tasks" && canAccess("tasks") && <TasksPage />}
        {tab === "salespeople" && canAccess("salespeople") && <SalespeopleTab salespeople={salespeople} allSalespeople={groupSalespeople.length ? groupSalespeople : salespeople} t={t} onReload={reload} isSalesperson={isSalesperson} currentSpId={user?.salesperson_id || ""} myDealerships={myDealerships} activeDealershipId={activeDealershipId} />}
        {tab === "financial" && canAccess("financial") && <Financial t={t} fpAlerts={isStaff ? fpAlerts : null} isOwner={isStaff} isTrueOwner={user?.role === "owner"} stores={myDealerships} activeStoreId={activeDealershipId} showStoresComparison={user?.role === "owner" && myDealerships.length > 1} rmvPending={rmvPendingList} onFixRmv={(v) => setEditing(v)} />}
        {tab === "post_sales" && canAccess("post_sales") && <PostSales t={t} />}
        {tab === "applications" && canAccess("applications") && <CreditApplications />}
        {tab === "receivables" && canAccess("receivables") && <Receivables t={t} />}

        {tab === "payment_auth" && <PaymentAuthorizationPage t={t} />}
        {tab === "passwords" && ["owner", "gerente", "salesperson"].includes(user?.role) && <PasswordsVault user={user} />}
        {tab === "activity" && ["owner", "gerente"].includes(user?.role) && <ActivityLogPanel t={t} />}
        {tab === "lemon_law" && <LemonLawPage />}
        {tab === "settings" && isOwner && <SettingsTab dealership={dealership} t={t} onRefresh={refreshDealership} />}

        {editing && (
          <VehicleForm t={t} vehicle={editing === "new" || (editing && editing.__prefill) ? null : editing} prefill={editing && editing.__prefill ? editing : null} salespeople={groupSalespeople.length ? groupSalespeople : salespeople} isSalesperson={isSalesperson} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} myDealerships={myDealerships} activeDealershipId={activeDealershipId} />
        )}

        {importOpen && (
          <ImportUrlModal
            t={t}
            onClose={() => setImportOpen(false)}
            onImported={(data) => {
              setImportOpen(false);
              setEditing({ __prefill: true, ...data });
            }}
          />
        )}
      </main>
      {historyVid && <VehicleHistoryModal vehicleId={historyVid} onClose={() => setHistoryVid(null)} />}
      {importPageOpen && <ImportInventoryPageModal t={t} onClose={() => setImportPageOpen(false)} onImported={() => reload()} />}
      {showAddStore && <AddStoreModal onClose={() => setShowAddStore(false)} />}
      <ChatWidget />
      <InstallPrompt />
      <ReceivablesAlertWidget t={t} role={user?.role} onGoToReceivables={() => setTab("receivables")} />
    </div>
  );
}

function UserProfileChip({ user, t, onPhotoChanged }) {
  const [uploading, setUploading] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  if (!user) return null;
  const onPick = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { photo_url, photo_public_id } = await uploadProfilePhoto(file);
      await api.put("/me/photo", { photo_url, photo_public_id });
      toast.success(t("saved"));
      onPhotoChanged && onPhotoChanged();
    } catch (err) {
      toast.error(err.message || t("error_generic"));
    } finally { setUploading(false); }
  };
  const roleLabel = user.role === "owner"
    ? t("owner_role")
    : user.role === "bdc"
    ? "BDC"
    : user.role === "gerente"
    ? t("manager")
    : user.role === "geral"
    ? t("general_role")
    : user.role === "fi"
    ? t("fi_role")
    : t("salesperson");
  return (
    <div className="px-4 py-3 border-b border-border flex items-center gap-3">
      <label className="relative cursor-pointer group" title={t("change_photo")}>
        <Avatar src={user.photo_url} name={user.full_name || user.email} size="lg" testid="sidebar-avatar" />
        <div className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          {uploading ? <span className="text-[10px] text-white">...</span> : <Upload size={14} className="text-white" />}
        </div>
        <input data-testid="sidebar-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} disabled={uploading} />
      </label>
      <div className="min-w-0 flex-1">
        <p className="font-display font-bold text-sm truncate">{user.full_name || user.email}</p>
        <p className="text-[10px] uppercase tracking-wider text-text-secondary">{roleLabel}</p>
        <button
          type="button"
          data-testid="open-change-password"
          onClick={() => setPwOpen(true)}
          className="text-[10px] uppercase tracking-wider text-text-secondary hover:text-primary inline-flex items-center gap-1 mt-0.5 transition-colors"
        >
          <Key size={9} /> {t("change_password_link")}
        </button>
      </div>
      {pwOpen && <ChangePasswordModal t={t} onClose={() => setPwOpen(false)} />}
    </div>
  );
}

function ChangePasswordModal({ t, onClose }) {
  const [current_password, setCurrent] = useState("");
  const [new_password, setNew] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (new_password !== confirm) {
      toast.error(t("change_password_mismatch"));
      return;
    }
    if (new_password.length < 6) {
      toast.error(t("change_password_too_short"));
      return;
    }
    setSaving(true);
    try {
      await api.post("/me/change-password", { current_password, new_password });
      toast.success(t("change_password_success"));
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-start justify-center overflow-auto py-12 px-4" data-testid="change-password-modal">
      <form onSubmit={submit} className="bg-background border border-border w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl uppercase tracking-tight inline-flex items-center gap-2">
            <Key size={18} /> {t("change_password_title")}
          </h2>
          <button type="button" onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>
        <p className="text-sm text-text-secondary">{t("change_password_intro")}</p>

        <div>
          <label className="label-eyebrow block mb-2">{t("change_password_current")}</label>
          <input
            data-testid="cp-current"
            required
            type={show ? "text" : "password"}
            value={current_password}
            onChange={(e) => setCurrent(e.target.value)}
            className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">{t("change_password_new")}</label>
          <input
            data-testid="cp-new"
            required
            type={show ? "text" : "password"}
            minLength={6}
            value={new_password}
            onChange={(e) => setNew(e.target.value)}
            className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">{t("change_password_confirm")}</label>
          <input
            data-testid="cp-confirm"
            required
            type={show ? "text" : "password"}
            minLength={6}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
          />
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none text-text-secondary">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
          {t("change_password_show")}
        </label>

        <div className="flex justify-end gap-3 pt-3 border-t border-border">
          <button type="button" onClick={onClose} disabled={saving} className="px-5 py-2.5 border border-border hover:border-primary text-xs font-display font-bold uppercase tracking-widest transition-colors">{t("cancel")}</button>
          <button type="submit" data-testid="cp-submit" disabled={saving} className="bg-primary hover:bg-primary-hover disabled:opacity-50 px-5 py-2.5 text-xs font-display font-bold uppercase tracking-widest text-white transition-colors">{saving ? "..." : t("save")}</button>
        </div>
      </form>
    </div>,
    document.body
  );
}





// FloorPlanAlertBanner moved to /components/FloorPlanAlertBanner.jsx — used inside Financial.jsx.



// ============================================================
// PodiumCard — Top 3 leaderboard winner card with metallic gold/silver/bronze treatment
// ============================================================
function PodiumCard({ rank, row, accent, featured = false, t, storeBadge = null }) {
  const PRESETS = {
    gold: {
      borderClass: "border-yellow-400/50",
      gradient: "linear-gradient(180deg, rgba(250,204,21,0.18) 0%, rgba(202,138,4,0.05) 70%, rgba(0,0,0,0) 100%)",
      color: "text-yellow-400",
      glow: "shadow-[0_0_40px_-10px_rgba(250,204,21,0.4)]",
      ribbon: t?.("vendedor_do_mes") || "VENDEDOR DO MÊS",
    },
    silver: {
      borderClass: "border-slate-300/40",
      gradient: "linear-gradient(180deg, rgba(203,213,225,0.14) 0%, rgba(100,116,139,0.04) 70%, rgba(0,0,0,0) 100%)",
      color: "text-slate-300",
      glow: "",
      ribbon: "",
    },
    bronze: {
      borderClass: "border-amber-700/40",
      gradient: "linear-gradient(180deg, rgba(180,83,9,0.18) 0%, rgba(120,53,15,0.04) 70%, rgba(0,0,0,0) 100%)",
      color: "text-amber-600",
      glow: "",
      ribbon: "",
    },
  };
  const cfg = PRESETS[accent] || PRESETS.silver;
  const Icon = rank === 1 ? Crown : Medal;

  return (
    <div
      data-testid={`lb-podium-${rank}`}
      className={`relative border ${cfg.borderClass} ${cfg.glow} ${featured ? "py-7 px-3" : "py-5 px-3"} flex flex-col items-center text-center ${featured ? "" : "mt-4"}`}
      style={{ background: cfg.gradient }}
    >
      {/* Ribbon for #1 */}
      {featured && cfg.ribbon && (
        <div className={`absolute -top-2.5 left-1/2 -translate-x-1/2 ${cfg.color} text-[9px] tracking-[0.2em] font-display font-black bg-background border ${cfg.borderClass} px-2.5 py-1 whitespace-nowrap`}>
          ★ {cfg.ribbon} ★
        </div>
      )}

      {/* Icon */}
      <Icon size={featured ? 28 : 22} className={`${cfg.color} mb-2`} />

      {/* Avatar */}
      <Avatar
        src={row.photo_url}
        name={row.salesperson_name}
        size={featured ? "xl" : "lg"}
        ring={featured}
      />

      {/* Name */}
      <p className={`mt-2.5 font-display font-bold uppercase ${featured ? "text-sm" : "text-xs"} truncate w-full tracking-wide`}>
        {row.salesperson_name}
      </p>

      {/* Store tag — only rendered on multi-store groups */}
      {storeBadge && (
        <span
          data-testid={`lb-store-badge-${rank}`}
          className={`${storeBadge.clr} mt-1 px-1.5 py-0.5 text-[8px] font-display font-black uppercase tracking-[0.15em] rounded-sm`}
        >
          {storeBadge.label}
        </span>
      )}

      {/* Count */}
      <p className={`font-display font-black ${featured ? "text-5xl mt-2" : "text-3xl mt-1.5"} ${cfg.color} leading-none`}>
        {row.count}
      </p>
      <p className="text-[9px] uppercase tracking-[0.2em] text-text-secondary mt-1">
        {(t?.("sales_count") || "Vendas")}
      </p>

      {/* Rank pill at bottom */}
      <div className={`mt-3 inline-flex items-center gap-1 border ${cfg.borderClass} ${cfg.color} text-[9px] font-display font-black tracking-widest px-2 py-0.5`}>
        #{rank}º LUGAR
      </div>
    </div>
  );
}

function Overview({ stats, dealership, t, isSalesperson, isBdc, fpAlerts, recAlerts, onGoToFinancial, onGoToReceivables, myDealerships = [], rmvPending = null, onFixRmv }) {
  const [leaderboard, setLeaderboard] = useState({ rows: [], total_sold: 0 });
  const [promotion, setPromotion] = useState(null);
  const [editingPromo, setEditingPromo] = useState(false);
  const [insights, setInsights] = useState(null);

  // Store tag for each leaderboard row — same color mapping used everywhere
  // (Loja 1 = brand red, Loja 2 = cyan, ...). Hidden on single-store groups.
  const storeBadgeFor = (row) => {
    if (myDealerships.length < 2 || !row?.dealership_id) return null;
    const idx = myDealerships.findIndex((s) => s.id === row.dealership_id);
    if (idx < 0) return null;
    const clrs = ["bg-brand text-white", "bg-cyan-500 text-white", "bg-purple-500 text-white", "bg-amber-500 text-black"];
    const label = (myDealerships[idx].short_name || myDealerships[idx].name || "").trim();
    return { label, clr: clrs[idx] || clrs[0] };
  };

  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get("/leaderboard"),
      api.get("/promotion"),
      api.get("/overview-insights").catch(() => ({ data: null })),
    ])
      .then(([lb, pm, ins]) => {
        if (!cancel) {
          setLeaderboard(lb.data);
          setPromotion(pm.data);
          setInsights(ins.data);
        }
      })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  const reloadPromo = async () => {
    try { const r = await api.get("/promotion"); setPromotion(r.data); } catch { /* noop */ }
  };

  // Show count cards only when we have stats (user has /stats access via inventory permission OR is owner)
  const hasStats = !!stats;
  const cards = hasStats ? [
    { label: t("in_stock"), value: (stats.in_stock || 0) + (stats.reserved || 0), icon: Package },
    { label: t("reserved"), value: stats.reserved, icon: Clock },
    { label: t("sold"), value: stats.sold, icon: CheckCircle2 },
    { label: t("available"), value: stats.in_stock, icon: Sparkles },
  ] : [];

  // Build the last 6 months bucket so the chart always shows 6 bars
  const sourceMonthly = (stats && stats.monthly_sales) || [];
  const monthMap = new Map(sourceMonthly.map(m => [m.month, m]));
  const monthly = [];
  const today = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    monthly.push(monthMap.get(key) || { month: key, count: 0 });
  }
  const maxBar = Math.max(...monthly.map(m => m.count || 0), 1);
  const totalSixMonths = monthly.reduce((s, m) => s + (m.count || 0), 0);
  const monthLabel = today.toLocaleString(undefined, { month: "long", year: "numeric" });

  // Weekday heatmap (last 90 days) — comes from /overview-insights
  const weekdayLabels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
  const weekdayCounts = insights?.weekday_counts || [0, 0, 0, 0, 0, 0, 0];
  const heatMax = Math.max(...weekdayCounts, 1);
  const heatColor = (n) => {
    if (n <= 0) return "rgba(212, 175, 55, 0.06)";
    const intensity = Math.min(n / heatMax, 1);
    const alpha = 0.15 + intensity * 0.85;
    return `rgba(212, 175, 55, ${alpha.toFixed(2)})`;
  };
  const bestWeekday = weekdayCounts.reduce((acc, v, i) => v > acc.v ? { v, i } : acc, { v: -1, i: 0 });
  const showHeatmap = !!insights;

  return (
    <div data-testid="overview-tab">
      {/* Hero with subtle shield watermark */}
      <div className="mb-6 lg:mb-10 bg-shield-watermark border border-border bg-surface/30 p-5 lg:p-8 relative overflow-hidden" style={{ "--shield-url": `url('${dealership?.logo_url || "/intercar-logo.png"}')` }}>
        <div className="bg-stripes-overlay absolute inset-0 opacity-50" />
        <div className="relative">
          <p className="label-eyebrow text-primary mb-2">{t("dashboard")} · {monthLabel}</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tighter mb-2">{t("overview")}</h1>
        </div>
      </div>

      {/* KPI cards (hidden when user has no stats access — e.g. BDC) */}
      {hasStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-10">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} data-testid={`stat-${i}`} className="bg-background p-5 sm:p-6 relative overflow-hidden group">
                <Icon size={64} className="absolute -bottom-4 -right-4 text-text-secondary/5 group-hover:text-primary/10 transition-colors" />
                <p className="label-eyebrow mb-3 relative">{c.label}</p>
                <p className="font-display font-black text-4xl text-white relative">{c.value}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Weekday heatmap REMOVED per user request */}

      {/* "Carros fundados sem emplacamento" panel MOVED to the Financial page (owner request, Jun 2026) */}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border border border-border mb-10">
        {/* Leaderboard — professional podium + list */}
        <div className="bg-background p-6 lg:col-span-2 relative overflow-hidden">
          {/* Background flair */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-yellow-500/5 rounded-full blur-3xl pointer-events-none" />

          {/* Header */}
          <div className="flex items-center justify-between mb-6 relative">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-primary/10 border border-primary/40 flex items-center justify-center">
                <Trophy size={20} className="text-primary" />
              </div>
              <div>
                <p className="label-eyebrow text-primary">{t("leaderboard_title")}</p>
                <p className="text-[10px] text-text-secondary uppercase tracking-widest mt-0.5">{monthLabel}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-display font-black text-3xl">{leaderboard.total_sold}</p>
              <p className="text-[10px] text-text-secondary uppercase tracking-widest">{t("sales_count")}</p>
            </div>
          </div>

          {leaderboard.rows.length === 0 || leaderboard.total_sold === 0 ? (
            <div className="text-center py-14 border border-dashed border-border relative">
              <Trophy size={36} className="mx-auto text-text-secondary/30 mb-3" />
              <p className="text-text-secondary text-sm">{t("leaderboard_empty")}</p>
            </div>
          ) : (
            <div className="relative">
              {/* Podium for top 3 (or 1-2 if less) */}
              {leaderboard.rows.length >= 3 ? (
                <div className="grid grid-cols-3 gap-3 mb-5 items-end">
                  <PodiumCard rank={2} row={leaderboard.rows[1]} accent="silver" t={t} storeBadge={storeBadgeFor(leaderboard.rows[1])} />
                  <PodiumCard rank={1} row={leaderboard.rows[0]} accent="gold" featured t={t} storeBadge={storeBadgeFor(leaderboard.rows[0])} />
                  <PodiumCard rank={3} row={leaderboard.rows[2]} accent="bronze" t={t} storeBadge={storeBadgeFor(leaderboard.rows[2])} />
                </div>
              ) : (
                <div className={`grid ${leaderboard.rows.length === 2 ? "grid-cols-2" : "grid-cols-1 max-w-xs mx-auto"} gap-3 mb-5`}>
                  {leaderboard.rows.slice(0, 2).map((r, i) => (
                    <PodiumCard
                      key={`${r.salesperson_id || i}-${r.dealership_id || ""}`}
                      rank={r.rank}
                      row={r}
                      accent={r.rank === 1 ? "gold" : "silver"}
                      featured={r.rank === 1}
                      t={t}
                      storeBadge={storeBadgeFor(r)}
                    />
                  ))}
                </div>
              )}

              {/* Rest of leaderboard (positions 4+) — side-by-side mini cards matching the podium.
                  Slice by POSITION (not rank) so ties beyond the podium still show. */}
              {leaderboard.rows.slice(3).filter((r) => r.count > 0).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4" data-testid="lb-rest-list">
                  {leaderboard.rows.slice(3).filter((r) => r.count > 0).map((r) => {
                    const b = storeBadgeFor(r);
                    return (
                      <div
                        key={`rest-${r.salesperson_id}-${r.dealership_id || ""}`}
                        className="relative border border-border py-4 px-3 flex flex-col items-center text-center"
                        style={{ background: "linear-gradient(180deg, rgba(120,120,130,0.07) 0%, rgba(0,0,0,0) 100%)" }}
                        data-testid={`lb-row-${r.rank}`}
                      >
                        <Avatar src={r.photo_url} name={r.salesperson_name} size="md" />
                        <p className="mt-2 font-display font-bold uppercase text-[11px] truncate w-full tracking-wide">
                          {r.salesperson_name}
                        </p>
                        {b && (
                          <span className={`${b.clr} mt-1 px-1.5 py-0.5 text-[8px] font-display font-black uppercase tracking-[0.15em] rounded-sm`}>
                            {b.label}
                          </span>
                        )}
                        <p className="font-display font-black text-2xl mt-1.5 leading-none">{r.count}</p>
                        <p className="text-[9px] uppercase tracking-[0.2em] text-text-secondary mt-1">{t("sales_count")}</p>
                        <div className="mt-2.5 inline-flex items-center gap-1 border border-border text-text-secondary text-[9px] font-display font-black tracking-widest px-2 py-0.5">
                          #{r.rank}º LUGAR
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Weekly Promotion — notebook page */}
        <div className="bg-background p-6 relative overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-primary" />
              <p className="label-eyebrow text-primary">{t("weekly_promo_title")}</p>
            </div>
            {!isSalesperson && (
              <button
                data-testid="edit-promotion"
                onClick={() => setEditingPromo(true)}
                className="w-7 h-7 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
                title={t("edit")}
              >
                <Edit2 size={12} />
              </button>
            )}
          </div>
          {!promotion?.title && !promotion?.description && !promotion?.image_url ? (
            <div data-testid="promo-empty" className="border border-dashed border-border min-h-[320px] flex flex-col items-center justify-center text-center p-8">
              <div className="w-14 h-14 border border-yellow-500/30 bg-yellow-500/5 flex items-center justify-center mb-4">
                <Sparkles size={22} className="text-yellow-500/70" />
              </div>
              <p className="font-display font-bold uppercase tracking-widest text-sm text-text-secondary">
                {t("no_promotion")}
              </p>
              {!isSalesperson && (
                <button
                  data-testid="add-promotion-cta"
                  onClick={() => setEditingPromo(true)}
                  className="mt-5 px-5 h-10 border border-primary text-primary hover:bg-primary hover:text-white text-[11px] font-display font-bold uppercase tracking-widest transition-colors"
                >
                  {t("create_promotion")} →
                </button>
              )}
            </div>
          ) : (
            <div data-testid="promotion-card" className="relative border border-yellow-500/25 bg-gradient-to-b from-yellow-500/[0.05] to-transparent min-h-[320px] p-6 overflow-hidden">
              <div className="absolute top-0 left-0 w-16 h-[3px] bg-yellow-500/80" />
              {promotion.image_url && (
                <div className="aspect-video overflow-hidden border border-border mb-5">
                  <img src={promotion.image_url} alt={promotion.title} className="w-full h-full object-cover" />
                </div>
              )}
              {promotion.title && (
                <p className="font-display font-black uppercase tracking-tight text-3xl leading-none mb-3 text-white">
                  {promotion.title}
                </p>
              )}
              {promotion.description && (
                <p className="text-text-secondary text-sm whitespace-pre-line leading-relaxed">
                  {promotion.description}
                </p>
              )}
              {promotion.valid_until && (
                <p className="mt-5 inline-flex items-center gap-2 border border-yellow-500/40 text-yellow-500 text-[10px] font-display font-bold uppercase tracking-widest px-3 py-1.5">
                  <Calendar size={12} /> {t("valid_until")}: {promotion.valid_until}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ===== Bloco de Notas — parts to buy + free-form scratchpad ===== */}
      <PartsNotesBlock t={t} canEdit={!isSalesperson && !isBdc} />

      {/* ===== Performance mensal — REDESENHADO (gráfico de área + barras + sparkline) ===== */}
      {hasStats && (
        <div data-testid="monthly-performance" className="border border-border bg-background p-6 lg:p-8 relative overflow-hidden">
          {/* Ambient backdrop */}
          <div className="absolute -top-24 -right-24 w-80 h-80 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-gold/5 rounded-full blur-3xl pointer-events-none" />

          {/* Header */}
          <div className="flex items-center justify-between mb-6 relative flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-primary/10 border border-primary/40 flex items-center justify-center">
                <BarChart3 size={18} className="text-primary" />
              </div>
              <div>
                <p className="label-eyebrow text-primary">{t("monthly_performance")}</p>
                <p className="text-[10px] text-text-secondary uppercase tracking-widest mt-0.5">Últimos 6 meses</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-[9px] uppercase tracking-widest text-text-secondary">Total no período</p>
                <p className="font-display font-black text-2xl text-white leading-none">{totalSixMonths}</p>
              </div>
              <div className="text-right">
                <p className="text-[9px] uppercase tracking-widest text-text-secondary">Mês atual</p>
                <p className="font-display font-black text-2xl text-primary leading-none">{monthly[monthly.length - 1]?.count || 0}</p>
              </div>
            </div>
          </div>

          {monthly.length === 0 ? (
            <p className="text-text-secondary text-sm py-8 text-center">—</p>
          ) : (
            <div className="relative">
              {/* Chart area with horizontal grid lines */}
              <div className="relative h-56 px-1 pt-4 pb-1">
                {/* Y-axis grid lines (4 levels) */}
                <div className="absolute inset-x-0 inset-y-4 flex flex-col justify-between pointer-events-none">
                  {[1, 0.75, 0.5, 0.25, 0].map((level, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-[9px] text-text-secondary/60 font-mono w-6 text-right">
                        {Math.round(maxBar * level)}
                      </span>
                      <div className="flex-1 border-t border-border/40 border-dashed" />
                    </div>
                  ))}
                </div>

                {/* Area path (SVG) behind bars */}
                <svg
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  viewBox={`0 0 ${monthly.length * 100} 100`}
                  preserveAspectRatio="none"
                >
                  <defs>
                    <linearGradient id="areaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#D92D20" stopOpacity="0.45" />
                      <stop offset="100%" stopColor="#D92D20" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#D4AF37" />
                      <stop offset="100%" stopColor="#F1C950" />
                    </linearGradient>
                  </defs>
                  {(() => {
                    const points = monthly.map((m, i) => {
                      const x = i * 100 + 50;
                      const y = 100 - Math.max(((m.count || 0) / maxBar) * 100, 1);
                      return [x, y];
                    });
                    const pathLine = points.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ");
                    const pathArea = pathLine + ` L${points[points.length - 1][0]},100 L${points[0][0]},100 Z`;
                    return (
                      <>
                        <path d={pathArea} fill="url(#areaGrad)" />
                        <path d={pathLine} fill="none" stroke="url(#lineGrad)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                        {points.map((p, i) => (
                          <circle
                            key={i}
                            cx={p[0]}
                            cy={p[1]}
                            r={i === points.length - 1 ? 3 : 2}
                            fill={i === points.length - 1 ? "#F1C950" : "#D4AF37"}
                            stroke="#0A0A0A"
                            strokeWidth="1.2"
                            vectorEffect="non-scaling-stroke"
                          />
                        ))}
                      </>
                    );
                  })()}
                </svg>

                {/* Bars overlay */}
                <div className="absolute inset-x-0 inset-y-4 flex items-end gap-2 pl-8 pr-1">
                  {monthly.map((m, idx) => {
                    const h = Math.max(((m.count || 0) / maxBar) * 100, 1.5);
                    const isCurrent = idx === monthly.length - 1;
                    const prevCount = idx > 0 ? (monthly[idx - 1].count || 0) : null;
                    const delta = prevCount === null ? null : (m.count || 0) - prevCount;
                    return (
                      <div key={m.month} className="flex-1 flex flex-col items-center h-full justify-end relative group">
                        {/* Tooltip */}
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-surface border border-border px-2 py-1 text-[10px] whitespace-nowrap z-10 pointer-events-none">
                          <p className="font-display font-bold text-white">{m.count || 0} {t("sales_count")?.toLowerCase()}</p>
                          {delta !== null && delta !== 0 && (
                            <p className={`text-[9px] ${delta > 0 ? "text-success" : "text-primary"}`}>
                              {delta > 0 ? "+" : ""}{delta} vs mês ant.
                            </p>
                          )}
                        </div>
                        {/* Bar */}
                        <div
                          className="w-full max-w-[44px] transition-all duration-300 ease-out group-hover:scale-y-105 origin-bottom relative"
                          style={{
                            height: `${h}%`,
                            background: isCurrent
                              ? "linear-gradient(180deg, #F87171 0%, #D92D20 60%, #991B1B 100%)"
                              : "linear-gradient(180deg, rgba(217, 45, 32, 0.4) 0%, rgba(120, 20, 14, 0.35) 100%)",
                            boxShadow: isCurrent ? "0 -2px 24px -4px rgba(217, 45, 32, 0.55)" : "none",
                            borderTop: isCurrent ? "1px solid #F1C950" : "none",
                          }}
                        >
                          {/* Value label */}
                          <span
                            className={`absolute -top-5 left-1/2 -translate-x-1/2 font-display font-black text-xs ${
                              isCurrent ? "text-white" : "text-text-secondary"
                            }`}
                          >
                            {m.count || 0}
                          </span>
                          {/* Top shine */}
                          {isCurrent && (
                            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-gold to-transparent" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* X-axis labels with delta vs previous */}
              <div className="flex gap-2 pl-8 pr-1 mt-2">
                {monthly.map((m, idx) => {
                  const isCurrent = idx === monthly.length - 1;
                  const [yr, mo] = m.month.split("-");
                  const labelDate = new Date(parseInt(yr), parseInt(mo) - 1, 1);
                  const monthName = labelDate.toLocaleString(undefined, { month: "short" }).replace(".", "");
                  return (
                    <div key={m.month} className="flex-1 text-center">
                      <p className={`font-display font-bold text-[10px] uppercase tracking-wider ${isCurrent ? "text-primary" : "text-text-secondary"}`}>
                        {monthName}
                      </p>
                      <p className="font-mono text-[9px] text-text-secondary/60">/{m.month.slice(2, 4)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {editingPromo && !isSalesperson && (
        <PromotionForm
          promotion={promotion}
          t={t}
          onClose={() => setEditingPromo(false)}
          onSaved={() => { setEditingPromo(false); reloadPromo(); }}
        />
      )}
    </div>
  );
}

/**
 * PartsNotesBlock — dashboard scratchpad + parts-to-buy checklist.
 *
 * Two side-by-side panels living inside the same dealership-scoped
 * document (`dealership.parts_notes`). Managers/owners can edit
 * everything; salespeople + BDCs can only toggle `done` on items so the
 * board acts as a shared floor to-do list.
 */
function PartsNotesBlock({ t, canEdit }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [text, setText] = useState("");
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ updated_at: "", updated_by: "" });
  const [newItem, setNewItem] = useState("");
  const [textDirty, setTextDirty] = useState(false);

  useEffect(() => {
    let cancel = false;
    api.get("/parts-notes")
      .then((r) => {
        if (cancel) return;
        setText(r.data?.text || "");
        setItems(Array.isArray(r.data?.items) ? r.data.items : []);
        setMeta({ updated_at: r.data?.updated_at || "", updated_by: r.data?.updated_by || "" });
      })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  const persist = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put("/parts-notes", patch);
      setText(r.data?.text || "");
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      setMeta({ updated_at: r.data?.updated_at || "", updated_by: r.data?.updated_by || "" });
      return true;
    } catch (err) {
      const msg = err?.response?.data?.detail || t("error_generic");
      toast.error(msg);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveText = async () => {
    if (!canEdit) return;
    const ok = await persist({ text });
    if (ok) { setTextDirty(false); toast.success(t("parts_notes_saved")); }
  };

  const toggleDone = async (id) => {
    const next = items.map(it => it.id === id ? { ...it, done: !it.done } : it);
    setItems(next);
    await persist({ items: next });
  };

  const addItem = async () => {
    if (!canEdit) return;
    const value = newItem.trim();
    if (!value) return;
    const next = [...items, { id: crypto.randomUUID(), text: value, done: false }];
    setNewItem("");
    const ok = await persist({ items: next });
    if (ok) toast.success(t("parts_notes_saved"));
  };

  const removeItem = async (id) => {
    if (!canEdit) return;
    const next = items.filter(it => it.id !== id);
    await persist({ items: next });
  };

  const pending = items.filter(i => !i.done).length;
  const done = items.length - pending;

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  return (
    <div data-testid="parts-notes-block" className="border border-border bg-background p-6 lg:p-8 mb-10 relative overflow-hidden">
      {/* Ambient accents */}
      <div className="absolute -top-20 -right-20 w-72 h-72 bg-warning/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <div className="relative flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-warning/10 border border-warning/40 flex items-center justify-center">
            <ClipboardList size={18} className="text-warning" />
          </div>
          <div>
            <p className="label-eyebrow text-warning">{t("parts_notes_eyebrow")}</p>
            <h3 className="font-display font-black text-xl uppercase tracking-tight text-white leading-tight">{t("parts_notes_title")}</h3>
            <p className="text-xs text-text-secondary mt-1 max-w-md">{t("parts_notes_subtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-4 text-right">
          <div>
            <p className="text-[9px] uppercase tracking-widest text-text-secondary">{t("parts_notes_pending")}</p>
            <p className="font-display font-black text-2xl text-warning leading-none" data-testid="parts-notes-pending-count">{pending}</p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-widest text-text-secondary">{t("parts_notes_done")}</p>
            <p className="font-display font-black text-2xl text-success leading-none" data-testid="parts-notes-done-count">{done}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border border border-border">
          {[0, 1].map((i) => (
            <div key={i} className="bg-background p-5 space-y-3">
              <div className="h-3 w-24 bg-surface animate-pulse" />
              <div className="h-24 bg-surface animate-pulse" />
              <div className="h-3 w-2/3 bg-surface animate-pulse" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border border border-border relative">
          {/* Scratchpad */}
          <div className="bg-background p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <label className="label-eyebrow text-text-secondary">{t("parts_notes_scratch_label")}</label>
              {canEdit && textDirty && (
                <button
                  type="button"
                  data-testid="parts-notes-save-text"
                  onClick={saveText}
                  disabled={saving}
                  className="text-[10px] uppercase tracking-widest font-display font-bold px-3 h-8 bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Check size={12} /> {t("parts_notes_save")}
                </button>
              )}
            </div>
            <textarea
              data-testid="parts-notes-text"
              value={text}
              onChange={(e) => { setText(e.target.value); setTextDirty(true); }}
              readOnly={!canEdit}
              rows={9}
              placeholder={canEdit ? t("parts_notes_scratch_placeholder") : t("parts_notes_manager_only")}
              className={`w-full bg-surface border border-border ${canEdit ? "focus:border-warning" : "cursor-default"} focus:outline-none px-3 py-2 text-sm placeholder:text-text-secondary/50 resize-none`}
            />
            {meta.updated_by && meta.updated_at && (
              <p className="text-[10px] text-text-secondary mt-2 font-mono">
                {t("parts_notes_last_update")} <span className="text-white/80">{meta.updated_by}</span> · {fmtDate(meta.updated_at)}
              </p>
            )}
          </div>

          {/* Checklist */}
          <div className="bg-background p-5">
            <label className="label-eyebrow text-text-secondary block mb-3">{t("parts_notes_list_label")}</label>

            {canEdit && (
              <div className="flex gap-2 mb-4">
                <input
                  data-testid="parts-notes-new-item"
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
                  placeholder={t("parts_notes_list_placeholder")}
                  className="flex-1 bg-surface border border-border focus:border-warning focus:outline-none px-3 h-10 text-sm placeholder:text-text-secondary/50"
                />
                <button
                  type="button"
                  data-testid="parts-notes-add-btn"
                  onClick={addItem}
                  disabled={saving || !newItem.trim()}
                  className="px-3 h-10 bg-warning text-black font-display font-bold text-xs uppercase tracking-widest hover:brightness-110 transition disabled:opacity-40 flex items-center gap-1.5"
                >
                  <Plus size={14} /> {t("parts_notes_add")}
                </button>
              </div>
            )}

            {items.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-border">
                <ClipboardList size={26} className="mx-auto text-text-secondary/30 mb-2" />
                <p className="text-text-secondary text-sm">{t("parts_notes_empty")}</p>
              </div>
            ) : (
              <ul data-testid="parts-notes-list" className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {items.map((it) => (
                  <li
                    key={it.id}
                    data-testid={`parts-notes-item-${it.id}`}
                    className={`flex items-center gap-3 px-3 py-2 border ${it.done ? "border-success/30 bg-success/[0.04]" : "border-border bg-surface"} group`}
                  >
                    <button
                      type="button"
                      data-testid={`parts-notes-toggle-${it.id}`}
                      onClick={() => toggleDone(it.id)}
                      className={`w-5 h-5 border ${it.done ? "border-success bg-success" : "border-border hover:border-warning"} flex items-center justify-center transition-colors flex-shrink-0`}
                      aria-label="toggle"
                    >
                      {it.done && <Check size={12} className="text-black" strokeWidth={3} />}
                    </button>
                    <span className={`flex-1 text-sm ${it.done ? "line-through text-text-secondary" : ""}`}>
                      {it.text}
                    </span>
                    {canEdit && (
                      <button
                        type="button"
                        data-testid={`parts-notes-delete-${it.id}`}
                        onClick={() => removeItem(it.id)}
                        className="opacity-0 group-hover:opacity-100 text-text-secondary hover:text-primary transition-all flex-shrink-0"
                        aria-label="delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PromotionForm({ promotion, t, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: promotion?.title || "",
    description: promotion?.description || "",
    image_url: promotion?.image_url || "",
    valid_until: promotion?.valid_until || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onImage = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error(t("file_too_large")); return; }
    try {
      const sig = (await api.get("/cloudinary/signature", { params: { folder: "promotions/" } })).data;
      const fd = new FormData();
      fd.append("file", file); fd.append("api_key", sig.api_key); fd.append("timestamp", sig.timestamp);
      fd.append("signature", sig.signature); fd.append("folder", sig.folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: "POST", body: fd });
      const json = await res.json();
      if (!json.secure_url) throw new Error("upload failed");
      set("image_url", json.secure_url);
      toast.success(t("saved"));
    } catch (err) { toast.error(err.message || t("error_generic")); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/promotion", form);
      toast.success(t("saved"));
      onSaved();
    } catch { toast.error(t("error_generic")); }
    finally { setSaving(false); }
  };

  const clearAll = async () => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.put("/promotion", { title: "", description: "", image_url: "", valid_until: "" });
      toast.success(t("saved"));
      onSaved();
    } catch { toast.error(t("error_generic")); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-12 px-4">
      <form onSubmit={submit} className="bg-background border border-border w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="label-eyebrow text-primary mb-1">{t("weekly_promo_title")}</p>
            <h2 className="font-display font-black text-xl uppercase tracking-tight">{promotion?.title ? t("edit") : t("create_promotion")}</h2>
          </div>
          <button type="button" onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>

        <div>
          <label className="label-eyebrow block mb-2">{t("promo_image")}</label>
          {form.image_url ? (
            <div className="relative aspect-video bg-surface overflow-hidden border border-border">
              <img src={form.image_url} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => set("image_url", "")}
                className="absolute top-2 right-2 w-8 h-8 bg-black/70 text-white hover:bg-primary flex items-center justify-center"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <label data-testid="promo-image-label" className="cursor-pointer border border-dashed border-border hover:border-primary aspect-video flex flex-col items-center justify-center gap-2 text-text-secondary hover:text-primary transition-colors">
              <Upload size={20} />
              <span className="text-xs uppercase tracking-wider">{t("upload_image")}</span>
              <input data-testid="promo-image" type="file" accept="image/*" onChange={(e) => onImage(e.target.files?.[0])} className="hidden" />
            </label>
          )}
        </div>

        <div>
          <label className="label-eyebrow block mb-2">{t("title")}</label>
          <input
            data-testid="promo-title"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder={t("promo_title_placeholder")}
            className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">{t("description")}</label>
          <textarea
            data-testid="promo-desc"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            rows={4}
            placeholder={t("promo_desc_placeholder")}
            className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">{t("valid_until")} <span className="text-text-secondary normal-case">({t("optional")})</span></label>
          <input
            data-testid="promo-until"
            type="date"
            value={form.valid_until}
            onChange={(e) => set("valid_until", e.target.value)}
            className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
          />
        </div>

        <div className="flex justify-between gap-3 pt-4 border-t border-border">
          {(promotion?.title || promotion?.description || promotion?.image_url) ? (
            <button type="button" data-testid="promo-clear" onClick={clearAll} className="text-xs font-display font-bold uppercase tracking-widest text-text-secondary hover:text-primary inline-flex items-center gap-2">
              <Trash2 size={12} /> {t("delete")}
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-border hover:border-primary text-xs font-display font-bold uppercase tracking-widest transition-colors">{t("cancel")}</button>
            <button type="submit" data-testid="promo-submit" disabled={saving} className="bg-primary hover:bg-primary-hover disabled:opacity-50 px-5 py-2.5 text-xs font-display font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2">
              <Check size={14} /> {saving ? "..." : t("save")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Inventory({ vehicles, t, search, setSearch, onAdd, onImport, onImportPage, onEdit, onDelete, onHistory, onSimulate, isSalesperson, myDealerships = [], canTransfer = false, onTransferred }) {
  const [view, setView] = useState(() => localStorage.getItem("inventory_view") || "grid");
  const [statusFilter, setStatusFilter] = useState("all");
  const [makeFilter, setMakeFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [bodyFilter, setBodyFilter] = useState("");
  // Persist the user's sort choice so refreshing doesn't blow it away.
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("inventory_sort") || "make_az");
  // Aging filter shows ONLY cars sitting 60+ days. Toggled from the analytics
  // strip so the owner can click the "5 parados" chip and drill in instantly.
  const [agingOnly, setAgingOnly] = useState(false);
  // Multi-store filter: "all" | dealership_id — persist so a refresh doesn't
  // blow away the owner's choice mid-triage.
  const [storeFilter, setStoreFilter] = useState(() => localStorage.getItem("inventory_store_filter") || "all");
  const setStoreFilterSticky = (v) => { setStoreFilter(v); localStorage.setItem("inventory_store_filter", v); };
  const setViewSticky = (v) => { setView(v); localStorage.setItem("inventory_view", v); };
  const setSortSticky = (s) => { setSortBy(s); localStorage.setItem("inventory_sort", s); };

  // Helper: how many whole days a car has been sitting in stock. Uses the
  // vehicle's `created_at` ISO timestamp (set on insert by the backend). Missing
  // dates → 0 so we never sort them to the top of "longest sitting".
  const daysInStock = (v) => {
    if (!v.created_at) return 0;
    const ms = Date.now() - new Date(v.created_at).getTime();
    return Math.max(0, Math.floor(ms / 86400000));
  };

  // Sold vehicles disappear from inventory — they live in Esteira de Entrega + Financeiro.
  // EXCEPT while searching: the lupa must find any car/client, even already sold
  // (the card keeps its "VENDIDO" pill so there's no confusion).
  const searchActive = !!(search && search.trim());
  const activeVehicles = vehicles.filter(v => v.status !== "sold");
  const soldMatches = searchActive ? vehicles.filter(v => v.status === "sold") : [];

  // Build dropdown options dynamically from current stock.
  // Models depend on the picked make so users see only relevant options.
  const allMakes = Array.from(new Set(activeVehicles.map(v => (v.make || "").trim()).filter(Boolean))).sort();
  // Body types: fixed canonical list so users always see every option.
  const allBodies = ["Sedan", "SUV", "Truck", "Coupe", "Hatch", "Convertible", "Wagon", "Van"];
  const modelsForMake = Array.from(new Set(
    activeVehicles
      .filter(v => !makeFilter || (v.make || "").toLowerCase() === makeFilter.toLowerCase())
      .map(v => (v.model || "").trim()).filter(Boolean)
  )).sort();

  const counts = {
    all: activeVehicles.length + soldMatches.length,
    in_stock: activeVehicles.filter(v => v.status === "in_stock").length,
    reserved: activeVehicles.filter(v => v.status === "reserved").length,
  };

  // ── Analytics for the summary strip. Cheap to compute (single pass) and
  // gives the owner an instant read on inventory health.
  const totalPrice = activeVehicles.reduce((sum, v) => sum + (Number(v.sale_price) || 0), 0);
  const withPrice = activeVehicles.filter(v => Number(v.sale_price) > 0).length;
  const avgPrice = withPrice > 0 ? Math.round(totalPrice / withPrice) : 0;
  const totalAge = activeVehicles.reduce((sum, v) => sum + daysInStock(v), 0);
  const avgAge = activeVehicles.length > 0 ? Math.round(totalAge / activeVehicles.length) : 0;
  const agingCount = activeVehicles.filter(v => daysInStock(v) >= 60).length;

  const filtered = [...activeVehicles, ...soldMatches].filter(v => {
    if (statusFilter !== "all" && v.status !== statusFilter) return false;
    if (makeFilter && (v.make || "").toLowerCase() !== makeFilter.toLowerCase()) return false;
    if (modelFilter && (v.model || "").toLowerCase() !== modelFilter.toLowerCase()) return false;
    if (bodyFilter && (v.body_type || "").toLowerCase() !== bodyFilter.toLowerCase()) return false;
    if (agingOnly && daysInStock(v) < 60) return false;
    if (storeFilter !== "all" && v.dealership_id !== storeFilter) return false;
    return true;
  });

  // Apply the chosen sort AFTER filtering so counts stay right. Default is
  // Make A-Z which was the original behavior (kept for muscle memory).
  const sortFns = {
    make_az: (a, b) => {
      const ma = (a.make || "").toLowerCase();
      const mb = (b.make || "").toLowerCase();
      if (ma !== mb) return ma.localeCompare(mb);
      return (a.model || "").toLowerCase().localeCompare((b.model || "").toLowerCase());
    },
    newest: (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
    oldest: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
    price_desc: (a, b) => (Number(b.sale_price) || 0) - (Number(a.sale_price) || 0),
    price_asc: (a, b) => (Number(a.sale_price) || 0) - (Number(b.sale_price) || 0),
  };
  const sorted = filtered.slice().sort(sortFns[sortBy] || sortFns.make_az);

  const hasFilters = makeFilter || modelFilter || bodyFilter || agingOnly || storeFilter !== "all";
  const clearFilters = () => { setMakeFilter(""); setModelFilter(""); setBodyFilter(""); setAgingOnly(false); setStoreFilterSticky("all"); };

  return (
    <div data-testid="inventory-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="label-eyebrow text-primary mb-2">{t("inventory")}</p>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">{t("inventory")}</h1>
        </div>
        {!isSalesperson && (
          <div className="flex gap-2 flex-wrap">
            {onImportPage && (
              <button data-testid="import-inventory-page-btn" onClick={onImportPage} className="border border-border hover:border-primary hover:text-primary transition-colors px-5 py-3 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2">
                <ListChecks size={14} /> {t("import_inv_btn")}
              </button>
            )}
            <button data-testid="import-url" onClick={onImport} className="border border-border hover:border-primary hover:text-primary transition-colors px-5 py-3 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2">
              <Download size={14} className="rotate-180" /> {t("import_from_url")}
            </button>
            <button data-testid="add-vehicle" onClick={onAdd} className="bg-primary hover:bg-primary-hover px-5 py-3 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2">
              <Plus size={14} /> {t("add_vehicle")}
            </button>
          </div>
        )}
      </div>

      {/* Analytics summary strip — one-line health check of the whole stock. */}
      <InventorySummaryStrip
        t={t}
        total={activeVehicles.length}
        avgPrice={avgPrice}
        avgAge={avgAge}
        agingCount={agingCount}
        agingOnly={agingOnly}
        onToggleAging={() => setAgingOnly((v) => !v)}
      />

      {/* Search + dropdown filters + View toggle */}
      <div className="flex flex-wrap gap-3 mb-3">
        <div className="border border-border flex items-center px-4 h-12 flex-1 min-w-[280px]">
          <Search size={16} className="text-text-secondary mr-3" />
          <input data-testid="inventory-search" type="text" placeholder={t("inventory_search_placeholder")} value={search} onChange={(e) => setSearch(e.target.value)} className="bg-transparent w-full focus:outline-none text-sm" />
        </div>
        <select
          data-testid="filter-make"
          value={makeFilter}
          onChange={(e) => { setMakeFilter(e.target.value); setModelFilter(""); }}
          className="bg-surface border border-border focus:border-primary focus:outline-none px-3 h-12 text-sm min-w-[140px]"
        >
          <option value="">{t("filter_all_makes")}</option>
          {allMakes.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select
          data-testid="filter-model"
          value={modelFilter}
          onChange={(e) => setModelFilter(e.target.value)}
          disabled={modelsForMake.length === 0}
          className="bg-surface border border-border focus:border-primary focus:outline-none px-3 h-12 text-sm min-w-[140px] disabled:opacity-50"
        >
          <option value="">{t("filter_all_models")}</option>
          {modelsForMake.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select
          data-testid="filter-body"
          value={bodyFilter}
          onChange={(e) => setBodyFilter(e.target.value)}
          className="bg-surface border border-border focus:border-primary focus:outline-none px-3 h-12 text-sm min-w-[140px]"
        >
          <option value="">{t("filter_all_bodies")}</option>
          {allBodies.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select
          data-testid="filter-sort"
          value={sortBy}
          onChange={(e) => setSortSticky(e.target.value)}
          className="bg-surface border border-border focus:border-primary focus:outline-none px-3 h-12 text-sm min-w-[170px]"
          title={t("sort_label")}
        >
          <option value="make_az">{t("sort_make_az")}</option>
          <option value="newest">{t("sort_newest")}</option>
          <option value="oldest">{t("sort_oldest")}</option>
          <option value="price_desc">{t("sort_price_desc")}</option>
          <option value="price_asc">{t("sort_price_asc")}</option>
        </select>
        <div className="flex border border-border h-12">
          <button
            data-testid="view-grid"
            onClick={() => setViewSticky("grid")}
            title="Grid"
            className={`w-12 flex items-center justify-center transition-colors ${view === "grid" ? "bg-primary text-white" : "text-text-secondary hover:text-white"}`}
          >
            <LayoutGrid size={16} />
          </button>
          <button
            data-testid="view-list"
            onClick={() => setViewSticky("list")}
            title="List"
            className={`w-12 flex items-center justify-center transition-colors ${view === "list" ? "bg-primary text-white" : "text-text-secondary hover:text-white"}`}
          >
            <List size={16} />
          </button>
        </div>
      </div>

      {/* Active filter chips */}
      {hasFilters && (
        <div className="flex flex-wrap gap-2 items-center mb-5">
          <span className="text-[10px] uppercase tracking-widest text-text-secondary">{t("active_filters")}:</span>
          {makeFilter && (
            <button onClick={() => { setMakeFilter(""); setModelFilter(""); }} data-testid="chip-make" className="inline-flex items-center gap-1.5 border border-primary text-primary px-2 py-1 text-[11px] font-display font-bold uppercase tracking-wider hover:bg-primary/10">
              {makeFilter} <X size={11} />
            </button>
          )}
          {modelFilter && (
            <button onClick={() => setModelFilter("")} data-testid="chip-model" className="inline-flex items-center gap-1.5 border border-primary text-primary px-2 py-1 text-[11px] font-display font-bold uppercase tracking-wider hover:bg-primary/10">
              {modelFilter} <X size={11} />
            </button>
          )}
          {bodyFilter && (
            <button onClick={() => setBodyFilter("")} data-testid="chip-body" className="inline-flex items-center gap-1.5 border border-primary text-primary px-2 py-1 text-[11px] font-display font-bold uppercase tracking-wider hover:bg-primary/10">
              {bodyFilter} <X size={11} />
            </button>
          )}
          {agingOnly && (
            <button onClick={() => setAgingOnly(false)} data-testid="chip-aging" className="inline-flex items-center gap-1.5 border border-warning text-warning px-2 py-1 text-[11px] font-display font-bold uppercase tracking-wider hover:bg-warning/10">
              {t("inv_filter_aging_active")} <X size={11} />
            </button>
          )}
          <button onClick={clearFilters} data-testid="chip-clear" className="text-[11px] uppercase tracking-widest text-text-secondary hover:text-primary">
            {t("clear_filters")}
          </button>
        </div>
      )}

      {/* Multi-store filter — only rendered when the group has 2+ stores.
          Client-side filter over the shared inventory list. Persisted so a
          refresh doesn't blow away the choice mid-triage. */}
      {myDealerships.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="label-eyebrow self-center mr-1">Loja:</span>
          {[
            { id: "all", label: "Ambas", color: null },
            ...myDealerships.map((s, idx) => ({
              id: s.id,
              label: (s.short_name || s.name || "").toUpperCase(),
              color: idx === 0 ? "brand" : "cyan",
            })),
          ].map((s) => {
            const isActive = storeFilter === s.id;
            const cnt = s.id === "all" ? activeVehicles.length : activeVehicles.filter((v) => v.dealership_id === s.id).length;
            const activeCls =
              s.color === "brand" ? "border-brand text-white bg-brand"
              : s.color === "cyan" ? "border-cyan-500 text-white bg-cyan-500"
              : "border-primary text-primary bg-primary/10";
            return (
              <button
                key={s.id}
                data-testid={`inv-store-${s.id}`}
                onClick={() => setStoreFilterSticky(s.id)}
                className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-widest border transition-colors ${
                  isActive ? activeCls : "border-border text-text-secondary hover:text-white"
                }`}
              >
                {s.color && <Package size={11} className="inline mr-1 -mt-0.5" />}
                {s.label} <span className={`ml-1 ${isActive ? "opacity-80" : "text-text-secondary"}`}>({cnt})</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Status filter pills (sold cars are excluded — they live in Esteira de Entrega + Financeiro) */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: "all", label: t("all_time") },
          { id: "in_stock", label: t("in_stock") },
          { id: "reserved", label: t("reserved") },
        ].map(s => (
          <button
            key={s.id}
            data-testid={`inv-filter-${s.id}`}
            onClick={() => setStatusFilter(s.id)}
            className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-widest border transition-colors ${
              statusFilter === s.id ? "border-primary text-primary bg-primary/10" : "border-border text-text-secondary hover:text-white"
            }`}
          >
            {s.label} <span className="ml-1 text-text-secondary">({counts[s.id]})</span>
          </button>
        ))}
      </div>

      {sorted.length === 0 ? (
        <p className="border border-dashed border-border text-text-secondary text-sm text-center py-16">{t("no_vehicles")}</p>
      ) : view === "grid" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {sorted.map((v) => (
            <VehicleCard key={v.id} v={v} t={t} onEdit={onEdit} onDelete={onDelete} onHistory={onHistory} onSimulate={onSimulate} isSalesperson={isSalesperson} daysInStock={daysInStock(v)} myDealerships={myDealerships} canTransfer={canTransfer} onTransferred={onTransferred} />
          ))}
        </div>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface border-b border-border">
              <tr>
                <th className="text-left p-3 label-eyebrow w-20"></th>
                <th className="text-left p-3 label-eyebrow">{t("make")}/{t("model")}</th>
                <th className="text-left p-3 label-eyebrow">{t("year")}</th>
                <th className="text-left p-3 label-eyebrow">
                  <span className="inline-flex items-center gap-1"><Gauge size={11} /> Milhas</span>
                </th>
                <th className="text-left p-3 label-eyebrow">{t("vin")}</th>
                <th className="text-left p-3 label-eyebrow">{t("sale_price")}</th>
                <th className="text-left p-3 label-eyebrow">{t("status")}</th>
                <th className="text-left p-3 label-eyebrow"><Clock size={11} className="inline" /></th>
                <th className="text-right p-3 label-eyebrow"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((v) => (
                <tr key={v.id} data-testid={`row-${v.id}`} className="border-b border-border hover:bg-surface transition-colors">
                  <td className="p-2">
                    <img src={vehiclePhoto(v)} alt={`${v.make} ${v.model}`} className="w-16 h-12 object-cover" />
                  </td>
                  <td className="p-3"><p className="font-display font-bold">{v.make} {v.model}</p><p className="text-xs text-text-secondary">{v.color}</p></td>
                  <td className="p-3">{v.year}</td>
                  <td className="p-3 font-mono text-xs">
                    {v.mileage > 0 ? (
                      <span className="inline-flex items-center gap-1 text-primary">
                        <Gauge size={11} />
                        {Number(v.mileage).toLocaleString("en-US")}
                        <span className="text-text-secondary text-[10px]">mi</span>
                      </span>
                    ) : <span className="text-text-secondary">—</span>}
                  </td>
                  <td className="p-3 font-mono text-xs">{v.vin || "—"}</td>
                  <td className="p-3 font-display font-bold">{formatCurrency(v.sale_price)}</td>
                  <td className="p-3"><StatusPill status={v.status} t={t} /></td>
                  <td className="p-3"><AgingBadge days={daysInStock(v)} /></td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      {onHistory && (
                        <button data-testid={`history-${v.id}`} onClick={() => onHistory(v.id)} className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors" title={t("vehicle_history")}><History size={14} /></button>
                      )}
                      {onSimulate && (
                        <button data-testid={`sim-${v.id}`} onClick={() => onSimulate(v)} className="w-8 h-8 border border-border hover:border-[#C9A227] hover:text-[#C9A227] flex items-center justify-center transition-colors" title="Simulação de financiamento"><Calculator size={14} /></button>
                      )}
                      <button data-testid={`edit-${v.id}`} onClick={() => onEdit(v)} className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"><Edit2 size={14} /></button>
                      {!isSalesperson && (
                        <button data-testid={`delete-${v.id}`} onClick={() => onDelete(v.id)} className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function VehicleCard({ v, t, onEdit, onDelete, onHistory, onSimulate, isSalesperson, daysInStock = 0, myDealerships = [], canTransfer = false, onTransferred }) {
  const photoCount = v.images?.length || 0;
  // Quick store-switch dropdown (owner + gerente): click the banner → pick the
  // destination store → the car moves instantly via the transfer API.
  const [storeMenuOpen, setStoreMenuOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  // Multi-store: show which store this car physically lives at. Only when the
  // owner has more than one store — single-store owners don't need the noise.
  const store = myDealerships.length > 1
    ? myDealerships.find((s) => s.id === v.dealership_id)
    : null;
  // Badge label: prefer short_name (custom "Everett"/"Peabody"), otherwise fall
  // back to the full store name. Capped at 16 chars so it doesn't overflow the
  // banner on skinny mobile viewports.
  const rawLabel = store ? (store.short_name || store.name || "").trim() : "";
  const storeLabel = rawLabel.length > 16 ? `${rawLabel.slice(0, 16)}…` : rawLabel;
  // Distinct color per store so the salesperson can differentiate at a glance
  // even before reading the text. Deterministic map based on index (stable
  // across reloads) with fallback to the brand red.
  const storeIdx = store ? myDealerships.findIndex((s) => s.id === v.dealership_id) : -1;
  const storeColors = [
    "bg-brand text-white",       // Loja 1 (default red)
    "bg-cyan-500 text-white",    // Loja 2
    "bg-purple-500 text-white",  // Loja 3
    "bg-amber-500 text-black",   // Loja 4
  ];
  const storeClr = storeColors[storeIdx] || "bg-brand text-white";
  const storeDots = ["bg-brand", "bg-cyan-500", "bg-purple-500", "bg-amber-500"];
  const transferable = canTransfer && myDealerships.length > 1;
  const quickMove = async (targetId) => {
    setSwitching(true);
    try {
      const r = await api.post(`/vehicles/${v.id}/transfer`, {
        target_dealership_id: targetId,
        reason: "Troca rápida pelo estoque",
      });
      toast.success(`Carro movido para ${r.data.to_dealership_name}`);
      setStoreMenuOpen(false);
      onTransferred && onTransferred();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao mover o carro");
    } finally {
      setSwitching(false);
    }
  };
  return (
    <div data-testid={`card-grid-${v.id}`} className="group border border-border bg-surface overflow-visible hover:border-primary transition-colors relative">
      {/* Full-width store banner across the top — impossible to miss. Serves as
          a visual anchor so the vendedor immediately knows which lot to walk to.
          For owner/gerente it doubles as a quick store-switch dropdown. */}
      {storeLabel && (
        <div className="relative">
          {transferable ? (
            <button
              type="button"
              data-testid={`store-quick-switch-${v.id}`}
              onClick={() => setStoreMenuOpen((o) => !o)}
              disabled={switching}
              className={`${storeClr} w-full px-2 py-1 flex items-center justify-center gap-1.5 font-display font-black text-[11px] uppercase tracking-[0.18em] hover:opacity-90 transition-opacity disabled:opacity-60`}
              title={`Este carro está na loja ${storeLabel} — clique para mover de loja`}
            >
              <Package size={11} strokeWidth={2.5} /> {storeLabel}
              <ChevronDown size={11} strokeWidth={3} className={`transition-transform ${storeMenuOpen ? "rotate-180" : ""}`} />
            </button>
          ) : (
            <div
              data-testid={`store-badge-${v.id}`}
              className={`${storeClr} px-2 py-1 flex items-center justify-center gap-1.5 font-display font-black text-[11px] uppercase tracking-[0.18em]`}
              title={`Este carro está na loja ${storeLabel}`}
            >
              <Package size={11} strokeWidth={2.5} /> {storeLabel}
            </div>
          )}
          {storeMenuOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setStoreMenuOpen(false)} />
              <div data-testid={`store-quick-menu-${v.id}`} className="absolute left-0 right-0 top-full z-30 bg-background border border-border shadow-xl">
                <p className="px-2 py-1 text-[9px] uppercase tracking-widest text-text-secondary border-b border-border">Mover para:</p>
                {myDealerships.filter((s) => s.id !== v.dealership_id).map((s) => {
                  const idx = myDealerships.findIndex((x) => x.id === s.id);
                  const raw = (s.short_name || s.name || "").trim();
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`store-quick-opt-${s.id}`}
                      onClick={() => quickMove(s.id)}
                      disabled={switching}
                      className="w-full px-2 py-2 flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-wider hover:bg-bg-elev transition-colors disabled:opacity-50 text-left"
                    >
                      <span className={`w-2 h-2 rounded-full shrink-0 ${storeDots[idx] || "bg-brand"}`} />
                      {switching ? "Movendo..." : raw}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={() => onEdit(v)}
        className="block w-full aspect-[4/3] bg-background relative overflow-hidden"
      >
        {/* Use vehiclePhoto() which filters out source-site placeholders
            (e.g. "VEÍCULO EM PREPARAÇÃO") and falls back to our local
            "NEW ARRIVAL" image automatically. */}
        <img
          src={vehiclePhoto(v)}
          alt={`${v.make} ${v.model}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-1.5 left-1.5 scale-75 origin-top-left"><StatusPill status={v.status} t={t} /></div>
        {/* Days-in-stock chip lives in the bottom-left of the photo so it's
            visible even when the card is very short. Colored bands make old
            cars pop instantly. */}
        <div className="absolute bottom-1.5 left-1.5"><AgingBadge days={daysInStock} /></div>
        {photoCount > 1 && (
          <span className="absolute top-1.5 right-1.5 bg-black/70 text-white text-[9px] px-1.5 py-0.5 inline-flex items-center gap-0.5">
            <ImageIcon size={9} /> {photoCount}
          </span>
        )}
      </button>

      <div className="p-2.5">
        <div className="flex items-start justify-between gap-1 mb-1.5">
          <div className="min-w-0 flex-1">
            <p className="font-display font-bold uppercase truncate text-xs leading-tight">{v.make} {v.model}</p>
            <p className="text-[10px] text-text-secondary truncate">{v.year}{v.color ? ` · ${v.color}` : ""}</p>
            <p className="text-[10px] truncate inline-flex items-center gap-1 mt-0.5">
              <Gauge size={10} className="text-primary shrink-0" />
              {v.mileage > 0 ? (
                <>
                  <span className="font-mono text-white">{Number(v.mileage).toLocaleString("en-US")}</span>
                  <span className="text-text-secondary/70">mi</span>
                </>
              ) : (
                <span className="text-text-secondary">— mi</span>
              )}
            </p>
          </div>
          <div className="flex gap-0.5 shrink-0">
            {onHistory && (
              <button
                data-testid={`history-${v.id}`}
                onClick={(e) => { e.stopPropagation(); onHistory(v.id); }}
                className="w-6 h-6 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
                title={t("vehicle_history")}
              >
                <History size={11} />
              </button>
            )}
            {onSimulate && (
              <button
                data-testid={`sim-${v.id}`}
                onClick={(e) => { e.stopPropagation(); onSimulate(v); }}
                className="w-6 h-6 border border-border hover:border-[#C9A227] hover:text-[#C9A227] flex items-center justify-center transition-colors"
                title="Simulação de financiamento"
              >
                <Calculator size={11} />
              </button>
            )}
            <button
              data-testid={`edit-${v.id}`}
              onClick={(e) => { e.stopPropagation(); onEdit(v); }}
              className="w-6 h-6 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
              title={t("edit")}
            >
              <Edit2 size={11} />
            </button>
            {!isSalesperson && (
              <button
                data-testid={`delete-${v.id}`}
                onClick={(e) => { e.stopPropagation(); onDelete(v.id); }}
                className="w-6 h-6 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
                title={t("delete")}
              >
                <Trash2 size={11} />
              </button>
            )}
          </div>
        </div>

        <div className="pt-2 border-t border-border">
          <p className="font-display font-black text-sm text-primary leading-none">{formatCurrency(v.sale_price)}</p>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status, t }) {
  const cls = status === "sold" ? "border-success text-success" : status === "reserved" ? "border-warning text-warning" : "border-blue-500 text-blue-400";
  return <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border ${cls}`}>{t(status)}</span>;
}

/**
 * AgingBadge — shows how many days a car has been sitting in inventory.
 *
 * Color bands are the industry norm for aging inventory reports at dealers:
 *   0-30d   → success/green  (fresh)
 *   31-60d  → warning/yellow (starting to sit)
 *   61-90d  → orange         (aging — start marketing push)
 *   90d+    → primary/red    (stale — priced too high or forgotten)
 *
 * Uses the same "border + tiny caps" language as the rest of the app so it
 * blends into the existing brutalist design without a redesign.
 */
function AgingBadge({ days }) {
  const d = Math.max(0, Math.round(Number(days) || 0));
  let cls, label;
  if (d <= 30)      { cls = "border-success text-success bg-success/10";           label = `${d}d`; }
  else if (d <= 60) { cls = "border-warning text-warning bg-warning/10";           label = `${d}d`; }
  else if (d <= 90) { cls = "border-orange-500 text-orange-400 bg-orange-500/10";  label = `${d}d`; }
  else              { cls = "border-primary text-primary bg-primary/15";           label = `${d}d`; }
  return (
    <span
      data-testid={`aging-${d}`}
      className={`inline-flex items-center gap-1 px-1.5 h-5 text-[10px] font-mono font-bold border ${cls}`}
      title={`${d} dias em estoque`}
    >
      <Clock size={9} /> {label}
    </span>
  );
}

/**
 * InventorySummaryStrip — one-line health check of the whole stock.
 *
 * Renders 4 KPIs across the top of the inventory tab: total cars, average
 * price, average age, and count of "aging" cars (60+ days). The aging chip
 * is CLICKABLE — pressing it flips a filter that hides everything else so
 * the owner can drill in with one click.
 */
function InventorySummaryStrip({ t, total, avgPrice, avgAge, agingCount, agingOnly, onToggleAging }) {
  return (
    <div data-testid="inventory-summary-strip" className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-6">
      <div className="bg-background p-4">
        <p className="label-eyebrow text-text-secondary mb-1">{t("inv_summary_total")}</p>
        <p className="font-display font-black text-2xl leading-none">{total}</p>
      </div>
      <div className="bg-background p-4">
        <p className="label-eyebrow text-text-secondary mb-1">{t("inv_summary_avg_price")}</p>
        <p className="font-display font-black text-2xl leading-none text-primary">{formatCurrency(avgPrice)}</p>
      </div>
      <div className="bg-background p-4">
        <p className="label-eyebrow text-text-secondary mb-1">{t("inv_summary_avg_age")}</p>
        <p className="font-display font-black text-2xl leading-none">
          {avgAge}<span className="text-sm text-text-secondary ml-1 font-sans">{t("inv_badge_days")}</span>
        </p>
      </div>
      <button
        type="button"
        data-testid="inventory-summary-aging-toggle"
        onClick={onToggleAging}
        className={`bg-background p-4 text-left transition-colors group ${
          agingOnly ? "ring-2 ring-inset ring-warning" : "hover:bg-surface"
        } ${agingCount > 0 ? "cursor-pointer" : "cursor-default"}`}
        disabled={agingCount === 0}
      >
        <p className="label-eyebrow text-text-secondary mb-1 flex items-center gap-1">
          <AlertTriangle size={10} className={agingCount > 0 ? "text-warning" : "text-text-secondary"} />
          {t("inv_summary_aging")}
        </p>
        <p className={`font-display font-black text-2xl leading-none ${agingCount > 0 ? "text-warning" : ""}`}>
          {agingCount}
        </p>
        {agingCount > 0 && !agingOnly && (
          <p className="text-[10px] text-text-secondary mt-1 group-hover:text-warning transition-colors">
            {t("inv_summary_aging_click")}
          </p>
        )}
      </button>
    </div>
  );
}

// Groups delivered vehicles (status=sold, delivery_step=8) into collapsible
// monthly folders so the VENDIDO column doesn't keep growing forever. Newest
// month opens by default; older months are collapsed (one click to expand).
function DeliveredArchive({ vehicles, t, onEdit, onHistory }) {
  const delivered = vehicles.filter(v => v.status === "sold" && (v.delivery_step || 0) === 8);
  // Group by YYYY-MM using delivered_at (preferred) or sold_at (fallback).
  const groups = {};
  for (const v of delivered) {
    const dateStr = v.delivered_at || v.sold_at || "";
    if (!dateStr) continue;
    const key = dateStr.slice(0, 7); // YYYY-MM
    (groups[key] ||= []).push(v);
  }
  const monthKeys = Object.keys(groups).sort((a, b) => b.localeCompare(a)); // newest first
  const currentMonthKey = new Date().toISOString().slice(0, 7);
  const [openKey, setOpenKey] = useState(currentMonthKey);

  const monthLabel = (key) => {
    const [y, m] = key.split("-");
    const dt = new Date(Number(y), Number(m) - 1, 1);
    return dt.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  };

  // Opens the monthly delivery report PDF in a new tab.
  // Endpoint is owner / manager only; the button is hidden for everyone else.
  const exportMonthlyReport = async (key) => {
    const [y, m] = key.split("-");
    try {
      const res = await api.get("/delivery/monthly-report.pdf", {
        params: { year: Number(y), month: Number(m) },
        responseType: "blob",
      });
      openPdfBlob(res.data, `relatorio-entregas-${key}.pdf`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error_generic"));
    }
  };

  // Build the last-6-months series for the inline trend bar chart.
  // Always emits exactly 6 buckets (current + 5 previous) so the chart keeps
  // the same shape even when a month has zero deliveries.
  const trend = [];
  {
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      const short = dt.toLocaleDateString(undefined, { month: "short" }).replace(".", "").toUpperCase();
      trend.push({ key, short, count: (groups[key] || []).length });
    }
  }
  const trendMax = Math.max(1, ...trend.map(b => b.count));
  const currentCount = trend[trend.length - 1].count;
  const prevCount = trend[trend.length - 2].count;
  const deltaPct = prevCount > 0 ? Math.round(((currentCount - prevCount) / prevCount) * 100) : (currentCount > 0 ? 100 : 0);
  const deltaPositive = currentCount >= prevCount;

  if (delivered.length === 0) return null;

  return (
    <div className="mt-10" data-testid="delivered-archive">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="label-eyebrow text-success mb-1">{t("delivered_archive_eyebrow")}</p>
          <h2 className="font-display font-black text-2xl uppercase tracking-tighter">{t("delivered_archive_title")}</h2>
        </div>
        <span className="text-xs text-text-secondary">
          {delivered.length} {t("delivered_archive_count")}
        </span>
      </div>

      {/* 6-month trend mini chart — instant read of "am I delivering more or less than last month?" */}
      <div className="border border-border bg-surface p-4 mb-4" data-testid="delivered-trend-chart">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <p className="label-eyebrow text-text-secondary">{t("delivered_trend_title")}</p>
          <div className="flex items-baseline gap-2 text-xs">
            <span className="text-text-secondary">{t("delivered_trend_vs_prev")}</span>
            <span className={`font-display font-bold ${deltaPositive ? "text-success" : "text-primary"}`}>
              {deltaPositive ? "+" : ""}{deltaPct}%
            </span>
            {deltaPositive ? (
              <ArrowUpRight size={14} className="text-success" />
            ) : (
              <ArrowDownRight size={14} className="text-primary" />
            )}
          </div>
        </div>
        <div className="flex items-end gap-2 h-24">
          {trend.map((b, i) => {
            const isCurrent = i === trend.length - 1;
            const pct = (b.count / trendMax) * 100;
            return (
              <div
                key={b.key}
                data-testid={`delivered-trend-bar-${b.key}`}
                className="flex-1 flex flex-col items-center justify-end gap-1 group"
                title={`${b.short}: ${b.count}`}
              >
                <span className={`font-display font-bold text-[10px] ${isCurrent ? "text-success" : "text-text-secondary"}`}>
                  {b.count}
                </span>
                <div
                  className={`w-full transition-all ${
                    isCurrent
                      ? "bg-success/80 group-hover:bg-success"
                      : "bg-primary/60 group-hover:bg-primary"
                  }`}
                  style={{ height: `${Math.max(pct, 4)}%` }}
                />
                <span className={`text-[9px] uppercase tracking-widest ${isCurrent ? "text-success" : "text-text-secondary"}`}>
                  {b.short}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        {monthKeys.map((key) => {
          const list = groups[key].sort((a, b) => (b.delivered_at || b.sold_at || "").localeCompare(a.delivered_at || a.sold_at || ""));
          const isOpen = openKey === key;
          const isCurrent = key === currentMonthKey;
          return (
            <div key={key} data-testid={`delivered-month-${key}`} className={`border ${isCurrent ? "border-success/40" : "border-border"} bg-surface`}>
              <div className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-background/40 transition-colors group">
                <button
                  type="button"
                  data-testid={`delivered-month-toggle-${key}`}
                  onClick={() => setOpenKey(isOpen ? null : key)}
                  className="flex items-center gap-3 flex-1 text-left"
                >
                  <span className={`label-eyebrow capitalize ${isCurrent ? "text-success" : "text-text-secondary group-hover:text-primary transition-colors"}`}>
                    {monthLabel(key)}
                  </span>
                  {isCurrent && (
                    <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-success/10 border border-success/30 text-success">
                      {t("delivered_archive_current")}
                    </span>
                  )}
                </button>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-text-secondary">
                  <button
                    type="button"
                    data-testid={`delivered-month-export-${key}`}
                    onClick={(e) => { e.stopPropagation(); exportMonthlyReport(key); }}
                    title={t("delivered_archive_export_pdf")}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-success/40 text-success hover:bg-success hover:text-white transition-colors text-[10px]"
                  >
                    <Printer size={11} /> PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpenKey(isOpen ? null : key)}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-border hover:border-primary hover:text-primary transition-colors"
                  >
                    <span className="px-1 py-0">{list.length}</span>
                    {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-border p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {list.map((v) => {
                    const thumb = (v.images && v.images[0]) || v.image || null;
                    const vinTail = v.vin ? String(v.vin).slice(-6).toUpperCase() : "";
                    const deliveredAt = v.delivered_at || v.sold_at;
                    const dateLabel = deliveredAt
                      ? new Date(deliveredAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
                      : null;
                    return (
                      <div key={v.id} data-testid={`delivered-card-${v.id}`} className="bg-background border border-border hover:border-success/50 transition-colors p-3 flex gap-3">
                        {thumb ? (
                          <img src={thumb} alt="" className="w-14 h-14 object-cover border border-border shrink-0" loading="lazy" />
                        ) : (
                          <div className="w-14 h-14 grid place-items-center bg-surface border border-border text-text-secondary shrink-0">
                            <Car size={18} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-display font-bold text-xs truncate">{v.make} {v.model}</p>
                          <p className="text-[10px] text-text-secondary truncate">
                            {v.year}{vinTail ? <> · <span className="font-mono">{vinTail}</span></> : null}
                          </p>
                          {v.buyer_name && (
                            <p className="text-[11px] text-success font-display font-bold truncate" title={v.buyer_name}>
                              {v.buyer_name}
                            </p>
                          )}
                          <div className="mt-1 flex items-baseline justify-between gap-2">
                            <p className="font-display font-bold text-primary text-xs">{formatCurrency(v.sold_price || v.sale_price)}</p>
                            {dateLabel && (
                              <span className="text-[9px] uppercase tracking-widest text-text-secondary font-display font-bold">{dateLabel}</span>
                            )}
                          </div>
                          <div className="mt-1.5 flex gap-1">
                            <button
                              onClick={() => onEdit(v)}
                              className="text-[9px] px-1.5 py-0.5 border border-border hover:border-primary hover:text-primary uppercase tracking-wider transition-colors"
                            >
                              {t("edit")}
                            </button>
                            {onHistory && (
                              <button
                                onClick={() => onHistory(v.id)}
                                className="text-[9px] px-1.5 py-0.5 border border-border hover:border-primary hover:text-primary uppercase tracking-wider transition-colors inline-flex items-center gap-1"
                                title={t("vehicle_history")}
                              >
                                <History size={9} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Pipeline({ vehicles, t, onMove, onEdit, onHistory }) {
  const [dpTotals, setDpTotals] = useState({});
  const [dpDialogVehicle, setDpDialogVehicle] = useState(null);
  // Stale-deal filter — null = show all, 20 = only >20 days, 30 = only >30 days.
  const [staleFilter, setStaleFilter] = useState(null);
  const [search, setSearch] = useState("");

  const reloadTotals = async () => {
    try {
      const r = await api.get("/down-payments/totals");
      setDpTotals(r.data || {});
    } catch {
      /* silent — non-blocking widget */
    }
  };

  useEffect(() => { reloadTotals(); /* eslint-disable-next-line */ }, []);

  // The Sales Pipeline now focuses 100% on deals in progress (sold but not yet
  // delivered). Inventory + Reservados live in their own dedicated tabs. Cars
  // already delivered (step=8) cascade into the monthly DeliveredArchive below.
  const computeDaysOpen = (v) => {
    if (!v.sold_at) return 0;
    const d = new Date(v.sold_at);
    if (Number.isNaN(d.getTime())) return 0;
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24)));
  };

  const allOpen = vehicles
    .filter((v) => v.status === "sold" && (v.delivery_step || 0) < 8)
    .map((v) => ({ ...v, _daysOpen: computeDaysOpen(v) }))
    .sort((a, b) => {
      const ad = a.sold_at || a.updated_at || a.created_at || "";
      const bd = b.sold_at || b.updated_at || b.created_at || "";
      return bd.localeCompare(ad);
    });

  // After we know days-open, count stale ones and filter the visible list.
  const staleCount = allOpen.filter((v) => v._daysOpen > 20).length;
  const veryStaleCount = allOpen.filter((v) => v._daysOpen > 30).length;
  const vendidosByStale = staleFilter == null
    ? allOpen
    : allOpen.filter((v) => v._daysOpen > staleFilter);
  // Lupa — busca por nome do cliente (e carro/VIN/vendedor de bônus)
  const _q = search.trim().toLowerCase();
  const vendidos = !_q ? vendidosByStale : vendidosByStale.filter((v) =>
    (v.buyer_name || "").toLowerCase().includes(_q) ||
    (v.buyer_phone || "").toLowerCase().includes(_q) ||
    (v.salesperson_name || "").toLowerCase().includes(_q) ||
    (v.vin || "").toLowerCase().includes(_q) ||
    `${v.year || ""} ${v.make || ""} ${v.model || ""}`.toLowerCase().includes(_q)
  );

  return (
    <div data-testid="pipeline-tab">
      <p className="label-eyebrow text-primary mb-2">{t("pipeline")}</p>
      <h1 className="font-display font-black text-4xl uppercase tracking-tighter mb-8">{t("sales")}</h1>

      {/* Performance Panel — replaces the dead KPI strip. Tells a story:
          who's winning the month + how the week is pacing. Both halves are
          computed live from `vehicles` — no extra API call. */}
      <PipelinePerformancePanel vehicles={vehicles} allOpen={allOpen} t={t} />

      {/* Stale-deal filter pills */}
      {allOpen.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-[10px] uppercase tracking-widest text-text-secondary mr-1">{t("stale_filter_label")}</span>
          {[
            { value: null, label: t("stale_filter_all"), count: allOpen.length, color: "border-border" },
            { value: 20, label: t("stale_filter_20"), count: staleCount, color: "border-warning/50 text-warning" },
            { value: 30, label: t("stale_filter_30"), count: veryStaleCount, color: "border-primary/50 text-primary" },
          ].map((opt) => {
            const active = staleFilter === opt.value;
            return (
              <button
                key={String(opt.value)}
                type="button"
                data-testid={`stale-filter-${opt.value ?? "all"}`}
                onClick={() => setStaleFilter(opt.value)}
                disabled={opt.value != null && opt.count === 0}
                className={`text-[10px] uppercase tracking-widest font-display font-bold px-3 py-1.5 border transition-colors inline-flex items-center gap-1.5 ${
                  active
                    ? "bg-primary border-primary text-white"
                    : opt.value != null && opt.count === 0
                      ? "border-border text-text-secondary/40 cursor-not-allowed"
                      : `${opt.color} hover:border-primary hover:text-primary`
                }`}
              >
                {opt.label}
                <span className={`text-[9px] px-1.5 py-0 border ${active ? "border-white/40 bg-white/10" : "border-current/30"}`}>{opt.count}</span>
              </button>
            );
          })}
          {staleFilter != null && (
            <button
              type="button"
              data-testid="stale-filter-clear"
              onClick={() => setStaleFilter(null)}
              className="text-[10px] uppercase tracking-widest text-text-secondary hover:text-primary inline-flex items-center gap-1 ml-2"
            >
              <X size={11} /> {t("clear_filters")}
            </button>
          )}
        </div>
      )}

      {/* ============ DEAL CARDS ============ */}
      <div className="border-t-2 border-t-green-500 bg-surface mb-8" data-testid="section-vendido">
        <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="font-display font-bold uppercase text-sm tracking-wider text-success">{t("open_deals_title")}</span>
            <span className="text-[10px] uppercase tracking-widest text-text-secondary">
              {staleFilter != null
                ? (staleFilter === 30 ? t("stale_view_30") : t("stale_view_20"))
                : t("open_deals_subtitle")}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-1 min-w-[220px] justify-end">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
              <input
                data-testid="pipeline-search"
                className="w-full bg-background border border-border focus:border-primary focus:outline-none pl-8 pr-8 h-9 text-sm"
                placeholder={t("sold_search_placeholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button type="button" data-testid="pipeline-search-clear" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-primary">
                  <X size={13} />
                </button>
              )}
            </div>
            <span className="bg-background border border-border px-2 py-0.5 text-xs font-display font-bold">{vendidos.length}</span>
          </div>
        </div>
        <div className="p-4">
          {vendidos.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex w-16 h-16 bg-background border border-border items-center justify-center mb-4">
                {_q ? <Search size={28} className="text-text-secondary" /> : staleFilter != null ? <CheckCircle2 size={28} className="text-success" /> : <Trophy size={28} className="text-text-secondary" />}
              </div>
              <p data-testid="pipeline-search-empty" className="text-text-secondary text-sm">
                {_q ? t("sold_search_empty") : staleFilter != null ? t("stale_empty_good_news") : t("open_deals_empty")}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {vendidos.map((v) => (
                <DealCard
                  key={v.id}
                  v={v}
                  t={t}
                  dpTotals={dpTotals}
                  onMove={onMove}
                  onEdit={onEdit}
                  onHistory={onHistory}
                  onOpenDp={() => setDpDialogVehicle(v)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ============ DELIVERED ARCHIVE — grouped by month ============ */}
      <DeliveredArchive vehicles={vehicles} t={t} onEdit={onEdit} onHistory={onHistory} />

      {dpDialogVehicle && (
        <DownPaymentDialog
          vehicle={dpDialogVehicle}
          t={t}
          onClose={() => { setDpDialogVehicle(null); reloadTotals(); }}
          onChange={() => reloadTotals()}
        />
      )}
    </div>
  );
}

// Pipeline header panel — replaces the dead 5-KPI strip. Two halves that tell
// a story: monthly leaderboard (LEFT) + weekly velocity sparkline (RIGHT).
// Both halves derive from `vehicles` so no extra API call is needed.
function PipelinePerformancePanel({ vehicles, allOpen, t }) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();

  // 1) LEFT — Top 3 salespeople for the CURRENT month (by deal count).
  //    A car counts as "won this month" when sold_at falls in the month.
  const monthClosed = vehicles.filter((v) => {
    if (v.status !== "sold" || !v.sold_at) return false;
    const t0 = new Date(v.sold_at).getTime();
    return Number.isFinite(t0) && t0 >= monthStart;
  });
  const bySp = {};
  for (const v of monthClosed) {
    // Bucket by trimmed name so trailing/internal whitespace doesn't split
    // the same person across multiple rows (was happening with "Rone " vs "Rone").
    const name = (v.salesperson_name || "—").trim().replace(/\s+/g, " ") || "—";
    if (!bySp[name]) bySp[name] = { name, deals: 0, revenue: 0 };
    bySp[name].deals += 1;
    bySp[name].revenue += Number(v.sold_price) || 0;
  }
  // Sort by deals first (more impactful for the salesperson view), break ties by revenue.
  const ranked = Object.values(bySp).sort(
    (a, b) => (b.deals - a.deals) || (b.revenue - a.revenue),
  );
  const leaderboard = ranked.slice(0, 5);  // bumped from 3 → 5 so mid-pack
                                            // salespeople are visible too
  const extraSellers = Math.max(0, ranked.length - leaderboard.length);
  const totalDeals = monthClosed.length;  // grand total, not just the
                                           // slice — gives the owner a
                                           // clean "X vendas este mês" line

  // 2) RIGHT — Last 14-day sparkline of sales count + revenue.
  //    Pre-build 14 day-buckets so the chart shape stays consistent on dry days.
  const days = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
    days.push({ ts: d.getTime(), label: d.getDate(), deals: 0, revenue: 0 });
  }
  for (const v of vehicles) {
    if (v.status !== "sold" || !v.sold_at) continue;
    const t0 = new Date(v.sold_at);
    const stamp = new Date(t0.getFullYear(), t0.getMonth(), t0.getDate()).getTime();
    const bucket = days.find((d) => d.ts === stamp);
    if (bucket) {
      bucket.deals += 1;
      bucket.revenue += Number(v.sold_price) || 0;
    }
  }
  const maxDeals = Math.max(1, ...days.map((d) => d.deals));
  // Sparkline path math
  const sw = 240; // svg width
  const sh = 56;  // svg height
  const step = sw / (days.length - 1);
  const linePoints = days.map((d, i) => {
    const x = i * step;
    const y = sh - (d.deals / maxDeals) * (sh - 8) - 4;
    return [x, y];
  });
  const linePath = linePoints.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const areaPath = linePath + ` L${sw},${sh} L0,${sh} Z`;

  const thisWeekDeals = days.slice(-7).reduce((s, d) => s + d.deals, 0);
  const prevWeekDeals = days.slice(0, 7).reduce((s, d) => s + d.deals, 0);
  const deltaPct = prevWeekDeals > 0
    ? Math.round(((thisWeekDeals - prevWeekDeals) / prevWeekDeals) * 100)
    : (thisWeekDeals > 0 ? 100 : 0);
  const deltaPositive = thisWeekDeals >= prevWeekDeals;

  const monthLabel = now.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div data-testid="pipeline-performance-panel" className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border border border-border mb-6">
      {/* ============== LEFT · LEADERBOARD ============== */}
      <div className="bg-background p-5">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="label-eyebrow text-primary mb-1 inline-flex items-center gap-1">
              <Trophy size={11} /> {t("pipeline_top_sellers_eyebrow")}
            </p>
            <p className="font-display font-black text-lg uppercase tracking-tighter capitalize">{monthLabel}</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] uppercase tracking-widest text-text-secondary">{t("pipeline_deals_closed_eyebrow")}</p>
            <p className="font-display font-black text-primary text-3xl">{monthClosed.length}</p>
            <p className="text-[10px] text-text-secondary mt-0.5">{t("pipeline_deals_closed")}</p>
          </div>
        </div>

        {leaderboard.length === 0 ? (
          <div className="text-center py-8">
            <div className="inline-flex w-12 h-12 bg-surface border border-border items-center justify-center mb-3">
              <Trophy size={20} className="text-text-secondary" />
            </div>
            <p className="text-text-secondary text-xs">{t("pipeline_leaderboard_empty")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {leaderboard.map((sp, i) => {
              const pct = totalDeals > 0 ? (sp.deals / totalDeals) * 100 : 0;
              const rank = i + 1;
              const rankClass =
                rank === 1 ? "bg-warning text-black border-warning"
                : rank === 2 ? "bg-text-secondary/20 text-text-secondary border-text-secondary/40"
                : "bg-primary/15 text-primary border-primary/40";
              const rankIcon = rank === 1 ? <Crown size={11} /> : rank === 2 ? <Medal size={11} /> : <Award size={11} />;
              return (
                <div
                  key={sp.name}
                  data-testid={`leaderboard-rank-${rank}`}
                  className="flex items-center gap-3 bg-surface border border-border p-2.5 hover:border-primary/40 transition-colors"
                >
                  <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 border font-display font-black text-[10px] ${rankClass}`}>
                    {rankIcon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 mb-1.5">
                      <span className="font-display font-bold text-sm truncate" title={sp.name}>{sp.name}</span>
                      <span className="font-display font-black text-primary text-base shrink-0 tabular-nums">
                        {sp.deals} <span className="text-[9px] uppercase tracking-widest text-text-secondary font-bold">{sp.deals === 1 ? t("pipeline_deal") : t("pipeline_deals")}</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-1 bg-background border border-border overflow-hidden flex-1">
                        <div
                          className={`h-full transition-all ${rank === 1 ? "bg-warning" : rank === 2 ? "bg-text-secondary" : "bg-primary"}`}
                          style={{ width: `${Math.max(pct, 4)}%` }}
                        />
                      </div>
                      <span className="text-[9px] uppercase tracking-widest text-text-secondary whitespace-nowrap tabular-nums">
                        {pct.toFixed(0)}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            {extraSellers > 0 && (
              <p className="text-[10px] text-text-secondary text-center mt-2 uppercase tracking-widest">
                + {extraSellers} {extraSellers === 1 ? "vendedor" : "vendedores"} fora do top 5
              </p>
            )}
          </div>
        )}
      </div>

      {/* ============== RIGHT · VELOCITY SPARKLINE ============== */}
      <div className="bg-background p-5">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="label-eyebrow text-success mb-1 inline-flex items-center gap-1">
              <Activity size={11} /> {t("pipeline_velocity_eyebrow")}
            </p>
            <p className="font-display font-black text-lg uppercase tracking-tighter">{t("pipeline_velocity_title")}</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] uppercase tracking-widest text-text-secondary">{t("pipeline_this_week")}</p>
            <p className="font-display font-black text-success text-xl">{thisWeekDeals}</p>
            <p className={`text-[10px] inline-flex items-center gap-1 font-display font-bold ${deltaPositive ? "text-success" : "text-primary"}`}>
              {deltaPositive ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
              {deltaPositive ? "+" : ""}{deltaPct}% {t("pipeline_vs_prev_week")}
            </p>
          </div>
        </div>

        {/* Sparkline */}
        <div className="bg-surface border border-border p-3 mb-3">
          <svg
            viewBox={`0 0 ${sw} ${sh}`}
            preserveAspectRatio="none"
            className="w-full h-16"
            data-testid="velocity-sparkline"
          >
            <defs>
              <linearGradient id="sparkFill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="rgb(16 185 129)" stopOpacity="0.35" />
                <stop offset="100%" stopColor="rgb(16 185 129)" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Subtle midline */}
            <line x1="0" y1={sh / 2} x2={sw} y2={sh / 2} stroke="rgba(255,255,255,0.05)" strokeDasharray="2 2" />
            <path d={areaPath} fill="url(#sparkFill)" />
            <path d={linePath} fill="none" stroke="rgb(16 185 129)" strokeWidth="1.5" strokeLinejoin="round" />
            {linePoints.map(([x, y], i) => (
              <circle
                key={i}
                cx={x}
                cy={y}
                r={days[i].deals > 0 ? 2.5 : 1.5}
                fill={i === linePoints.length - 1 ? "rgb(16 185 129)" : "rgba(16,185,129,0.6)"}
                stroke={i === linePoints.length - 1 ? "rgb(255,255,255)" : "transparent"}
                strokeWidth="0.8"
              />
            ))}
          </svg>
          <div className="flex justify-between text-[9px] uppercase tracking-widest text-text-secondary mt-1.5 px-0.5">
            <span>-14d</span>
            <span>-7d</span>
            <span>{t("pipeline_today")}</span>
          </div>
        </div>

        {/* Bottom stats strip — count-only, no revenue (per owner's request) */}
        <div className="grid grid-cols-2 gap-px bg-border border border-border">
          <div className="bg-surface p-2.5">
            <p className="text-[9px] uppercase tracking-widest text-text-secondary mb-1">{t("pipeline_open_deals_short")}</p>
            <p className="font-display font-black text-success text-sm">{allOpen.length}</p>
          </div>
          <div className="bg-surface p-2.5">
            <p className="text-[9px] uppercase tracking-widest text-text-secondary mb-1">{t("pipeline_prev_week")}</p>
            <p className="font-display font-black text-text-secondary text-sm">{prevWeekDeals}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Deal-focused card: emphasises buyer + sold price + payment status + commission.
// Photo is a thumbnail (not hero) because in this view the data matters more
// than the car. 3-per-row at xl, 2 at md, 1 mobile.
function DealCard({ v, t, dpTotals, onMove, onEdit, onHistory, onOpenDp }) {
  const thumb = (v.images && v.images[0]) || v.image || null;
  const vinTail = v.vin ? String(v.vin).slice(-6).toUpperCase() : "";
  const agreed = Number(v.down_payment) || 0;
  const paid = Number(dpTotals[v.id]?.paid) || 0;
  const showDpStatus = agreed > 0;
  const balance = Math.max(agreed - paid, 0);
  const fullyPaid = agreed > 0 && balance <= 0;
  const pct = agreed > 0 ? Math.min(100, Math.round((paid / agreed) * 100)) : 0;
  const soldAt = v.sold_at ? new Date(v.sold_at) : null;
  const soldDate = soldAt
    ? soldAt.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
    : null;
  // Aging — how long has this deal been open? Useful to spot stale ones.
  const daysOpen = soldAt
    ? Math.max(0, Math.floor((Date.now() - soldAt.getTime()) / (1000 * 60 * 60 * 24)))
    : null;
  const ageColor =
    daysOpen == null ? "text-text-secondary"
    : daysOpen > 30 ? "text-primary"
    : daysOpen > 14 ? "text-warning"
    : "text-text-secondary";
  const commission = Number(v.commission_amount) || 0;
  const commissionPaid = !!v.commission_paid;
  // Visual stale-deal escalation — softens at >14d, alarms at >20d, hard alarm >30d.
  const isStale = daysOpen != null && daysOpen > 20;
  const isVeryStale = daysOpen != null && daysOpen > 30;

  return (
    <div
      data-testid={`card-${v.id}`}
      data-stale={isStale || undefined}
      className={`bg-background border transition-colors overflow-hidden flex flex-col relative ${
        isVeryStale
          ? "border-primary shadow-[0_0_0_1px_rgba(217,45,32,0.4)]"
          : isStale
            ? "border-warning/70"
            : "border-border hover:border-primary/60"
      }`}
    >
      {isStale && (
        <span
          data-testid={`stale-badge-${v.id}`}
          className={`absolute top-2 right-2 z-10 text-[8.5px] uppercase tracking-widest font-display font-black px-1.5 py-0.5 inline-flex items-center gap-1 ${
            isVeryStale ? "bg-primary text-white" : "bg-warning text-black"
          }`}
          title={t("deal_stale_hint")}
        >
          <AlertTriangle size={9} /> {daysOpen}d
        </span>
      )}
      {/* Top strip: photo (left) + buyer hero (right) */}
      <div className="flex gap-3 p-3 border-b border-border">
        <div className="w-24 h-20 shrink-0 bg-surface border border-border overflow-hidden">
          {thumb ? (
            <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full grid place-items-center text-text-secondary">
              <Car size={22} />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-sm truncate" title={`${v.make} ${v.model}`}>{v.make} {v.model}</p>
          <p className="text-[10px] text-text-secondary truncate">
            {v.year}
            {v.color ? <> · {v.color}</> : null}
            {vinTail ? <> · <span className="font-mono">{vinTail}</span></> : null}
          </p>
          {v.buyer_name && (
            <p
              className="text-sm text-success font-display font-bold mt-1 truncate inline-flex items-center gap-1"
              data-testid={`buyer-${v.id}`}
              title={v.buyer_name}
            >
              <User size={11} className="shrink-0" /> {v.buyer_name}
            </p>
          )}
        </div>
      </div>

      {/* Price row */}
      <div className="px-3 py-2.5 flex items-baseline justify-between gap-2 border-b border-border">
        <span className="font-display font-black text-primary text-xl">{formatCurrency(v.sold_price || v.sale_price)}</span>
        <div className="flex items-center gap-2 text-[9px] uppercase tracking-widest">
          {soldDate && (
            <span className="px-1.5 py-0.5 bg-surface border border-border text-text-secondary font-display font-bold">
              {soldDate}
            </span>
          )}
          {daysOpen != null && (
            <span className={`inline-flex items-center gap-1 font-display font-bold ${ageColor}`} title={t("deal_days_open_hint")}>
              <Clock size={9} /> {daysOpen}d
            </span>
          )}
        </div>
      </div>

      {/* Payment chips */}
      {(v.payment_method || v.bank_name) && (
        <div className="px-3 py-2 flex flex-wrap gap-1.5 border-b border-border">
          {v.payment_method && (
            <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-surface border border-border text-text-secondary">
              {v.payment_method}
            </span>
          )}
          {v.bank_name && (
            <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-primary/10 border border-primary/30 text-primary truncate max-w-[200px]" title={v.bank_name}>
              {v.bank_name}
            </span>
          )}
        </div>
      )}

      {/* Down-payment widget */}
      {showDpStatus && (
        <div
          className={`mx-3 my-2 px-2.5 py-2 border ${fullyPaid ? "border-success/50 bg-success/[0.06]" : "border-warning/40 bg-warning/[0.05]"}`}
          data-testid={`dp-status-${v.id}`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className={`text-[9px] uppercase tracking-widest font-display font-bold ${fullyPaid ? "text-success" : "text-warning"}`}>
              {fullyPaid ? (t("dp_fully_paid") || "Entrada Quitada") : (t("dp_entrance") || "Entrada")}
            </span>
            <span className="text-[10px] font-mono text-text-secondary">{pct}%</span>
          </div>
          <div className="h-1.5 bg-background border border-border overflow-hidden mb-1">
            <div
              className={`h-full transition-all ${fullyPaid ? "bg-success" : "bg-warning"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-text-secondary">{formatCurrency(paid)} / {formatCurrency(agreed)}</span>
            {!fullyPaid && (
              <span className="text-warning font-display font-bold">{t("dp_owes") || "Falta"} {formatCurrency(balance)}</span>
            )}
          </div>
          <button
            data-testid={`dp-open-${v.id}`}
            onClick={onOpenDp}
            className={`mt-2 w-full text-[10px] uppercase tracking-widest font-display font-bold py-1.5 border transition-colors ${
              fullyPaid
                ? "border-success/40 text-success hover:bg-success hover:text-white"
                : "border-success/40 bg-success/15 text-success hover:bg-success hover:text-white"
            }`}
          >
            <HandCoins size={11} className="inline mr-1 -mt-px" />
            {fullyPaid ? (t("dp_view_history") || "Ver Histórico") : (t("dp_add_payment") || "Adicionar Pagamento")}
          </button>
        </div>
      )}

      {/* Salesperson + commission */}
      {(v.salesperson_name || commission > 0) && (
        <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-border text-[11px]">
          <span className="inline-flex items-center gap-1.5 text-text-secondary truncate" title={v.salesperson_name || ""}>
            <Trophy size={11} className="text-warning shrink-0" />
            <NameWithAvatar name={v.salesperson_name || "—"} size="sm" />
          </span>
          {commission > 0 && (
            <span className={`text-[9px] uppercase tracking-widest font-display font-bold px-1.5 py-0.5 border whitespace-nowrap ${
              commissionPaid
                ? "border-success/40 text-success bg-success/10"
                : "border-warning/40 text-warning bg-warning/[0.05]"
            }`}>
              {formatCurrency(commission)} · {commissionPaid ? (t("commission_paid") || "Paga") : (t("commission_pending") || "Pendente")}
            </span>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="px-3 py-2 mt-auto flex flex-wrap gap-1">
        <button
          data-testid={`move-${v.id}-in_stock`}
          onClick={() => onMove(v.id, "in_stock")}
          className="text-[9px] px-2 py-1 border border-border hover:border-primary hover:text-primary uppercase tracking-wider transition-colors"
        >
          → {t("in_stock")}
        </button>
        <button
          onClick={() => onEdit(v)}
          className="text-[9px] px-2 py-1 border border-border hover:border-primary hover:text-primary uppercase tracking-wider transition-colors"
        >
          {t("edit")}
        </button>
        {onHistory && (
          <button
            onClick={() => onHistory(v.id)}
            title={t("vehicle_history")}
            className="text-[9px] px-2 py-1 border border-border hover:border-primary hover:text-primary uppercase tracking-wider transition-colors inline-flex items-center gap-1"
          >
            <History size={9} /> {t("vehicle_history")}
          </button>
        )}
      </div>
    </div>
  );
}

function SettingsTab({ dealership, t, onRefresh }) {
  const [copied, setCopied] = useState(false);
  if (!dealership) return null;
  const apiUrl = `${PUBLIC_API_BASE}/inventory?token=${dealership.api_token}`;
  const copy = () => {
    navigator.clipboard.writeText(apiUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(t("copied"));
  };
  const regen = async () => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.post("/dealership/regenerate-token"); await onRefresh(); toast.success(t("saved")); }
    catch { toast.error(t("error_generic")); }
  };

  return (
    <div data-testid="settings-tab" className="max-w-3xl">
      <p className="label-eyebrow text-primary mb-2">{t("settings")}</p>
      <h1 className="font-display font-black text-4xl uppercase tracking-tighter mb-10">{dealership.name}</h1>

      {/* Dealership profile editor — logo, name, address, phone, website, theme */}
      <DealershipProfileSection dealership={dealership} onRefresh={onRefresh} t={t} />

      {/* Other stores in the group — full-profile editor per store + "create new" flow.
          Lets the owner manage every dealership in one place instead of switching first. */}
      <OtherStoresSection activeDealershipId={dealership.id} onRefresh={onRefresh} />

      <SignatureSection dealership={dealership} onRefresh={onRefresh} />

      <div className="border border-border p-6">
        <p className="label-eyebrow text-primary mb-2">{t("public_api")}</p>
        <p className="text-text-secondary text-sm mb-6">{t("api_description")}</p>

        <label className="label-eyebrow block mb-2">Endpoint URL</label>
        <div className="flex gap-2 mb-4">
          <input data-testid="api-url" readOnly value={apiUrl} className="flex-1 bg-surface border border-border px-4 h-11 text-xs font-mono" />
          <button data-testid="copy-url" onClick={copy} className="px-4 border border-border hover:border-primary hover:text-primary transition-colors text-xs uppercase tracking-wider inline-flex items-center gap-2">
            <Copy size={14} /> {copied ? t("copied") : t("copy")}
          </button>
        </div>

        <button data-testid="regen-token" onClick={regen} className="text-xs px-4 py-2 border border-border hover:border-primary hover:text-primary transition-colors uppercase tracking-wider inline-flex items-center gap-2">
          <RefreshCw size={14} /> {t("regenerate")}
        </button>
      </div>

      {/* Team-wide email notifications toggle */}
      <NotificationsSection t={t} />

      {/* Team management (salespeople + BDC + permissions) */}
      <TeamSection t={t} />
    </div>
  );
}

// Owner toggle for the team-wide email notifications stream.
// Reads / writes /api/dealership/notifications. Owner-only — the API
// already enforces this, but we hide the section for non-owners.
function NotificationsSection({ t }) {
  const [enabled, setEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/settings/test-email");
      setTestResult(r.data);
      (r.data?.sent ? toast.success : toast.warning)(r.data?.message || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error_generic"));
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/dealership/notifications");
        setEnabled(!!r.data?.team_events);
      } catch { /* silent */ }
      setLoaded(true);
    })();
  }, []);

  const toggle = async () => {
    setSaving(true);
    try {
      const r = await api.put("/dealership/notifications", { team_events: !enabled });
      setEnabled(!!r.data?.team_events);
      toast.success(t("saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error_generic"));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;
  return (
    <div className="border border-border p-6 mt-6" data-testid="notifications-section">
      <p className="label-eyebrow text-primary mb-2">{t("notifications_title")}</p>
      <p className="text-text-secondary text-sm mb-6">{t("notifications_description")}</p>

      <div className="flex items-start justify-between gap-4 bg-surface border border-border p-4">
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-sm">{t("notifications_team_events_title")}</p>
          <p className="text-xs text-text-secondary mt-1 leading-snug">{t("notifications_team_events_hint")}</p>
          <ul className="text-[11px] text-text-secondary mt-2 space-y-0.5 leading-snug">
            <li>· {t("notif_event_sold")}</li>
            <li>· {t("notif_event_funded")}</li>
            <li>· {t("notif_event_delivered")}</li>
            <li>· {t("notif_event_application")}</li>
            <li>· {t("notif_event_down_payment")}</li>
            <li>· {t("notif_event_installment")}</li>
            <li>· {t("notif_event_lead")}</li>
          </ul>
        </div>
        <button
          type="button"
          data-testid="notifications-toggle"
          onClick={toggle}
          disabled={saving}
          aria-pressed={enabled}
          className={`relative shrink-0 w-14 h-7 border transition-colors ${
            enabled ? "bg-success border-success" : "bg-surface border-border"
          } ${saving ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
        >
          <span
            className={`absolute top-0.5 ${enabled ? "left-7" : "left-0.5"} w-5 h-5 bg-white border border-border transition-all duration-200`}
          />
        </button>
      </div>
      <p className="text-[10px] uppercase tracking-widest text-text-secondary mt-3">
        {enabled ? t("notifications_status_on") : t("notifications_status_off")}
      </p>
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          data-testid="send-test-email"
          onClick={sendTest}
          disabled={testing}
          className="px-4 h-10 border border-border hover:border-success hover:text-success text-[11px] font-display font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Mail size={13} /> {testing ? "…" : t("test_email_btn")}
        </button>
        {testResult && (
          <span data-testid="test-email-result" className={`text-xs ${testResult.sent ? "text-success" : "text-warning"}`}>
            {testResult.message}
          </span>
        )}
      </div>
    </div>
  );
}

const PERMISSION_LABELS = {
  overview: { key: "dashboard", icon: "🏠" },
  inventory: { key: "inventory", icon: "📦" },
  pipeline: { key: "pipeline", icon: "🚗" },
  delivery: { key: "delivery", icon: "🚚" },
  leads: { key: "leads_title", icon: "📞" },
  salespeople: { key: "salespeople", icon: "🏆" },
  financial: { key: "financial", icon: "💰" },
  post_sales: { key: "post_sales_tab", icon: "🛠️" },
  applications: { key: "applications_tab", icon: "📝" },
  receivables: { key: "receivables_tab", icon: "💵" },
};

function TeamMemberAvatarUploader({ member, t, onChanged, disabled = false }) {
  const [uploading, setUploading] = useState(false);
  const photoMap = useTeamPhotos();
  const onPick = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { photo_url, photo_public_id } = await uploadProfilePhoto(file);
      await api.put(`/team/${member.id}/photo`, { photo_url, photo_public_id });
      toast.success(t("saved"));
      photoMap.refresh && photoMap.refresh();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.message || t("error_generic"));
    } finally { setUploading(false); }
  };
  return (
    <label className={`relative ${disabled ? "" : "cursor-pointer group"} shrink-0`} title={disabled ? "" : t("change_photo")}>
      <Avatar src={member.photo_url} name={member.full_name} size="lg" testid={`team-avatar-${member.id}`} />
      {!disabled && (
        <div className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          {uploading ? <span className="text-[10px] text-white">...</span> : <Upload size={14} className="text-white" />}
        </div>
      )}
      <input data-testid={`team-photo-input-${member.id}`} type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} disabled={uploading || disabled} />
    </label>
  );
}

function TeamSection({ t }) {
  const { user: currentUser } = useAuth();
  const [team, setTeam] = useState({ members: [], all_permissions: [], role_defaults: {} });
  const [salespeople, setSalespeople] = useState([]);
  const [editingMember, setEditingMember] = useState(null); // {} new, member edit
  const [diagnosing, setDiagnosing] = useState(null); // member being diagnosed
  const [savingPerms, setSavingPerms] = useState(null); // memberId being saved

  const reload = async () => {
    try {
      const [tm, sp] = await Promise.all([api.get("/team"), api.get("/salespeople")]);
      setTeam(tm.data);
      setSalespeople(sp.data);
    } catch { /* noop */ }
  };
  useEffect(() => { reload(); }, []);

  const togglePerm = async (member, permKey) => {
    const current = member.effective_permissions || [];
    const next = current.includes(permKey) ? current.filter(p => p !== permKey) : [...current, permKey];
    setSavingPerms(member.id);
    try {
      await api.put(`/team/${member.id}`, { permissions: next });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { setSavingPerms(null); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/team/${id}`); toast.success(t("saved")); reload(); }
    catch { toast.error(t("error_generic")); }
  };

  return (
    <div className="border border-border p-6 mt-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <p className="label-eyebrow text-primary mb-1">{t("team_title")}</p>
          <p className="text-text-secondary text-sm">{t("team_hint")}</p>
        </div>
        <button
          data-testid="add-team"
          type="button"
          onClick={() => setEditingMember({})}
          className="bg-primary hover:bg-primary-hover px-4 py-2.5 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2"
        >
          <UserPlus size={14} /> {t("add_team_member")}
        </button>
      </div>

      {team.members.length === 0 ? (
        <p className="text-text-secondary text-sm border border-dashed border-border py-8 text-center">{t("no_team_members")}</p>
      ) : (
        <div className="space-y-3">
          {team.members.map(m => {
            const perms = m.effective_permissions || [];
            const isSaving = savingPerms === m.id;
            const isSelf = currentUser?.id === m.id;
            const isOwnerRow = m.role === "owner";
            return (
              <div key={m.id} data-testid={`team-row-${m.id}`} className={`border border-border p-4 ${isSaving ? "opacity-50" : ""}`}>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <TeamMemberAvatarUploader member={m} t={t} onChanged={reload} disabled={isSelf} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-display font-bold uppercase">{m.full_name}</p>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${
                          m.role === "owner"
                            ? "border-yellow-500 text-yellow-400 bg-yellow-500/10"
                            : m.role === "bdc"
                            ? "border-cyan-500 text-cyan-400 bg-cyan-500/10"
                            : m.role === "gerente"
                            ? "border-amber-500 text-amber-400 bg-amber-500/10"
                            : m.role === "geral"
                            ? "border-emerald-500 text-emerald-400 bg-emerald-500/10"
                            : "border-primary text-primary bg-primary/10"
                        }`}>
                          {m.role === "owner" ? t("owner_role") : m.role === "bdc" ? "BDC" : m.role === "gerente" ? t("manager") : m.role === "geral" ? t("general_role") : m.role === "fi" ? t("fi_role") : t("salesperson")}
                        </span>
                        {isSelf && (
                          <span className="text-[9px] uppercase tracking-widest text-text-secondary">{t("self_badge")}</span>
                        )}
                      </div>
                      <p className="text-xs text-text-secondary font-mono">{m.email}</p>
                      {m.salesperson_name && <p className="text-xs text-text-secondary mt-1">→ {m.salesperson_name}</p>}
                    </div>
                  </div>
                  {!isSelf && (
                    <div className="inline-flex gap-1">
                      <button data-testid={`diagnose-member-${m.id}`} onClick={() => setDiagnosing(m)} className="w-8 h-8 border border-border hover:border-success hover:text-success flex items-center justify-center transition-colors" title={t("diag_title")}>
                        <Stethoscope size={13} />
                      </button>
                      <button onClick={() => setEditingMember(m)} className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors" title={t("edit")}>
                        <Edit2 size={13} />
                      </button>
                      <button onClick={() => remove(m.id)} className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors" title={t("delete")}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>

                {isOwnerRow ? (
                  <div className="border-t border-border pt-3">
                    <p className="text-xs text-text-secondary">{t("owner_full_access_note")}</p>
                  </div>
                ) : (
                  <div className="border-t border-border pt-3">
                    <p className="label-eyebrow text-[10px] mb-2">{t("permissions_label")}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {team.all_permissions.map(p => {
                        const meta = PERMISSION_LABELS[p] || { key: p, icon: "·" };
                        const enabled = perms.includes(p);
                        return (
                        <button
                          key={p}
                          type="button"
                          data-testid={`perm-${m.id}-${p}`}
                          onClick={() => togglePerm(m, p)}
                          disabled={isSaving}
                          className={`px-2.5 py-1.5 text-xs font-display font-bold uppercase tracking-wider border transition-colors inline-flex items-center gap-1.5 ${
                            enabled
                              ? "border-success text-success bg-success/10"
                              : "border-border text-text-secondary hover:border-primary"
                          }`}
                        >
                          <span className="text-base leading-none">{meta.icon}</span>
                          {t(meta.key)}
                          {enabled && <Check size={11} />}
                        </button>
                      );
                    })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {diagnosing && (
        <TeamDiagnoseModal
          member={diagnosing}
          t={t}
          onClose={() => { setDiagnosing(null); reload(); }}
        />
      )}

      {editingMember && (
        <TeamMemberForm
          member={editingMember.id ? editingMember : null}
          allPermissions={team.all_permissions}
          roleDefaults={team.role_defaults}
          salespeople={salespeople}
          existingTeam={team.members}
          t={t}
          onClose={() => setEditingMember(null)}
          onSaved={() => { setEditingMember(null); reload(); }}
        />
      )}
    </div>
  );
}

function TeamMemberForm({ member, allPermissions, roleDefaults, salespeople, existingTeam, t, onClose, onSaved }) {
  const isEdit = !!member;
  const photoMap = useTeamPhotos();
  const [form, setForm] = useState({
    full_name: member?.full_name || "",
    email: member?.email || "",
    password: "",
    role: member?.role || "salesperson",
    salesperson_id: member?.salesperson_id || "",
    permissions: member?.effective_permissions || roleDefaults?.salesperson || [],
    photo_url: member?.photo_url || "",
    photo_public_id: member?.photo_public_id || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggle = (perm) => {
    setForm(f => {
      const has = f.permissions.includes(perm);
      return { ...f, permissions: has ? f.permissions.filter(p => p !== perm) : [...f.permissions, perm] };
    });
  };

  // Apply role defaults when user changes role (only when creating)
  const applyRoleDefaults = (newRole) => {
    set("role", newRole);
    if (!isEdit) set("permissions", roleDefaults[newRole] || []);
  };

  // Handles both create (upload now, save URL on submit) and edit (upload + persist immediately).
  const onPickPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const { photo_url, photo_public_id } = await uploadProfilePhoto(file);
      setForm(f => ({ ...f, photo_url, photo_public_id }));
      if (isEdit) {
        await api.put(`/team/${member.id}/photo`, { photo_url, photo_public_id });
        photoMap.refresh && photoMap.refresh();
        toast.success(t("saved"));
      }
    } catch (err) {
      toast.error(err.message || t("error_generic"));
    } finally { setUploadingPhoto(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const upd = {
          full_name: form.full_name,
          email: form.email,
          permissions: form.permissions,
        };
        if (form.password) upd.password = form.password;
        await api.put(`/team/${member.id}`, upd);
      } else {
        // Backend now auto-creates the salespeople record when role=salesperson and no salesperson_id is given
        await api.post("/team", form);
      }
      toast.success(t("saved"));
      photoMap.refresh && photoMap.refresh();
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-12 px-4">
      <form onSubmit={submit} className="bg-background border border-border w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl uppercase tracking-tight">
            {isEdit ? t("edit_team_member") : t("add_team_member")}
          </h2>
          <button type="button" onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>

        {!isEdit && (
          <div>
            <label className="label-eyebrow block mb-2">{t("role")}</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "salesperson", icon: Trophy, label: t("salesperson") },
                { id: "bdc", icon: Headphones, label: "BDC" },
                { id: "gerente", icon: Crown, label: t("manager") },
                { id: "geral", icon: Wrench, label: t("general_role") },
                { id: "fi", icon: HandCoins, label: t("fi_role") },
                { id: "owner", icon: ShieldCheck, label: t("owner_role"), full: true },
              ].map(({ id, icon: Icon, label, full }) => (
                <button
                  key={id}
                  type="button"
                  data-testid={`role-${id}`}
                  onClick={() => applyRoleDefaults(id)}
                  className={`flex items-center justify-center gap-2 px-4 py-3 text-xs font-display font-bold uppercase tracking-widest border transition-colors ${full ? "col-span-2" : ""} ${
                    form.role === id ? "border-primary text-primary bg-primary/10" : "border-border text-text-secondary hover:border-primary/60"
                  }`}
                >
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
            {form.role === "owner" && (
              <p className="text-[11px] text-text-secondary mt-2">{t("owner_role_hint")}</p>
            )}
          </div>
        )}

        {/* Photo + Name side-by-side (Monday-style) */}
        <div>
          <label className="label-eyebrow block mb-2">{t("full_name")}</label>
          <div className="flex items-center gap-3">
            <label
              className="relative cursor-pointer group shrink-0"
              title={t("change_photo")}
              data-testid="tm-photo-picker"
            >
              <Avatar
                src={form.photo_url}
                name={form.full_name || t("full_name")}
                size="lg"
                testid="tm-photo-preview"
              />
              <div className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                {uploadingPhoto
                  ? <span className="text-[10px] text-white">...</span>
                  : <Upload size={14} className="text-white" />}
              </div>
              <input
                data-testid="tm-photo-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onPickPhoto(e.target.files?.[0])}
                disabled={uploadingPhoto || saving}
              />
            </label>
            <input
              data-testid="tm-name"
              required
              value={form.full_name}
              onChange={(e) => set("full_name", e.target.value)}
              placeholder={t("full_name")}
              className="flex-1 bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
            />
          </div>
          <p className="text-[11px] text-text-secondary mt-1.5">{t("team_photo_hint")}</p>
        </div>

        <div>
          <label className="label-eyebrow block mb-2">{t("email")}</label>
          <input data-testid="tm-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm" />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">
            {t("password")} {isEdit && <span className="text-text-secondary normal-case">({t("leave_blank_to_keep")})</span>}
          </label>
          <input data-testid="tm-password" type="password" required={!isEdit} value={form.password} onChange={(e) => set("password", e.target.value)} className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm" />
        </div>

        <div>
          <p className="label-eyebrow mb-2">{t("permissions_label")}</p>
          <p className="text-xs text-text-secondary mb-3">{t("permissions_hint")}</p>
          <div className="grid grid-cols-2 gap-2">
            {allPermissions.map(p => {
              const meta = PERMISSION_LABELS[p] || { key: p, icon: "·" };
              const enabled = form.permissions.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  data-testid={`tm-perm-${p}`}
                  onClick={() => toggle(p)}
                  className={`flex items-center gap-2 px-3 py-2.5 border transition-colors text-sm ${
                    enabled
                      ? "border-success text-success bg-success/10"
                      : "border-border text-text-secondary hover:border-primary/60"
                  }`}
                >
                  <span className="text-lg leading-none">{meta.icon}</span>
                  <span className="font-display font-bold uppercase text-xs tracking-wider flex-1 text-left">{t(meta.key)}</span>
                  {enabled ? <Check size={14} /> : <span className="w-3.5 h-3.5 border border-current rounded-sm" />}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-3 border-t border-border">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-border hover:border-primary text-xs font-display font-bold uppercase tracking-widest transition-colors">{t("cancel")}</button>
          <button type="submit" data-testid="tm-submit" disabled={saving} className="bg-primary hover:bg-primary-hover disabled:opacity-50 px-5 py-2.5 text-xs font-display font-bold uppercase tracking-widest transition-colors">
            {saving ? "..." : t("save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function LeadSourcePicker({ value, set, t }) {
  // Ordered by likely usage in the dealership. Icon = emoji for visual scanability.
  const SOURCES = [
    ["walk_in",         "🚶", "Walking"],
    ["referral",        "🤝", "Indicação"],
    ["bdc",             "📞", "BDC"],
    ["facebook",        "📘", "Facebook"],
    ["instagram",       "📸", "Instagram"],
    ["whatsapp",        "💬", "WhatsApp"],
    ["marketplace",     "🛒", "Marketplace"],
    ["tiktok",          "🎵", "TikTok"],
    ["youtube",         "▶️", "YouTube"],
    ["google_ads",      "🔍", "Google Ads"],
    ["cargurus",        "🚗", "CarGurus"],
    ["carfax",          "📄", "Carfax"],
    ["craigslist",      "📰", "Craigslist"],
    ["website",         "🌐", "Site próprio"],
    ["phone",           "☎️", "Telefone"],
    ["repeat_customer", "🔁", "Cliente antigo"],
    ["other",           "•",  "Outro"],
  ];
  const label = t("lead_source_label") || "Como o cliente chegou até a loja?";
  const hint  = t("lead_source_hint")  || "Nos ajuda a saber qual estratégia de venda está funcionando melhor.";
  return (
    <div className="rounded-lg border border-brand/30 bg-brand/[0.04] p-4">
      <div className="flex items-baseline justify-between mb-2 gap-3">
        <div>
          <div className="label-eyebrow text-brand">{label}</div>
          <div className="text-[11px] text-text-secondary mt-0.5">{hint}</div>
        </div>
        {value && (
          <button type="button" onClick={() => set("")} className="text-[11px] text-text-secondary hover:text-brand underline">
            limpar
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {SOURCES.map(([key, icon, defaultLabel]) => {
          const active = value === key;
          const displayLabel = t(`lead_source_${key}`) || defaultLabel;
          return (
            <button
              key={key}
              type="button"
              onClick={() => set(active ? "" : key)}
              data-testid={`lead-source-${key}`}
              className={`px-3 py-1.5 rounded-full border text-sm font-display font-medium transition-all ${
                active
                  ? "bg-brand text-white border-brand shadow-[0_0_0_2px_rgba(217,45,32,0.15)]"
                  : "bg-bg-elev border-border text-text-primary hover:border-brand/50 hover:bg-brand/5"
              }`}
            >
              <span className="mr-1">{icon}</span>{displayLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function VehicleForm({ vehicle, prefill, salespeople = [], isSalesperson, onClose, onSaved, t, myDealerships = [], activeDealershipId }) {
  const isEdit = !!vehicle;
  const initial = vehicle || {
    make: prefill?.make || "", model: prefill?.model || "", year: prefill?.year || 2024, color: "", vin: prefill?.vin || "",
    mileage: prefill?.mileage || 0,
    transmission: "Automatic", fuel_type: "Gasoline", body_type: "Sedan",
    purchase_price: 0, sale_price: prefill?.price || 0, expenses: 0, description: prefill?.description || "",
    images: [], status: "in_stock", buyer_name: "", buyer_phone: "", buyer_email: "", buyer_address: "", buyer_dob: "", lead_source: "", payment_method: "", sold_price: 0, bank_name: "",
    finance_term_months: 0, finance_installment_amount: 0, payment_frequency: "monthly", warranty_months: 0, charge_doc_fee: true,
    trade_in_make: "", trade_in_model: "", trade_in_year: 0, trade_in_value: 0, trade_in_payoff_amount: 0, trade_in_payoff_bank: "",
    salesperson_id: "", salesperson_name: "",
    commission_amount: 0, commission_paid: false,
    floor_plan_id: "",
  };
  const [form, setForm] = useState(initial);
  const [floorPlans, setFloorPlans] = useState([]);
  const [photos, setPhotos] = useState(
    vehicle?.images?.length
      ? vehicle.images
      : (prefill?.images?.length ? prefill.images : (prefill?.image ? [prefill.image] : []))
  );
  const [expenseItems, setExpenseItems] = useState(vehicle?.expense_items || []);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [transferOpen, setTransferOpen] = useState(false);
  // Bank outside the fixed list → the salesperson typed a custom one ("Outro banco")
  const [customBank, setCustomBank] = useState(() => !!(vehicle?.bank_name && !FINANCE_BANKS.includes(vehicle.bank_name)));
  // Multi-store: which store this NEW car will be created in. Defaults to the
  // active dealership. Only shown/settable on creation (edits are handled by
  // the Transfer flow to keep the history clean).
  const [targetDealershipId, setTargetDealershipId] = useState(activeDealershipId || "");

  // Load floor plans once so the dropdown can show them. Failure is silent —
  // the field just shows the "Sem floor plan" empty state.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/floor-plans");
        setFloorPlans(Array.isArray(r.data) ? r.data : []);
      } catch { /* silent — non-owner roles can't list plans */ }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const numFields = [
    "year", "mileage", "purchase_price", "sale_price", "expenses", "sold_price", "commission_amount",
    // sold-financing breakdown — must be numeric so deletes (empty string) save as 0
    "down_payment", "bank_check_amount", "registration_cost", "discount",
    "finance_term_months", "finance_installment_amount", "warranty_months",
    "trade_in_year", "trade_in_value", "trade_in_payoff_amount",
  ];
  // Free-text fields that must always be sent as a string (empty when cleared)
  const strFields = [
    "make", "model", "color", "plate", "vin", "transmission", "fuel_type", "body_type",
    "description", "buyer_name", "buyer_phone", "buyer_email", "buyer_address", "lead_source", "payment_method", "bank_name",
    "trade_in_make", "trade_in_model", "trade_in_payoff_bank",
    "salesperson_id", "salesperson_name",
    "floor_plan_id",
  ];

  const save = async (e) => {
    e.preventDefault();
    if (savingRef.current) return;
    // When the car is being marked as sold, buyer_email is now required so
    // the revision-receipt / delivery-doc email flow works with a single
    // click — no prompt asking "qual o email do cliente?" at delivery time.
    if (form.status === "sold") {
      const email = (form.buyer_email || "").trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error("Email do comprador é obrigatório em vendas — precisamos dele pra enviar o comprovante");
        return;
      }
    }
    savingRef.current = true;
    setSaving(true);
    const payload = { ...form, images: photos };
    // Force numeric fields. Number("") === NaN → fall back to 0 so the backend
    // doesn't reject the whole payload with 422.
    numFields.forEach((k) => { payload[k] = Number(payload[k]) || 0; });
    // Force string fields to actual strings so empty values save as "" (clearing
    // the field). Without this, clearing buyer_name leaves the previous value.
    strFields.forEach((k) => {
      if (payload[k] === undefined || payload[k] === null) payload[k] = "";
      else if (typeof payload[k] !== "string") payload[k] = String(payload[k]);
    });
    if (isSalesperson) {
      // Salespeople cannot edit cost/profit fields. Backend also strips these.
      delete payload.purchase_price;
      delete payload.expenses;
      delete payload.expense_items;
      delete payload.commission_amount;
      delete payload.commission_paid;
    } else {
      payload.expense_items = expenseItems.map((it) => ({ ...it, amount: Math.round(Number(it.amount) || 0) }));
      payload.expenses = payload.expense_items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
    }
    try {
      if (isEdit) await api.put(`/vehicles/${vehicle.id}`, payload);
      else {
        // Multi-store: include the target store id when the owner picked a
        // different one from the switcher's active store.
        if (targetDealershipId && targetDealershipId !== activeDealershipId) {
          payload.dealership_id = targetDealershipId;
        }
        await api.post("/vehicles", payload);
      }
      toast.success(t("saved"));
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || t("error_generic")); }
    finally { savingRef.current = false; setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-12 px-4">
      <form onSubmit={save} className="bg-background border border-border w-full max-w-3xl p-8 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">{isEdit ? t("edit") : t("add_vehicle")}</h2>
          <button type="button" onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>

        {/* Multi-store: pick which store the NEW car belongs to. Hidden on
            edits (change stores via Transfer instead) and single-store owners. */}
        {!isEdit && !isSalesperson && myDealerships.length > 1 && (
          <div className="rounded-lg border border-brand/30 bg-brand/[0.04] p-3">
            <p className="label-eyebrow text-brand mb-2">
              🏬 Loja onde este carro vai entrar no estoque
            </p>
            <div className="flex flex-wrap gap-2">
              {myDealerships.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setTargetDealershipId(s.id)}
                  data-testid={`f-store-${s.id}`}
                  className={`px-3 py-1.5 rounded-full border text-sm font-display font-medium transition-all ${
                    targetDealershipId === s.id
                      ? "bg-brand text-white border-brand shadow-[0_0_0_2px_rgba(217,45,32,0.15)]"
                      : "bg-bg-elev border-border text-text-primary hover:border-brand/50 hover:bg-brand/5"
                  }`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Input label={t("make")} value={form.make} set={(v) => set("make", v)} testid="f-make" required />
          <Input label={t("model")} value={form.model} set={(v) => set("model", v)} testid="f-model" required />
          <Input label={t("year")} type="number" value={form.year} set={(v) => set("year", v)} testid="f-year" required />
          <Input label={t("color")} value={form.color} set={(v) => set("color", v)} testid="f-color" />
          <Input label={t("vin")} value={form.vin} set={(v) => set("vin", v)} testid="f-vin" />
          <Input label={t("mileage")} type="number" value={form.mileage ?? 0} set={(v) => set("mileage", v)} testid="f-mileage" />
          <Select label={t("transmission")} value={form.transmission} set={(v) => set("transmission", v)} options={["Automatic", "Manual"]} testid="f-trans" />
          <Select label={t("fuel_type")} value={form.fuel_type} set={(v) => set("fuel_type", v)} options={["Gasoline", "Diesel", "Hybrid", "Electric", "Flex"]} testid="f-fuel" />
          <Select label={t("body_type")} value={form.body_type} set={(v) => set("body_type", v)} options={["Sedan", "SUV", "Truck", "Coupe", "Hatch", "Convertible", "Wagon", "Van"]} testid="f-body" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
          <Input label={t("sale_price")} type="number" value={form.sale_price} set={(v) => set("sale_price", v)} testid="f-sale" />
          {!isSalesperson && (
            <div>
              <label className="label-eyebrow block mb-2">
                {t("floor_plan_label")}
                <span className="ml-1 text-text-secondary/60 normal-case font-normal tracking-normal">· {t("floor_plan_hint")}</span>
              </label>
              <select
                data-testid="f-floor-plan"
                value={form.floor_plan_id || ""}
                onChange={(e) => set("floor_plan_id", e.target.value)}
                className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
              >
                <option value="">— {t("floor_plan_none")} —</option>
                {floorPlans.map((pl) => (
                  <option key={pl.id} value={pl.id}>{pl.name}</option>
                ))}
              </select>
              {floorPlans.length === 0 && (
                <p className="text-[10px] text-text-secondary mt-1">{t("floor_plan_no_plans_hint")}</p>
              )}
            </div>
          )}
        </div>

        {!isSalesperson && (
          <div className="pt-4 border-t border-border">
            <ExpenseManager items={expenseItems} onChange={setExpenseItems} t={t} />
          </div>
        )}

        <Select label={t("status")} value={form.status} set={(v) => set("status", v)} options={["in_stock", "reserved", "sold"]} testid="f-status" />

        {form.status === "sold" && (
          <div className="space-y-4 pt-4 border-t border-border">
            {/* Lead source · onde o cliente veio (marketing analytics) */}
            <LeadSourcePicker value={form.lead_source} set={(v) => set("lead_source", v)} t={t} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label={t("buyer_name")} value={form.buyer_name} set={(v) => set("buyer_name", v)} testid="f-buyer-name" />
              <Input label={t("buyer_phone")} value={form.buyer_phone} set={(v) => set("buyer_phone", v)} testid="f-buyer-phone" />
              <Input label={t("buyer_email") || "Email do comprador"} type="email" value={form.buyer_email || ""} set={(v) => set("buyer_email", v)} testid="f-buyer-email" required />
              <Input label={t("buyer_address") || "Endereço do comprador"} value={form.buyer_address || ""} set={(v) => set("buyer_address", v)} testid="f-buyer-address" />
              <Input label={`🎂 ${t("buyer_dob") || "Aniversário do cliente"}`} type="date" value={form.buyer_dob || ""} set={(v) => set("buyer_dob", v)} testid="f-buyer-dob" />
              <SelectWithPlaceholder
                label={t("payment_method")}
                value={form.payment_method}
                set={(v) => {
                  const prev = form.payment_method;
                  set("payment_method", v);
                  // Clear bank_name when switching away from Finance
                  if (v !== "Finance") set("bank_name", "");
                  // When switching FROM Finance to Cash/Bank Check, the Finance
                  // formula may have left sold_price at 0 (because dp+bc weren't
                  // filled). Auto-populate from sale_price so the user sees
                  // something sensible instead of an empty box.
                  if (prev === "Finance" && v !== "Finance") {
                    const cur = Number(form.sold_price) || 0;
                    if (cur === 0) {
                      const sp = Number(form.sale_price) || 0;
                      const reg = Number(form.registration_cost) || 0;
                      const base = sp + (form.registration_in_price ? reg : 0);
                      set("sold_price", Number(base.toFixed(2)));
                    }
                    // Also clear dp/bc since they don't apply to Cash/BC.
                    if (form.down_payment) set("down_payment", 0);
                    if (form.bank_check_amount) set("bank_check_amount", 0);
                  }
                }}
                placeholder={t("select_option") || "Selecione..."}
                options={["Finance", "Cash", "Bank Check"]}
                testid="f-payment"
              />
              {form.payment_method === "Finance" ? (
                <div className="space-y-2">
                  <SelectWithPlaceholder
                    label={t("bank_name_label")}
                    value={customBank ? (t("bank_other_option") || "Outro banco (digitar)...") : (form.bank_name || "")}
                    set={(v) => {
                      if (v === (t("bank_other_option") || "Outro banco (digitar)...")) {
                        setCustomBank(true);
                        set("bank_name", "");
                      } else {
                        setCustomBank(false);
                        set("bank_name", v);
                      }
                    }}
                    placeholder={t("select_bank") || "Selecione o banco..."}
                    options={[...FINANCE_BANKS, t("bank_other_option") || "Outro banco (digitar)..."]}
                    testid="f-bank"
                  />
                  {customBank && (
                    <input
                      data-testid="f-bank-custom"
                      type="text"
                      value={form.bank_name || ""}
                      onChange={(e) => set("bank_name", e.target.value)}
                      placeholder={t("bank_other_placeholder") || "Digite o nome do banco novo..."}
                      className="w-full bg-surface border border-primary/60 focus:border-primary focus:outline-none px-3 h-10 text-sm"
                      autoFocus
                    />
                  )}
                </div>
              ) : (
                <div /> /* placeholder spacer so grid stays aligned */
              )}
            </div>

            {/* Sale breakdown — fields shown depend on the payment method.
                Finance: down payment + bank check − registration → final sold price (auto)
                Cash / Bank Check: customer paid the full amount in one shot, so we
                only ask for the final sold price + registration cost. */}
            <div className="bg-success/5 border border-success/30 p-4 space-y-3">
              <p className="label-eyebrow text-success">{t("sale_breakdown")}</p>
              {form.payment_method === "Finance" ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input label={`💵 ${t("down_payment")}`} type="number" value={form.down_payment ?? ""} set={(v) => {
                    set("down_payment", v);
                    const dp = Number(v) || 0;
                    const bc = Number(form.bank_check_amount) || 0;
                    const reg = Number(form.registration_cost) || 0;
                    const ti = Number(form.trade_in_value) || 0;
                    // dp + bc + troca são o pagamento do carro. Quando o
                    // emplacamento está incluso no preço (a loja absorve),
                    // soma em cima — e ele também vira despesa do carro.
                    const final = dp + bc + ti + (form.registration_in_price ? reg : 0);
                    set("sold_price", final > 0 ? Number(final.toFixed(2)) : 0);
                  }} testid="f-down-payment" />
                  <Input label={`🏦 ${t("bank_check_amount")}`} type="number" value={form.bank_check_amount ?? ""} set={(v) => {
                    set("bank_check_amount", v);
                    const bc = Number(v) || 0;
                    const dp = Number(form.down_payment) || 0;
                    const reg = Number(form.registration_cost) || 0;
                    const ti = Number(form.trade_in_value) || 0;
                    const final = dp + bc + ti + (form.registration_in_price ? reg : 0);
                    set("sold_price", final > 0 ? Number(final.toFixed(2)) : 0);
                  }} testid="f-bank-check" />
                  <RegistrationField
                    t={t}
                    value={form.registration_cost ?? ""}
                    salePrice={form.sale_price}
                    inPrice={!!form.registration_in_price}
                    onInPriceChange={(v) => {
                      set("registration_in_price", v);
                      const dp = Number(form.down_payment) || 0;
                      const bc = Number(form.bank_check_amount) || 0;
                      const reg = Number(form.registration_cost) || 0;
                      const ti = Number(form.trade_in_value) || 0;
                      const final = dp + bc + ti + (v ? reg : 0);
                      set("sold_price", final > 0 ? Number(final.toFixed(2)) : 0);
                    }}
                    onChange={(v) => {
                      set("registration_cost", v);
                      const reg = Number(v) || 0;
                      const dp = Number(form.down_payment) || 0;
                      const bc = Number(form.bank_check_amount) || 0;
                      const ti = Number(form.trade_in_value) || 0;
                      const final = dp + bc + ti + (form.registration_in_price ? reg : 0);
                      set("sold_price", final > 0 ? Number(final.toFixed(2)) : 0);
                    }}
                  />
                  {/* Financing terms — printed on the Purchase & Sale Agreement */}
                  <Input
                    label={`📆 ${t("finance_term") || "Qtd. de parcelas"}`}
                    type="number"
                    value={form.finance_term_months ?? ""}
                    set={(v) => set("finance_term_months", v)}
                    testid="f-finance-term"
                  />
                  <Input
                    label={`💳 ${t("finance_installment") || "Valor da parcela (US$)"}`}
                    type="number"
                    value={form.finance_installment_amount ?? ""}
                    set={(v) => set("finance_installment_amount", v)}
                    testid="f-finance-installment"
                  />
                  <Input
                    label={`🛡️ ${t("warranty_months_label") || "Garantia (meses)"}`}
                    type="number"
                    value={form.warranty_months ?? ""}
                    set={(v) => set("warranty_months", v)}
                    testid="f-warranty-months"
                  />
                  <div>
                    <label className="label-eyebrow block mb-2">🔁 {t("payment_frequency_label") || "Frequência do pagamento"}</label>
                    <select
                      data-testid="f-payment-frequency"
                      value={form.payment_frequency || "monthly"}
                      onChange={(e) => set("payment_frequency", e.target.value)}
                      className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm"
                    >
                      <option value="monthly">{t("freq_monthly") || "Mensal"}</option>
                      <option value="weekly">{t("freq_weekly") || "Semanal"}</option>
                    </select>
                  </div>
                  <div className="md:col-span-3 border border-info/40 bg-info/[0.05] px-3 py-2">
                    <p data-testid="doc-fee-included-note" className="text-[11px] text-info">
                      🧾 {t("doc_fee_included") || "DOC FEE US$ 499.00 — incluído em todo financiamento (sai especificado no contrato)"}
                    </p>
                  </div>
                </div>
              ) : (
                // Cash / Bank Check / nothing selected yet: just the sold price + registration + optional discount.
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      label={`💵 ${t("sold_price")}`}
                      type="number"
                      value={form.sold_price ?? ""}
                      set={(v) => {
                        // Treat the typed amount as the dealer's authoritative
                        // sold_price (already including reg if the user wants it,
                        // and already net of discount).
                        set("sold_price", v);
                        if (form.down_payment) set("down_payment", 0);
                        if (form.bank_check_amount) set("bank_check_amount", 0);
                      }}
                      testid="f-sold-price"
                    />
                    <RegistrationField
                      t={t}
                      value={form.registration_cost ?? ""}
                      salePrice={form.sale_price}
                      inPrice={!!form.registration_in_price}
                      onInPriceChange={(v) => {
                        const reg = Number(form.registration_cost) || 0;
                        const cur = Number(form.sold_price) || 0;
                        if (v && !form.registration_in_price) {
                          set("sold_price", Number((cur + reg).toFixed(2)));
                        } else if (!v && form.registration_in_price) {
                          set("sold_price", Number(Math.max(cur - reg, 0).toFixed(2)));
                        }
                        set("registration_in_price", v);
                      }}
                      onChange={(v) => {
                        const newReg = Number(v) || 0;
                        const oldReg = Number(form.registration_cost) || 0;
                        set("registration_cost", v);
                        if (form.registration_in_price) {
                          const cur = Number(form.sold_price) || 0;
                          const adjusted = Math.max(cur + (newReg - oldReg), 0);
                          set("sold_price", Number(adjusted.toFixed(2)));
                        }
                      }}
                    />
                  </div>
                  {/* Discount field — Cash & Bank Check only. Reduces sold_price by the delta. */}
                  <div className="border border-warning/40 bg-warning/[0.04] p-3 mt-2">
                    <label className="label-eyebrow block mb-2 text-warning">
                      🏷️ {t("discount_label") || "Desconto"}
                      <span className="ml-2 text-text-secondary normal-case tracking-normal font-normal">
                        {t("discount_hint") || "(opcional — abatido do preço final)"}
                      </span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                      <input
                        data-testid="f-discount"
                        type="number"
                        value={form.discount ?? 0}
                        onChange={(e) => {
                          const v = e.target.value;
                          const newDisc = Number(v) || 0;
                          const oldDisc = Number(form.discount) || 0;
                          set("discount", v);
                          // Apply discount delta to sold_price so the user sees
                          // the net amount immediately.
                          const cur = Number(form.sold_price) || 0;
                          const adjusted = Math.max(cur - (newDisc - oldDisc), 0);
                          set("sold_price", Number(adjusted.toFixed(2)));
                        }}
                        className="w-full bg-surface border border-border focus:border-warning focus:outline-none px-3 h-10 text-sm"
                        placeholder="0.00"
                      />
                      {Number(form.discount) > 0 && (
                        <p data-testid="discount-applied-hint" className="text-[11px] text-warning leading-tight">
                          {t("discount_applied_hint") || "Desconto aplicado · subtraído do Preço Final Vendido"}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Doc fee — cash/bank-check deals: big visible selector */}
                  {form.payment_method && (
                    <div data-testid="doc-fee-selector" className="border-2 border-info bg-info/[0.08] p-4 mt-2">
                      <p className="label-eyebrow text-info">🧾 DOC FEE — US$ 499.00</p>
                      <p className="text-[11px] text-text-secondary mt-1 mb-3">
                        {t("doc_fee_hint") || "Escolha se o doc fee será cobrado nessa venda à vista — sai direto no contrato."}
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          data-testid="f-doc-fee-yes"
                          onClick={() => set("charge_doc_fee", true)}
                          className={`h-11 text-[11px] font-display font-black uppercase tracking-widest border-2 transition-colors ${form.charge_doc_fee !== false ? "border-success bg-success text-white" : "border-border text-text-secondary hover:border-success"}`}
                        >
                          ✓ {t("doc_fee_yes") || "Cobrar US$ 499"}
                        </button>
                        <button
                          type="button"
                          data-testid="f-doc-fee-no"
                          onClick={() => set("charge_doc_fee", false)}
                          className={`h-11 text-[11px] font-display font-black uppercase tracking-widest border-2 transition-colors ${form.charge_doc_fee === false ? "border-primary bg-primary text-white" : "border-border text-text-secondary hover:border-primary"}`}
                        >
                          ✗ {t("doc_fee_no") || "Não cobrar (desconto)"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* TRADE-IN (Veículo na troca) — auto-creates a new stock vehicle on save */}
            <div data-testid="trade-in-section" className="bg-warning/5 border border-warning/30 p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="label-eyebrow text-warning">🔄 Veículo na troca</p>
                {Number(form.trade_in_value) > 0 && (
                  <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                    Soma como pagamento → vai pro estoque após salvar
                  </p>
                )}
                {form.trade_in_vehicle_id && (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-success text-success bg-success/10 inline-flex items-center gap-1">
                    <Check size={10} /> Já no estoque
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input label="Marca" value={form.trade_in_make || ""} set={(v) => set("trade_in_make", v)} testid="f-ti-make" />
                <Input label="Modelo" value={form.trade_in_model || ""} set={(v) => set("trade_in_model", v)} testid="f-ti-model" />
                <Input label="Ano" type="number" value={form.trade_in_year || ""} set={(v) => set("trade_in_year", v)} testid="f-ti-year" />
                <Input label="💰 Valor avaliado" type="number" value={form.trade_in_value ?? ""} set={(v) => {
                  set("trade_in_value", v);
                  const ti = Number(v) || 0;
                  const dp = Number(form.down_payment) || 0;
                  const bc = Number(form.bank_check_amount) || 0;
                  const reg = Number(form.registration_cost) || 0;
                  // Mesma fórmula do backend: entrada + cheque + troca + (RMV se incluso no preço)
                  const final = dp + bc + ti + (form.registration_in_price ? reg : 0);
                  set("sold_price", final > 0 ? Number(final.toFixed(2)) : 0);
                }} testid="f-ti-value" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Saldo devedor no banco" type="number" value={form.trade_in_payoff_amount ?? ""} set={(v) => set("trade_in_payoff_amount", v)} testid="f-ti-payoff" />
                <Input label="Banco financiador" value={form.trade_in_payoff_bank || ""} set={(v) => set("trade_in_payoff_bank", v)} testid="f-ti-payoff-bank" />
              </div>
              {Number(form.trade_in_payoff_amount) > 0 && (
                <p className="text-[11px] text-text-secondary italic">
                  ⚠️ A quitação de R$ {Number(form.trade_in_payoff_amount).toLocaleString("pt-BR")} será lançada automaticamente como despesa do veículo no estoque.
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Finance flow auto-computes sold_price from dp+bc-reg, so we
                  keep a read-only mirror here just for confirmation. Cash /
                  Bank Check already have the sold_price input inside the
                  recebimento panel above. */}
              {form.payment_method === "Finance" ? (
                <div>
                  <label className="label-eyebrow block mb-2">{t("sold_price")}</label>
                  <input
                    data-testid="f-sold-price-display"
                    type="number"
                    value={form.sold_price ?? ""}
                    readOnly
                    className="w-full bg-surface/40 border border-border px-3 h-10 text-sm text-success font-display font-bold cursor-not-allowed"
                  />
                </div>
              ) : (
                <div />
              )}
              {/* Salesperson selector — owner only. Salespeople auto-assign themselves on save */}
              {!isSalesperson && (
                <div>
                  <label className="label-eyebrow block mb-2">{t("salesperson")}</label>
                  <select
                    data-testid="f-salesperson"
                    value={form.salesperson_id || ""}
                    onChange={(e) => {
                      const sp = salespeople.find(s => s.id === e.target.value);
                      set("salesperson_id", e.target.value);
                      set("salesperson_name", sp ? sp.name : "");
                      if (sp && !Number(form.commission_amount)) {
                        set("commission_amount", sp.commission_amount || 0);
                      }
                    }}
                    className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
                  >
                    <option value="">{t("select_salesperson")}</option>
                    {salespeople.filter(s => s.active !== false).map(s => (
                      <option key={s.id} value={s.id}>{s.name}{myDealerships.length > 1 && s.store_name ? ` · ${s.store_name}` : ""}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <label className="label-eyebrow block mb-2">{t("description")}</label>
          <textarea data-testid="f-desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-4 py-2 text-sm resize-none" />
        </div>

        <div>
          <label className="label-eyebrow block mb-2">{t("images")}</label>
          <PhotoUploader value={photos} onChange={setPhotos} folder="vehicles" t={t} />
        </div>

        {/* Receipts — always visible for any saved sold vehicle, so receipts can be reprinted at any time */}
        {vehicle && vehicle.id && vehicle.status === "sold" && (
          <ReceiptsPanel v={vehicle} t={t} />
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          {/* Multi-store: transfer button appears only when the owner has 2+ stores
              and the vehicle exists (isEdit). Sold cars can be moved too — the
              receipts and receivables follow the car to the new store. */}
          {isEdit && !isSalesperson && myDealerships.length > 1 && (
            <button
              type="button"
              onClick={() => setTransferOpen(true)}
              data-testid="btn-open-transfer"
              className="px-6 py-3 border border-border hover:border-brand text-xs font-display font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2 text-brand"
            >
              <Truck size={14} /> Transferir loja
            </button>
          )}
          <button type="button" onClick={onClose} className="px-6 py-3 border border-border hover:border-primary text-xs font-display font-bold uppercase tracking-widest transition-colors">{t("cancel")}</button>
          <button type="submit" data-testid="f-submit" disabled={saving} className="bg-primary hover:bg-primary-hover disabled:opacity-50 px-6 py-3 text-xs font-display font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2">
            <Check size={14} /> {saving ? "..." : t("save")}
          </button>
        </div>
      </form>
      {transferOpen && (
        <TransferVehicleModal
          vehicle={vehicle}
          stores={myDealerships}
          activeDealershipId={activeDealershipId}
          onClose={() => setTransferOpen(false)}
          onTransferred={() => { setTransferOpen(false); onSaved && onSaved(); onClose && onClose(); }}
        />
      )}
    </div>
  );
}

function Delivery({ deliveries, vehicles = [], team = [], currentUser, scheduleAlertCount = 0, salespeople: deliveriesSalespeople = [], t, onReload, isStaff = false, onHistory }) {
  const [editing, setEditing] = useState(null); // vehicle being edited (step or notes)
  const [editMode, setEditMode] = useState("step"); // "step" | "notes"
  const [filesOpen, setFilesOpen] = useState(null); // { vehicle, step }
  const [alertsOnly, setAlertsOnly] = useState(false);
  const [deliveryView, setDeliveryView] = useState(() => localStorage.getItem("delivery_view_tab") || "active"); // "active" | "delivered"
  const [progVehicle, setProgVehicle] = useState(null);  // car-scoped programação modal
  const [lemonLawVehicle, setLemonLawVehicle] = useState(null);  // car-scoped MA Lemon Law disclosure modal
  const [search, setSearch] = useState("");

  const setDeliveryViewSticky = (v) => { setDeliveryView(v); localStorage.setItem("delivery_view_tab", v); };

  const [contractVid, setContractVid] = useState(null);

  const STEPS = [1, 2, 3, 4, 5, 6, 7, 8];
  // Color per step (mimics screenshot: red→pink→blue→purple→green)
  const STEP_COLORS = {
    1: "bg-primary border-primary",
    2: "bg-pink-600 border-pink-600",
    3: "bg-pink-500 border-pink-500",
    4: "bg-purple-600 border-purple-600",
    5: "bg-blue-600 border-blue-600",
    6: "bg-blue-700 border-blue-700",
    7: "bg-blue-900 border-blue-900",
    8: "bg-success border-success",
  };

  const stuckCount = deliveries.filter(v => v.stuck_alert).length;
  // Lupa — busca por nome do cliente (e carro/VIN/vendedor de bônus)
  const _dq = search.trim().toLowerCase();
  const matchesSearch = (v) =>
    (v.buyer_name || "").toLowerCase().includes(_dq) ||
    (v.buyer_phone || "").toLowerCase().includes(_dq) ||
    (v.salesperson_name || "").toLowerCase().includes(_dq) ||
    (v.vin || "").toLowerCase().includes(_dq) ||
    `${v.year || ""} ${v.make || ""} ${v.model || ""}`.toLowerCase().includes(_dq);
  // Split into active (steps 1-7) and delivered (step 8). Delivered go into a dedicated tab.
  const activeDeliveries = deliveries.filter(v => (v.delivery_step || 0) < 8);
  const deliveredDeliveries = deliveries.filter(v => (v.delivery_step || 0) === 8);
  const visibleActive = ((isStaff && alertsOnly) ? activeDeliveries.filter(v => v.stuck_alert) : activeDeliveries).filter(v => !_dq || matchesSearch(v));
  const visibleDelivered = deliveredDeliveries.filter(v => !_dq || matchesSearch(v));

  // KPI metrics — delivered this month + avg delivery time (sold_at → delivered_at).
  const _now = new Date();
  const _ym = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}`;
  const deliveredThisMonth = deliveredDeliveries.filter(v => (v.delivered_at || "").startsWith(_ym)).length;
  const _diffs = deliveredDeliveries
    .map(v => {
      if (!v.sold_at || !v.delivered_at) return null;
      try {
        const a = new Date(v.sold_at).getTime();
        const b = new Date(v.delivered_at).getTime();
        if (Number.isNaN(a) || Number.isNaN(b) || b < a) return null;
        return Math.round((b - a) / 86400000);
      } catch { return null; }
    })
    .filter(n => n !== null);
  const avgDeliveryDays = _diffs.length ? Math.round(_diffs.reduce((s, n) => s + n, 0) / _diffs.length) : 0;

  const advance = async (v) => {
    const nextStep = Math.min((v.delivery_step || 1) + 1, 8);
    try {
      await api.put(`/vehicles/${v.id}`, { delivery_step: nextStep });
      toast.success(t("saved"));
      onReload();
    } catch { toast.error(t("error_generic")); }
  };

  const goBack = async (v) => {
    const prevStep = Math.max((v.delivery_step || 1) - 1, 1);
    if (prevStep === v.delivery_step) return;
    try {
      // Clear delivered_at if going back from step 8
      const payload = { delivery_step: prevStep };
      if ((v.delivery_step || 0) === 8) payload.delivered_at = null;
      await api.put(`/vehicles/${v.id}`, payload);
      toast.success(t("saved"));
      onReload();
    } catch { toast.error(t("error_generic")); }
  };

  const toggleBankContract = async (v) => {
    try {
      await api.put(`/vehicles/${v.id}`, { bank_contract_signed: !v.bank_contract_signed });
      toast.success(v.bank_contract_signed ? t("bank_contract_unmarked") : t("bank_contract_marked"));
      onReload();
    } catch { toast.error(t("error_generic")); }
  };

  const reopenDelivery = async (v) => {
    if (!window.confirm(t("reopen_delivery_confirm"))) return;
    try {
      await api.put(`/vehicles/${v.id}`, { delivery_step: 7, delivered_at: null });
      toast.success(t("delivery_reopened"));
      onReload();
    } catch { toast.error(t("error_generic")); }
  };

  const openNegotiationSheet = async (v) => {
    try {
      const res = await api.get(`/vehicles/${v.id}/negotiation-sheet.pdf`, { responseType: "blob" });
      openPdfBlob(res.data, "folha-negociacao.pdf");
    } catch { toast.error(t("error_generic")); }
  };

  return (
    <div data-testid="delivery-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="label-eyebrow text-primary mb-2">{t("delivery_pipeline_title")}</p>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">{t("delivery")}</h1>
        </div>
        {scheduleAlertCount > 0 && (
          <span
            data-testid="schedule-alert-chip-header"
            className="inline-flex items-center gap-2 px-3 py-2 border border-primary bg-primary/10 text-primary text-[10px] font-display font-bold uppercase tracking-widest animate-pulse"
            title="Programação de entrega com tarefas pendentes a menos de 24h"
          >
            <ClipboardList size={12} />
            {scheduleAlertCount} programação{scheduleAlertCount === 1 ? "" : "ões"} urgente{scheduleAlertCount === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {/* KPI strip — counts per tab + avg delivery time */}
      <div className="grid grid-cols-3 gap-px bg-border border border-border mb-6">
        <div data-testid="delivery-kpi-pending" className="bg-background p-4">
          <p className="label-eyebrow text-text-secondary mb-1">{t("pending_deliveries")}</p>
          <p className="font-display font-black text-3xl text-primary">{activeDeliveries.length}</p>
        </div>
        <div data-testid="delivery-kpi-month" className="bg-background p-4">
          <p className="label-eyebrow text-text-secondary mb-1">{t("delivered_this_month")}</p>
          <p className="font-display font-black text-3xl text-success">{deliveredThisMonth}</p>
        </div>
        <div data-testid="delivery-kpi-avg" className="bg-background p-4">
          <p className="label-eyebrow text-text-secondary mb-1">{t("avg_delivery_days")}</p>
          <p className="font-display font-black text-3xl">{avgDeliveryDays || "—"}</p>
        </div>
      </div>

      {/* Tab switcher — Active vs Delivered */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          type="button"
          data-testid="delivery-tab-active"
          onClick={() => setDeliveryViewSticky("active")}
          className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-widest border transition-colors inline-flex items-center gap-2 ${
            deliveryView === "active"
              ? "bg-primary border-primary text-white"
              : "border-border text-text-secondary hover:border-primary hover:text-primary"
          }`}
        >
          <Truck size={13} /> {t("delivery_view_active")} ({activeDeliveries.length})
        </button>
        <button
          type="button"
          data-testid="delivery-tab-delivered"
          onClick={() => setDeliveryViewSticky("delivered")}
          className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-widest border transition-colors inline-flex items-center gap-2 ${
            deliveryView === "delivered"
              ? "bg-success border-success text-white"
              : "border-border text-text-secondary hover:border-success hover:text-success"
          }`}
        >
          <CheckCircle2 size={13} /> {t("delivery_view_delivered")} ({deliveredDeliveries.length})
        </button>
        <div className="relative flex-1 min-w-[220px] max-w-sm ml-auto">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
          <input
            data-testid="delivery-search"
            className="w-full bg-background border border-border focus:border-primary focus:outline-none pl-8 pr-8 h-9 text-sm"
            placeholder={t("sold_search_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button type="button" data-testid="delivery-search-clear" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-primary">
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* ============ ACTIVE DELIVERIES VIEW (steps 1-7) ============ */}
      {deliveryView === "active" && (<>

      {/* Stuck alerts banner — owner + gerente only */}
      {isStaff && stuckCount > 0 && (
        <div
          data-testid="stuck-alert-banner"
          className="border border-primary bg-primary/10 p-4 mb-6 flex items-center gap-4"
        >
          <div className="w-10 h-10 rounded-full bg-primary/20 border border-primary flex items-center justify-center shrink-0">
            <AlertTriangle size={20} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-black uppercase tracking-tight text-primary">
              {stuckCount} {stuckCount === 1 ? t("stuck_alert_title_one") : t("stuck_alert_title_many")}
            </p>
            <p className="text-xs text-text-secondary mt-0.5">{t("stuck_alert_hint")}</p>
          </div>
          <button
            type="button"
            data-testid="toggle-stuck-only"
            onClick={() => setAlertsOnly(v => !v)}
            className={`px-4 py-2 text-[11px] font-display font-bold uppercase tracking-widest border transition-colors shrink-0 ${
              alertsOnly
                ? "bg-primary text-white border-primary"
                : "border-primary text-primary hover:bg-primary/10"
            }`}
          >
            {alertsOnly ? t("show_all") : t("show_only_stuck")}
          </button>
        </div>
      )}

      {/* Step legend */}
      <div className="flex flex-wrap gap-3 mb-8 p-4 border border-border bg-surface">
        {STEPS.map((n) => (
          <div key={n} className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${STEP_COLORS[n].split(" ")[0]}`} />
            <span className="text-xs text-text-secondary">{n}. {t(`step_${n}`)}</span>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        {visibleActive.length === 0 && (
          <p data-testid="delivery-search-empty" className="text-text-secondary text-center py-16 border border-dashed border-border">
            {_dq ? t("sold_search_empty") : alertsOnly ? t("no_stuck_deliveries") : t("no_deliveries")}
          </p>
        )}

        {visibleActive.map((v) => {
          const step = v.delivery_step || 1;
          const isDelivered = step === 8;
          const isStuck = isStaff && v.stuck_alert;
          return (
            <div
              key={v.id}
              data-testid={`delivery-${v.id}`}
              className={`border bg-surface p-5 ${isStuck ? "border-primary shadow-[0_0_0_1px_theme(colors.primary.DEFAULT)]" : "border-border"}`}
            >
              <div className="flex flex-wrap items-start gap-5">
                {/* Photo */}
                <div className="w-32 h-24 bg-background border border-border overflow-hidden flex-shrink-0 relative">
                  {v.images?.[0] ? (
                    <img src={v.images[0]} alt={`${v.make} ${v.model}`} className="w-full h-full object-cover" />
                  ) : (
                    <img src="/no-photo-placeholder.png" alt="" className="w-full h-full object-cover" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-[200px]">
                  <p className="font-display font-bold text-lg">{v.year} {v.make} {v.model}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm text-text-secondary">{v.buyer_name || "—"}</p>
                    {v.buyer_phone && v.buyer_name && (
                      <WhatsAppButton
                        phone={v.buyer_phone}
                        testid={`wa-buyer-${v.id}`}
                        message={(isDelivered
                          ? t("wa_delivered_message")
                          : t("wa_delivery_message"))
                          .replace("{name}", v.buyer_name)
                          .replace("{vehicle}", `${v.year} ${v.make} ${v.model}`)
                          .replace("{step}", t(`step_${step}`))}
                        variant="icon"
                        size={13}
                      />
                    )}
                  </div>
                  <p className={`text-sm font-display font-bold mt-1 ${isDelivered ? "text-success" : "text-primary"}`}>
                    {t(`step_${step}`)}
                  </p>
                  <RevisionBadge revision={v.revision} t={t} />
                  {isStuck && (
                    <span
                      data-testid={`stuck-chip-${v.id}`}
                      className="inline-flex items-center gap-1 mt-2 px-2 py-1 border border-primary bg-primary/15 text-primary text-[10px] font-display font-bold uppercase tracking-widest"
                    >
                      <AlertTriangle size={11} />
                      {t("stuck_days_chip").replace("{d}", v.days_in_step)}
                    </span>
                  )}
                </div>

                {/* Step navigation buttons */}
                <div className="flex gap-1.5 items-stretch flex-wrap">
                  {/* Bank contract signed toggle */}
                  <button
                    type="button"
                    data-testid={`bank-contract-${v.id}`}
                    onClick={() => toggleBankContract(v)}
                    title={v.bank_contract_signed ? t("bank_contract_signed_on") : t("bank_contract_pending")}
                    className={`px-3 h-12 inline-flex items-center gap-1.5 border text-[10px] uppercase tracking-wider font-display font-bold transition-colors ${
                      v.bank_contract_signed
                        ? "bg-success/15 border-success text-success hover:bg-success hover:text-white"
                        : "border-border text-text-secondary hover:border-warning hover:text-warning"
                    }`}
                  >
                    {v.bank_contract_signed ? <Check size={14} /> : <FileText size={14} />}
                    <span className="hidden sm:inline">{t("bank_contract_short")}</span>
                  </button>
                  {step > 1 && (
                    <button
                      data-testid={`back-${v.id}`}
                      onClick={() => goBack(v)}
                      className="border border-border hover:border-warning hover:text-warning w-12 h-12 flex items-center justify-center transition-colors"
                      title={t("back_step")}
                    >
                      <ChevronLeft size={24} />
                    </button>
                  )}
                  {!isDelivered && (
                    <button
                      data-testid={`advance-${v.id}`}
                      onClick={() => advance(v)}
                      className="bg-primary hover:bg-primary-hover w-12 h-12 flex items-center justify-center text-white transition-colors"
                      title={t("advance_step")}
                    >
                      <ChevronRight size={24} />
                    </button>
                  )}
                </div>
              </div>

              {/* Step indicator */}
              <div className="flex items-center mt-5 mb-4">
                {STEPS.map((n, i) => {
                  const completed = n < step;
                  const current = n === step;
                  const fileCount = (v.step_files?.[String(n)] || []).length;
                  const hasNotes = !!(v.step_notes?.[String(n)]);
                  return (
                    <div key={n} className="flex items-center flex-1 last:flex-none">
                      <button
                        type="button"
                        data-testid={`step-${v.id}-${n}`}
                        onClick={() => setFilesOpen({ vehicle: v, step: n })}
                        className={`relative w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all hover:scale-110 cursor-pointer ${
                          completed ? `${STEP_COLORS[n]} text-white` :
                          current ? `${STEP_COLORS[n]} text-white ring-4 ring-primary/30` :
                          "bg-background border-border text-text-secondary hover:border-primary"
                        }`}
                        title={`${t(`step_${n}`)}${fileCount ? ` · ${fileCount} ${t("files").toLowerCase()}` : ""}${hasNotes ? ` · ${t("notes").toLowerCase()}` : ""}`}
                      >
                        {completed ? <Check size={14} /> : n}
                        {fileCount > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center border-2 border-background">
                            {fileCount}
                          </span>
                        )}
                        {hasNotes && fileCount === 0 && (
                          <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-warning border-2 border-background" />
                        )}
                      </button>
                      {i < STEPS.length - 1 && (
                        <div className={`flex-1 h-0.5 ${n < step ? STEP_COLORS[n].split(" ")[0] : "bg-border"}`} />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Meta info pills */}
              <div className="flex flex-wrap gap-2 mb-4 items-center">
                {v.salesperson_name && (
                  <div className="inline-flex items-center gap-2 border border-border px-2 py-1 text-xs">
                    <span className="text-text-secondary uppercase tracking-wider text-[10px]">{t("salesperson")}:</span>
                    <NameWithAvatar name={v.salesperson_name} size="xs" className="font-display font-bold" />
                  </div>
                )}
                {v.buyer_name && <Pill label={t("buyer_name")} value={v.buyer_name} />}
                {v.bank_name && <Pill label={t("bank")} value={v.bank_name} />}
                {v.payment_method && <Pill label={t("payment_method")} value={v.payment_method} />}
              </div>

              {/* Step 8 — Delivery photos preview */}
              {(() => {
                const deliveryPhotos = (v.step_files?.["8"] || []).filter(f => (f.type || "").startsWith("image/"));
                if (deliveryPhotos.length === 0) return null;
                return (
                  <div className="mb-4 border border-success/40 bg-success/5 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="label-eyebrow text-success">{t("delivery_photos")} · {deliveryPhotos.length} {t("photos_count")}</p>
                      <button
                        type="button"
                        data-testid={`open-delivery-photos-${v.id}`}
                        onClick={() => setFilesOpen({ vehicle: v, step: 8 })}
                        className="text-[10px] text-success hover:underline uppercase tracking-wider"
                      >
                        {t("view")} →
                      </button>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {deliveryPhotos.slice(0, 10).map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setFilesOpen({ vehicle: v, step: 8 })}
                          className="w-16 h-16 flex-shrink-0 bg-background overflow-hidden border border-border hover:border-success transition-colors"
                        >
                          <img src={p.url || p.data_url} alt={p.name} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Finalized negotiation sheet — the F&I opens the EXACT deal here */}
              {v.last_contract_at && (
                <div className="mb-4 border border-yellow-500/40 bg-yellow-500/5 p-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="label-eyebrow text-yellow-500">{t("negotiation_sheet_ready")}</p>
                    <p className="text-[11px] text-text-secondary mt-0.5">
                      {v.last_contract_by ? `${v.last_contract_by} · ` : ""}
                      {new Date(v.last_contract_at).toLocaleString()}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid={`open-negotiation-sheet-${v.id}`}
                    onClick={() => openNegotiationSheet(v)}
                    className="border border-yellow-500/60 text-yellow-500 hover:bg-yellow-500 hover:text-black transition-colors px-3 py-2 inline-flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-widest shrink-0"
                  >
                    <FileSignature size={12} /> {t("open_sheet")} →
                  </button>
                </div>
              )}

              {/* Receipts + sale contract panel — from step 1 (the contract
                  button lives here; finalizing it is what moves the car to 2) */}
              <ReceiptsPanel v={v} t={t} />

              {/* Inline programação de entrega */}
              <InlineSchedule
                vehicle={v}
                onOpenModal={() => setProgVehicle(v)}
              />

              {/* Actions */}
              <div className={`grid grid-cols-1 ${onHistory ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-2`}>
                <button
                  data-testid={`edit-step-${v.id}`}
                  onClick={() => { setEditing(v); setEditMode("step"); }}
                  className="border border-border hover:border-primary hover:text-primary transition-colors py-2.5 text-xs font-display font-bold uppercase tracking-widest inline-flex items-center justify-center gap-2"
                >
                  <Edit2 size={14} /> {t("edit_step")}
                </button>
                <button
                  data-testid={`notes-${v.id}`}
                  onClick={() => { setEditing(v); setEditMode("notes"); }}
                  className="border border-border hover:border-primary hover:text-primary transition-colors py-2.5 text-xs font-display font-bold uppercase tracking-widest inline-flex items-center justify-center gap-2"
                >
                  <FileText size={14} /> {t("notes")}
                </button>
                {onHistory && (
                  <button
                    data-testid={`delivery-history-${v.id}`}
                    onClick={() => onHistory(v.id)}
                    className="border border-border hover:border-primary hover:text-primary transition-colors py-2.5 text-xs font-display font-bold uppercase tracking-widest inline-flex items-center justify-center gap-2"
                  >
                    <History size={14} /> {t("vehicle_history")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      </>)}
      {/* ============ END ACTIVE DELIVERIES VIEW ============ */}

      {/* ============ DELIVERED VIEW (step 8) — dedicated tab ============ */}
      {deliveryView === "delivered" && (
        <div data-testid="delivered-list" className="space-y-3">
          {visibleDelivered.length === 0 ? (
            <p className="text-text-secondary text-center py-16 border border-dashed border-border">
              {_dq ? t("sold_search_empty") : t("post_sales_no_delivered")}
            </p>
          ) : (
            visibleDelivered.map((v) => (
              <div
                key={v.id}
                data-testid={`delivered-${v.id}`}
                className="border border-border bg-surface/40 p-4 flex flex-wrap items-center gap-4 hover:border-success/60 transition-colors"
              >
                {(v.images && v.images[0]) && (
                  <img src={v.images[0]} alt="" className="w-20 h-16 object-cover shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold uppercase truncate">{v.year} {v.make} {v.model}</p>
                  <p className="text-xs text-text-secondary truncate">
                    {v.buyer_name || "—"}
                    {v.salesperson_name && (
                      <span className="ml-2 text-text-secondary/70">· {v.salesperson_name}</span>
                    )}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <span className="inline-flex items-center gap-1.5 border border-success text-success bg-success/10 px-2.5 py-1 text-[10px] font-display font-bold uppercase tracking-widest">
                    <CheckCircle2 size={11} /> {t("delivered_label")}
                  </span>
                  {v.delivered_at && (
                    <p className="text-[10px] text-text-secondary mt-1">
                      {new Date(v.delivered_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button
                    type="button"
                    data-testid={`delivered-contract-${v.id}`}
                    onClick={() => setContractVid(v.id)}
                    title={t("purchase_agreement")}
                    className="border border-info/50 text-info hover:bg-info hover:text-white transition-colors p-2 inline-flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-widest"
                  >
                    <FileSignature size={13} />
                    <span className="hidden sm:inline">Contrato</span>
                  </button>
                  <button
                    type="button"
                    data-testid={`delivered-lemon-law-${v.id}`}
                    onClick={() => setLemonLawVehicle(v)}
                    title="Garantia Lemon Law (MA)"
                    className="border border-primary/50 text-primary hover:bg-primary hover:text-white transition-colors p-2 inline-flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-widest"
                  >
                    <ShieldCheck size={13} />
                    <span className="hidden sm:inline">Lemon Law</span>
                  </button>
                  <button
                    type="button"
                    data-testid={`delivered-reopen-${v.id}`}
                    onClick={() => reopenDelivery(v)}
                    title={t("reopen_delivery")}
                    className="border border-warning/40 text-warning hover:bg-warning hover:text-black transition-colors p-2 inline-flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-widest"
                  >
                    <RefreshCw size={13} />
                    <span className="hidden sm:inline">{t("reopen_delivery")}</span>
                  </button>
                  <button
                    type="button"
                    data-testid={`delivered-files-${v.id}`}
                    onClick={() => setFilesOpen({ vehicle: v, step: 8 })}
                    title={t("delivery_photos")}
                    className="border border-border hover:border-primary hover:text-primary transition-colors p-2"
                  >
                    <ImageIcon size={14} />
                  </button>
                  {onHistory && (
                    <button
                      type="button"
                      data-testid={`delivered-history-${v.id}`}
                      onClick={() => onHistory(v.id)}
                      title={t("vehicle_history")}
                      className="border border-border hover:border-primary hover:text-primary transition-colors p-2"
                    >
                      <History size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {/* ============ END DELIVERED VIEW ============ */}

      {editing && (
        <DeliveryEditModal
          vehicle={editing}
          mode={editMode}
          t={t}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onReload(); }}
        />
      )}

      {filesOpen && (
        <StepFilesModal
          vehicle={filesOpen.vehicle}
          step={filesOpen.step}
          t={t}
          onClose={() => setFilesOpen(null)}
          onChanged={onReload}
        />
      )}

      {progVehicle && (
        <VehicleScheduleModal
          vehicle={progVehicle}
          team={team}
          currentUser={currentUser}
          t={t}
          onClose={() => setProgVehicle(null)}
        />
      )}

      {lemonLawVehicle && (
        <LemonLawWarrantyModal
          vehicle={lemonLawVehicle}
          onClose={() => setLemonLawVehicle(null)}
        />
      )}
      {contractVid && (
        <ContractPrintModal vehicleId={contractVid} t={t} onClose={() => setContractVid(null)} />
      )}
    </div>
  );
}

function Pill({ label, value }) {
  return (
    <span className="bg-background border border-border px-3 py-1 text-xs">
      <span className="text-text-secondary">{label}:</span> <span className="font-medium">{value}</span>
    </span>
  );
}

/**
 * RevisionBadge — shows the pre-delivery revision status on a delivery card.
 *
 * Renders one of 3 chip variants based on `vehicle.revision.status`:
 *   • in_shop → yellow "REVISÃO: EM OFICINA" with a wrench icon
 *   • ready   → green  "REVISÃO: PRONTA"     with a checkmark icon
 *   • pending → orange "REVISÃO: AGUARDANDO" with a clock icon (car flagged
 *               for revision but not sent to the shop yet)
 * Any other status (none / missing) renders nothing so the card stays clean.
 */
function RevisionBadge({ revision, t }) {
  const status = (revision || {}).status;
  if (!status || status === "none") return null;
  const VARIANTS = {
    in_shop: {
      key: "revision_badge_in_shop",
      icon: Wrench,
      className: "border-warning bg-warning/15 text-warning",
    },
    ready: {
      key: "revision_badge_ready",
      icon: CheckCircle2,
      className: "border-success bg-success/15 text-success",
    },
    pending: {
      key: "revision_badge_pending",
      icon: Clock,
      className: "border-orange-500 bg-orange-500/15 text-orange-400",
    },
  };
  const v = VARIANTS[status];
  if (!v) return null;
  const Icon = v.icon;
  return (
    <span
      data-testid={`revision-badge-${status}`}
      className={`inline-flex items-center gap-1.5 mt-2 px-2 py-1 border text-[10px] font-display font-bold uppercase tracking-widest ${v.className}`}
    >
      <Icon size={11} />
      {t(v.key)}
    </span>
  );
}

function ReceiptsPanel({ v, t }) {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/vehicles/${v.id}/receipts`);
      setReceipts(r.data || []);
    } catch {
      setReceipts([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [v.id]);

  const totalReceived = receipts.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const openPdf = async (rid) => {
    try {
      const res = await api.get(`/receipts/${rid}/pdf`, { responseType: "blob" });
      openPdfBlob(res.data, `recibo-${rid.slice(0, 8)}.pdf`);
    } catch {
      toast.error(t("error_generic"));
    }
  };

  const [contractOpen, setContractOpen] = useState(false);

  const removeReceipt = async (rid) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.delete(`/receipts/${rid}`);
      toast.success(t("saved"));
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error_generic"));
    }
  };

  return (
    <div className="mb-4 border border-border bg-background" data-testid={`receipts-panel-${v.id}`}>
      <div className="flex items-center justify-between gap-3 px-3 py-2 bg-surface border-b border-border">
        <div className="flex items-center gap-2">
          <FileText size={14} className="text-primary" />
          <p className="label-eyebrow text-primary">{t("receipts")}</p>
          {receipts.length > 0 && (
            <>
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-border text-text-secondary">{receipts.length}</span>
              <span className="text-[10px] uppercase tracking-widest text-success font-display font-bold">+ {formatCurrency(totalReceived)}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            data-testid={`contract-pdf-${v.id}`}
            onClick={() => setContractOpen(true)}
            title={t("purchase_agreement")}
            className="text-[10px] uppercase tracking-widest px-3 py-1.5 bg-info/10 border border-info/40 text-info hover:bg-info hover:text-white inline-flex items-center gap-1 transition-colors font-display font-bold"
          >
            <FileSignature size={11} /> {t("purchase_agreement")}
          </button>
          {contractOpen && <ContractPrintModal vehicleId={v.id} t={t} onClose={() => setContractOpen(false)} />}
          <button
            type="button"
            data-testid={`new-receipt-${v.id}`}
            onClick={() => setCreating(true)}
            className="text-[10px] uppercase tracking-widest px-3 py-1.5 bg-success/15 border border-success/40 text-success hover:bg-success hover:text-white inline-flex items-center gap-1 transition-colors font-display font-bold"
          >
            <Plus size={11} /> {t("new_receipt")}
          </button>
        </div>
      </div>
      {loading ? (
        <p className="text-xs text-text-secondary text-center py-4">…</p>
      ) : receipts.length === 0 ? (
        <p className="text-xs text-text-secondary text-center py-4">{t("no_receipts_yet")}</p>
      ) : (
        <ul className="divide-y divide-border">
          {receipts.map(r => (
            <li key={r.id} data-testid={`receipt-${r.id}`} className="flex items-center gap-3 px-3 py-2 text-xs hover:bg-surface/40 transition-colors">
              <span className="font-mono text-text-secondary">#{r.receipt_no}</span>
              <span className="font-display font-bold text-success">{formatCurrency(r.amount)}</span>
              <span className="text-text-secondary truncate flex-1">
                {r.payment_method || "—"}
                {r.created_at && <span className="ml-2">· {new Date(r.created_at).toLocaleDateString()}</span>}
                {r.issued_by_name && <span className="ml-2">· {r.issued_by_name}</span>}
              </span>
              <button
                type="button"
                data-testid={`open-receipt-${r.id}`}
                onClick={() => openPdf(r.id)}
                title={t("open_pdf")}
                className="w-7 h-7 border border-success/40 text-success hover:bg-success hover:text-white flex items-center justify-center transition-colors"
              >
                <Printer size={12} />
              </button>
              <button
                type="button"
                data-testid={`del-receipt-${r.id}`}
                onClick={() => removeReceipt(r.id)}
                title={t("delete")}
                className="w-7 h-7 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
              >
                <Trash2 size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {creating && (
        <NewReceiptModal
          vehicle={v}
          t={t}
          onClose={() => setCreating(false)}
          onCreated={(rid) => { setCreating(false); reload(); openPdf(rid); }}
        />
      )}
    </div>
  );
}

function NewReceiptModal({ vehicle, t, onClose, onCreated }) {
  const [form, setForm] = useState({
    amount: "",
    payment_method: "Cash",
    customer_name: vehicle.buyer_name || "",
    customer_phone: vehicle.buyer_phone || "",
    customer_address: "",
    non_refundable_amount: 499,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!Number(form.amount) || Number(form.amount) <= 0) {
      toast.error(t("receipt_amount_required"));
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/vehicles/${vehicle.id}/receipts`, {
        vehicle_id: vehicle.id,
        amount: Number(form.amount),
        payment_method: form.payment_method,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_address: form.customer_address,
        non_refundable_amount: Number(form.non_refundable_amount) || 499,
        notes: form.notes,
      });
      toast.success(t("receipt_created"));
      onCreated(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error_generic"));
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-8 px-4"
      onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") { e.preventDefault(); e.stopPropagation(); submit(); } }}
    >
      <div className="bg-background border border-success w-full max-w-xl p-6 space-y-4" data-testid="new-receipt-modal">
        <div className="flex items-center justify-between">
          <div>
            <p className="label-eyebrow text-success mb-1">{t("new_receipt")}</p>
            <h2 className="font-display font-black text-xl uppercase tracking-tight">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </h2>
          </div>
          <button type="button" onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label-eyebrow block mb-2">{t("receipt_amount")}</label>
            <input
              data-testid="rcp-amount"
              type="number" min="0" step="0.01" required autoFocus
              value={form.amount}
              onChange={e => set("amount", e.target.value)}
              className="w-full bg-surface border border-success focus:border-success focus:outline-none px-3 h-14 text-lg font-display font-black"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t("payment_method")}</label>
            <select data-testid="rcp-method" value={form.payment_method} onChange={e => set("payment_method", e.target.value)}
              className="w-full bg-surface border border-border focus:border-success focus:outline-none px-3 h-11 text-sm">
              <option>Cash</option><option>Check</option><option>Card</option><option>Transfer</option><option>Zelle</option><option>Other</option>
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t("non_refundable_amount")}</label>
            <input
              data-testid="rcp-nonref" type="number" min="0" step="0.01"
              value={form.non_refundable_amount}
              onChange={e => set("non_refundable_amount", e.target.value)}
              className="w-full bg-surface border border-border focus:border-success focus:outline-none px-3 h-11 text-sm"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t("buyer_name")}</label>
            <input data-testid="rcp-name" value={form.customer_name} onChange={e => set("customer_name", e.target.value)}
              className="w-full bg-surface border border-border focus:border-success focus:outline-none px-3 h-11 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t("buyer_phone")}</label>
            <input data-testid="rcp-phone" value={form.customer_phone} onChange={e => set("customer_phone", e.target.value)}
              className="w-full bg-surface border border-border focus:border-success focus:outline-none px-3 h-11 text-sm" />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-2">{t("address")}</label>
            <input data-testid="rcp-address" value={form.customer_address} onChange={e => set("customer_address", e.target.value)}
              className="w-full bg-surface border border-border focus:border-success focus:outline-none px-3 h-11 text-sm" />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-border hover:border-primary text-xs font-display font-bold uppercase tracking-widest transition-colors">{t("cancel")}</button>
          <button type="button" onClick={submit} data-testid="rcp-submit" disabled={saving}
            className="bg-success hover:opacity-90 disabled:opacity-50 text-white px-5 py-2.5 text-xs font-display font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2">
            <Check size={14} /> {saving ? "..." : t("create_and_print")}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeliveryEditModal({ vehicle, mode, t, onClose, onSaved }) {
  const [step, setStep] = useState(vehicle.delivery_step || 1);
  const [bank, setBank] = useState(vehicle.bank_name || "");
  const [notes, setNotes] = useState(vehicle.delivery_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = mode === "step"
        ? { delivery_step: Number(step), bank_name: bank }
        : { delivery_notes: notes };
      await api.put(`/vehicles/${vehicle.id}`, payload);
      toast.success(t("saved"));
      onSaved();
    } catch { toast.error(t("error_generic")); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-12 px-4">
      <form onSubmit={save} className="bg-background border border-border w-full max-w-lg p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-xl uppercase tracking-tight">
            {mode === "step" ? t("edit_step") : t("notes")}
          </h2>
          <button type="button" onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>
        <p className="text-sm text-text-secondary">{vehicle.year} {vehicle.make} {vehicle.model} · {vehicle.buyer_name}</p>

        {mode === "step" ? (
          <>
            <div>
              <label className="label-eyebrow block mb-2">{t("status")}</label>
              <select
                data-testid="modal-step"
                value={step}
                onChange={(e) => setStep(e.target.value)}
                className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <option key={n} value={n}>{n}. {t(`step_${n}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">{t("bank_name_label")}</label>
              <input
                data-testid="modal-bank"
                type="text"
                value={bank}
                onChange={(e) => setBank(e.target.value)}
                className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm"
              />
            </div>
          </>
        ) : (
          <div>
            <label className="label-eyebrow block mb-2">{t("delivery_notes")}</label>
            <textarea
              data-testid="modal-notes"
              rows={6}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 py-2 text-sm resize-none"
            />
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-border hover:border-primary text-xs font-display font-bold uppercase tracking-widest transition-colors">{t("cancel")}</button>
          <button type="submit" data-testid="modal-save" disabled={saving} className="bg-primary hover:bg-primary-hover disabled:opacity-50 px-5 py-2.5 text-xs font-display font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2">
            <Check size={14} /> {saving ? "..." : t("save")}
          </button>
        </div>
      </form>
    </div>
  );
}

// FUNDED com entrada em aberto: o backend devolve 409 pro dono — oferece liberar mesmo assim.
async function putFundedWithOverride(vehicle_id, body, t) {
  try {
    await api.put(`/vehicles/${vehicle_id}/funded`, body);
    return true;
  } catch (err) {
    if (err.response?.status === 409 && body.funded) {
      const msg = err.response?.data?.detail || "";
      if (window.confirm(`${msg}\n\n${t("fund_override_confirm")}`)) {
        await api.put(`/vehicles/${vehicle_id}/funded`, { ...body, dp_override: true });
        return true;
      }
      return false;
    }
    throw err;
  }
}

function InlineSalespersonSelect({ value, salespeople, onChange, t, testid }) {
  const [editing, setEditing] = useState(false);
  const current = salespeople.find(s => s.id === value);
  // Mixed-store list (owner assigning cross-store) → show the store after the name
  const multiStore = new Set(salespeople.map(s => s.dealership_id)).size > 1;

  if (editing) {
    return (
      <select
        data-testid={testid}
        autoFocus
        value={value || ""}
        onChange={(e) => { setEditing(false); onChange(e.target.value); }}
        onBlur={() => setEditing(false)}
        className="w-44 bg-surface border border-primary focus:outline-none px-2 h-8 text-sm font-display font-bold"
      >
        <option value="">— {t("unassigned")} —</option>
        {salespeople.filter(s => s.active !== false).map(s => (
          <option key={s.id} value={s.id}>{s.name}{multiStore && s.store_name ? ` · ${s.store_name}` : ""}</option>
        ))}
      </select>
    );
  }
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => setEditing(true)}
      className="inline-flex items-center gap-1 hover:text-primary transition-colors text-left group"
      title={t("change_salesperson")}
    >
      {current ? (
        <NameWithAvatar
          name={current.name}
          photoUrl={current.photo_url}
          size="sm"
          className={`border-b border-dashed border-text-secondary/40 group-hover:border-primary font-display font-bold`}
        />
      ) : (
        <span className="border-b border-dashed border-text-secondary/40 group-hover:border-primary text-warning text-xs uppercase tracking-wider">
          {t("unassigned")}
        </span>
      )}
      <Edit2 size={10} className="opacity-50" />
    </button>
  );
}


function InlineMoneyEdit({ value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? 0));

  const commit = () => {
    setEditing(false);
    if (Number(draft) !== Number(value)) onSave(draft);
  };

  if (editing) {
    return (
      <input
        data-testid={testid}
        type="number"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { setDraft(String(value ?? 0)); setEditing(false); }
        }}
        className="w-24 bg-surface border border-primary focus:outline-none px-2 h-8 text-sm font-display font-bold text-right"
      />
    );
  }
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => { setDraft(String(value ?? 0)); setEditing(true); }}
      className="font-display font-bold hover:text-primary transition-colors border-b border-dashed border-text-secondary/40 hover:border-primary inline-flex items-center gap-1"
      title="Click to edit"
    >
      <DollarSign size={11} className="opacity-60" />
      {formatCurrency(Number(value) || 0)}
    </button>
  );
}

function SalespeopleTab({ salespeople, allSalespeople = null, t, onReload, isSalesperson, currentSpId, myDealerships = [], activeDealershipId }) {
  const spOptions = allSalespeople && allSalespeople.length ? allSalespeople : salespeople;
  const [editingSp, setEditingSp] = useState(null);
  const [payrollSp, setPayrollSp] = useState(null);
  const [credsFor, setCredsFor] = useState(null); // salesperson object whose credentials are being edited
  const [accessFor, setAccessFor] = useState(null); // salesperson whose store-access is being edited
  const [transferSp, setTransferSp] = useState(null); // salesperson being moved to another store
  const [regPromptRow, setRegPromptRow] = useState(null); // sale row whose RMV is missing (inline modal to fill)
  const [periodFilter, setPeriodFilter] = useState("all");
  const [selectedSp, setSelectedSp] = useState(isSalesperson ? (currentSpId || "all") : "all");
  const [report, setReport] = useState({ rows: [], by_salesperson: [], total_sales: 0, total_revenue: 0, total_profit: 0 });
  const [credentialsMap, setCredentialsMap] = useState({}); // { spId: {has_login, login_email} }
  const [showBySp, setShowBySp] = useState(false); // collapsible "Por vendedor" table

  const loadReport = async () => {
    const params = {};
    const now = new Date();
    if (periodFilter === "this_month") {
      params.year = now.getFullYear();
      params.month = now.getMonth() + 1;
    } else if (periodFilter === "last_month") {
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      params.year = lm.getFullYear();
      params.month = lm.getMonth() + 1;
    }
    try {
      const r = await api.get("/sales-report", { params });
      setReport(r.data);
    } catch { /* noop */ }
  };

  const loadCreds = async () => {
    if (isSalesperson) return;
    try {
      const r = await api.get("/salespeople/credentials");
      setCredentialsMap(r.data || {});
    } catch { /* noop */ }
  };

  useEffect(() => { loadReport(); /* eslint-disable-next-line */ }, [periodFilter, salespeople.length]);
  useEffect(() => { loadCreds(); /* eslint-disable-next-line */ }, [salespeople.length]);

  const removeSp = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.delete(`/salespeople/${id}`);
      toast.success(t("saved"));
      onReload();
    } catch { toast.error(t("error_generic")); }
  };

  const togglePaid = async (vehicle_id, currentlyPaid) => {
    if (isSalesperson) return; // read-only for salespeople
    try {
      await api.put(`/vehicles/${vehicle_id}`, { commission_paid: !currentlyPaid });
      toast.success(t("saved"));
      loadReport();
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error_generic"));
    }
  };

  const toggleFunded = async (vehicle_id, currentlyFunded) => {
    if (isSalesperson) return;
    try {
      const ok = await putFundedWithOverride(vehicle_id, { funded: !currentlyFunded }, t);
      if (!ok) return;
      toast.success(currentlyFunded ? (t("funded_undone") || "FUNDED removido") : (t("funded_marked") || "FUNDED marcado"));
      loadReport();
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error_generic"));
    }
  };

  const updateCommissionAmount = async (vehicle_id, value) => {
    if (isSalesperson) return;
    const n = Number(value);
    if (Number.isNaN(n) || n < 0) { toast.error(t("error_generic")); return; }
    try {
      await api.put(`/vehicles/${vehicle_id}`, { commission_amount: n });
      toast.success(t("saved"));
      loadReport();
      onReload();
    } catch { toast.error(t("error_generic")); }
  };

  const updateSalesperson = async (vehicle_id, sp_id) => {
    if (isSalesperson) return;
    const sp = salespeople.find(s => s.id === sp_id);
    const payload = {
      salesperson_id: sp_id || "",
      salesperson_name: sp ? sp.name : "",
    };
    // When switching to a real salesperson, snapshot their default commission if current row has 0
    try {
      const v = await api.get(`/vehicles/${vehicle_id}`).then(r => r.data);
      if (sp && (Number(v.commission_amount) || 0) === 0) {
        payload.commission_amount = Number(sp.commission_amount) || 0;
      }
    } catch { /* noop */ }
    try {
      await api.put(`/vehicles/${vehicle_id}`, payload);
      toast.success(t("saved"));
      loadReport();
      onReload();
    } catch { toast.error(t("error_generic")); }
  };

  // Salespeople list visible to current viewer
  // Hide inactive salespeople from the main view; salespeople with zero sales
  // and active=false (e.g. someone added by mistake or who left) should not
  // pollute the sales report.
  const visibleSalespeople = isSalesperson
    ? salespeople.filter(s => s.id === currentSpId)
    : salespeople.filter(s => s.active !== false);

  return (
    <div data-testid="salespeople-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="label-eyebrow text-primary mb-2">{t("salespeople")}</p>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">{t("sales_report")}</h1>
        </div>
        {!isSalesperson && (
          <button
            data-testid="add-salesperson"
            onClick={() => setEditingSp({})}
            className="bg-primary hover:bg-primary-hover px-5 py-3 font-display font-bold uppercase text-xs tracking-widest inline-flex items-center gap-2"
          >
            <Plus size={14} /> {t("add_salesperson")}
          </button>
        )}
      </div>

      {/* Period filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: "all", label: t("all_time") },
          { id: "this_month", label: t("this_month") },
          { id: "last_month", label: t("last_month") },
        ].map((p) => (
          <button
            key={p.id}
            data-testid={`filter-${p.id}`}
            onClick={() => setPeriodFilter(p.id)}
            className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-widest border transition-colors ${
              periodFilter === p.id ? "border-primary text-primary" : "border-border text-text-secondary hover:text-white"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Top-level stats removed by user request — kept the period filter + tables only */}

      {/* Salespeople list with their performance — collapsible to keep page tidy */}
      <div className="border border-border mb-10">
        <button
          type="button"
          data-testid="toggle-by-salesperson"
          onClick={() => setShowBySp(s => !s)}
          className="w-full bg-surface px-4 py-3 border-b border-border flex items-center justify-between gap-3 hover:bg-surface/70 transition-colors group"
        >
          <span className="flex items-center gap-3">
            <Users size={16} className="text-primary" />
            <span className="label-eyebrow text-primary">{t("by_salesperson")}</span>
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 border border-border text-text-secondary group-hover:border-primary group-hover:text-primary transition-colors">
              {visibleSalespeople.length}
            </span>
          </span>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-text-secondary group-hover:text-primary transition-colors">
            {showBySp ? t("collapse") : t("expand")}
            {showBySp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </span>
        </button>
        {showBySp && (
          visibleSalespeople.length === 0 && report.by_salesperson.length === 0 ? (
            <div className="text-center py-14">
              <Users size={36} className="mx-auto text-text-secondary/30 mb-3" />
              <p className="text-text-secondary text-sm">{t("no_salespeople")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr>
                  <th className="text-left p-3 label-eyebrow">{t("salesperson")}</th>
                  <th className="text-left p-3 label-eyebrow">{t("commission_amount")}</th>
                  <th className="text-right p-3 label-eyebrow">{t("sales_count")}</th>
                  <th className="text-right p-3 label-eyebrow">{t("commission_paid")}</th>
                  <th className="text-right p-3 label-eyebrow">{t("commission_pending")}</th>
                  {!isSalesperson && <th className="text-left p-3 label-eyebrow">{t("login_email")}</th>}
                <th className="text-right p-3 label-eyebrow"></th>
              </tr>
            </thead>
            <tbody>
              {visibleSalespeople.map((sp) => {
                const stats = report.by_salesperson.find(b => b.salesperson_id === sp.id) || { count: 0, commission_paid_total: 0, commission_pending_total: 0, commission_paid_count: 0, commission_pending_count: 0 };
                const cred = credentialsMap[sp.id];
                return (
                  <tr
                    key={sp.id}
                    data-testid={`sp-row-${sp.id}`}
                    onClick={() => setSelectedSp(sp.id)}
                    className={`border-b border-border cursor-pointer transition-colors ${selectedSp === sp.id ? "bg-primary/10" : "hover:bg-surface"}`}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <Avatar src={sp.photo_url} name={sp.name} size="md" />
                        <div className="min-w-0">
                          <p className="font-display font-bold">{sp.name}</p>
                          <p className="text-xs text-text-secondary">{sp.phone || sp.email || ""}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 font-display font-bold">{formatCurrency(sp.commission_amount || 0)}</td>
                    <td className="p-3 text-right font-display font-bold">{stats.count}</td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1.5 justify-end">
                        <CheckCircle2 size={14} className="text-success" />
                        <span className="font-display font-bold text-success">{formatCurrency(stats.commission_paid_total)}</span>
                        {stats.commission_paid_count > 0 && <span className="text-xs text-text-secondary">({stats.commission_paid_count})</span>}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1.5 justify-end">
                        <Clock size={14} className="text-warning" />
                        <span className="font-display font-bold text-warning">{formatCurrency(stats.commission_pending_total)}</span>
                        {stats.commission_pending_count > 0 && <span className="text-xs text-text-secondary">({stats.commission_pending_count})</span>}
                      </div>
                    </td>
                    {!isSalesperson && (
                      <td className="p-3 text-xs">
                        {cred?.has_login ? (
                          <span className="text-success font-mono" data-testid={`sp-login-${sp.id}`}>{cred.login_email}</span>
                        ) : (
                          <span className="text-text-secondary uppercase tracking-wider">{t("no_login")}</span>
                        )}
                      </td>
                    )}
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
                        {!isSalesperson && (
                          <button
                            data-testid={`creds-sp-${sp.id}`}
                            onClick={() => setCredsFor(sp)}
                            title={cred?.has_login ? t("edit_login") : t("set_login")}
                            className={`w-8 h-8 border flex items-center justify-center transition-colors ${cred?.has_login ? "border-success text-success hover:bg-success/10" : "border-border hover:border-primary hover:text-primary"}`}
                          >
                            <Users size={14} />
                          </button>
                        )}
                        {!isSalesperson && (
                          <>
                            <button
                              data-testid={`close-week-sp-${sp.id}`}
                              onClick={() => setPayrollSp(sp)}
                              title="Fechar semana (gerar recibo de pagamento)"
                              className="h-8 px-3 border border-success/40 text-success hover:bg-success hover:text-white transition-colors text-[10px] font-display font-bold uppercase tracking-widest inline-flex items-center gap-1"
                            >
                              <Calendar size={11} /> Fechar Semana
                            </button>
                            {myDealerships.length > 1 && (
                              <button
                                data-testid={`access-sp-${sp.id}`}
                                onClick={() => setAccessFor(sp)}
                                title="Acesso multi-loja"
                                className="w-8 h-8 border border-border hover:border-brand hover:text-brand flex items-center justify-center transition-colors"
                              >
                                <Package size={14} />
                              </button>
                            )}
                            {myDealerships.length > 1 && (
                              <button
                                data-testid={`transfer-sp-${sp.id}`}
                                onClick={() => setTransferSp(sp)}
                                title="Transferir de loja"
                                className="w-8 h-8 border border-border hover:border-brand hover:text-brand flex items-center justify-center transition-colors"
                              >
                                <Truck size={14} />
                              </button>
                            )}
                            <button data-testid={`edit-sp-${sp.id}`} onClick={() => setEditingSp(sp)} className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"><Edit2 size={14} /></button>
                            <button
                              data-testid={`toggle-active-sp-${sp.id}`}
                              onClick={async () => {
                                const willInactivate = sp.active !== false;
                                if (willInactivate && !window.confirm(`Inativar ${sp.name}? Ele(a) sai do relatório de vendas mas as vendas históricas continuam intactas.`)) return;
                                try {
                                  await api.put(`/salespeople/${sp.id}`, { active: !willInactivate });
                                  toast.success(willInactivate ? "Inativado" : "Reativado");
                                  onReload();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || t("error_generic"));
                                }
                              }}
                              title={sp.active !== false ? "Inativar (esconder do relatório)" : "Reativar"}
                              className="w-8 h-8 border border-border hover:border-warning hover:text-warning flex items-center justify-center transition-colors"
                            >
                              {sp.active !== false ? <X size={14} /> : <Check size={14} />}
                            </button>
                            <button data-testid={`del-sp-${sp.id}`} onClick={() => removeSp(sp.id)} className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"><Trash2 size={14} /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {/* Unassigned row — owner only */}
              {!isSalesperson && report.by_salesperson.find(b => !b.salesperson_id) && (() => {
                const u = report.by_salesperson.find(b => !b.salesperson_id);
                return (
                  <tr className="border-b border-border bg-warning/5">
                    <td className="p-3">
                      <p className="font-display font-bold text-warning">{t("unassigned")}</p>
                    </td>
                    <td className="p-3">—</td>
                    <td className="p-3 text-right font-display font-bold">{u.count}</td>
                    <td className="p-3 text-right">—</td>
                    <td className="p-3 text-right">—</td>
                    <td></td>
                    <td></td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
            </div>
          )
        )}
      </div>

      {/* Detailed sales spreadsheet */}
      <div className="border border-border">
        <div className="bg-surface px-4 py-3 border-b border-border flex flex-wrap items-center gap-3">
          <p className="label-eyebrow text-primary">{t("detailed_sales")}</p>
        </div>

        {/* Salesperson filter pills — owner only (salesperson sees only their own sales) */}
        {!isSalesperson && (
          <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-border bg-surface/30">
            <button
              type="button"
              data-testid="filter-sp-all"
              onClick={() => setSelectedSp("all")}
              className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-widest border transition-colors ${
                selectedSp === "all" ? "border-primary text-primary bg-primary/10" : "border-border text-text-secondary hover:text-white"
              }`}
            >
              {t("all_time")} ({report.rows.length})
            </button>
            {salespeople.map((sp) => {
              const cnt = report.rows.filter(r => r.salesperson_id === sp.id).length;
              return (
                <button
                  key={sp.id}
                  type="button"
                  data-testid={`filter-sp-${sp.id}`}
                  onClick={() => setSelectedSp(sp.id)}
                  className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-widest border transition-colors ${
                    selectedSp === sp.id ? "border-primary text-primary bg-primary/10" : "border-border text-text-secondary hover:text-white"
                  }`}
                >
                  {sp.name} ({cnt})
                </button>
              );
            })}
            {report.rows.some(r => !r.salesperson_id) && (
              <button
                type="button"
                data-testid="filter-sp-unassigned"
                onClick={() => setSelectedSp("unassigned")}
                className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-widest border transition-colors ${
                  selectedSp === "unassigned" ? "border-warning text-warning bg-warning/10" : "border-border text-text-secondary hover:text-white"
                }`}
              >
                {t("unassigned")} ({report.rows.filter(r => !r.salesperson_id).length})
              </button>
            )}
          </div>
        )}

        {(() => {
          const filteredRows = report.rows.filter((r) => {
            if (isSalesperson) return true; // backend already filters to own sales
            if (selectedSp === "all") return true;
            if (selectedSp === "unassigned") return !r.salesperson_id;
            return r.salesperson_id === selectedSp;
          });
          // Subtotals for the filtered selection
          const subtotalRevenue = filteredRows.reduce((s, r) => s + r.sold_price, 0);
          const paidCount = filteredRows.filter(r => r.commission_paid).length;
          const pendingCount = filteredRows.filter(r => !r.commission_paid).length;
          const selectedSpName = selectedSp === "all" ? t("all_time") : selectedSp === "unassigned" ? t("unassigned") : (salespeople.find(p => p.id === selectedSp)?.name || "");

          return (
            <>
              {/* Subtotals card when a specific salesperson is selected — owner view only */}
              {!isSalesperson && selectedSp !== "all" && filteredRows.length > 0 && (
                <div className="px-4 py-3 border-b border-border bg-surface/50 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="label-eyebrow mb-1">{selectedSpName}</p>
                    <p className="font-display font-bold">{filteredRows.length} {t("sales_count")}</p>
                  </div>
                  <div>
                    <p className="label-eyebrow mb-1">{t("total_revenue")}</p>
                    <p className="font-display font-bold text-primary">{formatCurrency(subtotalRevenue)}</p>
                  </div>
                  <div>
                    <p className="label-eyebrow mb-1">{t("commission_paid")}</p>
                    <p className="font-display font-bold text-success">{paidCount}</p>
                  </div>
                  <div>
                    <p className="label-eyebrow mb-1">{t("commission_pending")}</p>
                    <p className="font-display font-bold text-warning">{pendingCount}</p>
                  </div>
                </div>
              )}

              {filteredRows.length === 0 ? (
                <p className="text-text-secondary text-sm text-center py-12">—</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-border">
                      <tr>
                        <th className="text-left p-3 label-eyebrow">{t("day_of_month")}</th>
                        <th className="text-left p-3 label-eyebrow">{t("sale_date")}</th>
                        <th className="text-left p-3 label-eyebrow">{t("make")}/{t("model")}</th>
                        <th className="text-left p-3 label-eyebrow">{t("buyer_name")}</th>
                        {!isSalesperson && <th className="text-left p-3 label-eyebrow">{t("salesperson")}</th>}
                        <th className="text-right p-3 label-eyebrow">{t("commission_amount")}</th>
                        <th className="text-center p-3 label-eyebrow">{t("funded") || "Funded"}</th>
                        <th className="text-center p-3 label-eyebrow">{t("paid")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((r) => (
                        <tr key={r.vehicle_id} data-testid={`sale-${r.vehicle_id}`} className="border-b border-border hover:bg-surface transition-colors">
                          <td className="p-3 font-display font-black text-primary text-2xl text-center w-16">{r.day || "—"}</td>
                          <td className="p-3 text-xs text-text-secondary">{r.sold_at ? new Date(r.sold_at).toLocaleDateString() : "—"}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {r.image && <img src={r.image} alt="" className="w-10 h-8 object-cover" />}
                              <div>
                                <p className="font-display font-bold">{r.make} {r.model}</p>
                                <p className="text-xs text-text-secondary">{r.year}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3">{r.buyer_name || "—"}</td>
                          {!isSalesperson && (
                            <td className="p-3">
                              <InlineSalespersonSelect
                                value={r.salesperson_id || ""}
                                salespeople={spOptions}
                                onChange={(spId) => updateSalesperson(r.vehicle_id, spId)}
                                t={t}
                                testid={`change-sp-${r.vehicle_id}`}
                              />
                            </td>
                          )}
                          <td className="p-3 text-right">
                            {isSalesperson ? (
                              <span className="font-display font-bold">{formatCurrency(r.commission_amount || 0)}</span>
                            ) : (
                              <InlineMoneyEdit
                                value={r.commission_amount || 0}
                                onSave={(val) => updateCommissionAmount(r.vehicle_id, val)}
                                testid={`commission-edit-${r.vehicle_id}`}
                              />
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {(() => {
                              const rmvMissing = !r.funded && !(Number(r.registration_cost) > 0);
                              return (
                                <>
                                  <button
                                    type="button"
                                    data-testid={`toggle-funded-${r.vehicle_id}`}
                                    onClick={() => {
                                      if (isSalesperson) return;
                                      // Owner is about to turn on FUNDED but the vehicle is missing its RMV cost
                                      // (Massachusetts registration + title + tax). Open the inline mini-modal instead
                                      // of letting the backend reject with a toast — this way the owner fixes it in
                                      // one click without leaving the salespeople dashboard.
                                      if (rmvMissing) { setRegPromptRow(r); return; }
                                      toggleFunded(r.vehicle_id, r.funded);
                                    }}
                                    disabled={isSalesperson}
                                    title={
                                      isSalesperson
                                        ? ""
                                        : rmvMissing
                                          ? "Emplacamento pendente — preencha ou funde sem ele"
                                          : (r.funded ? (t("unmark_funded") || "Desmarcar FUNDED") : (t("mark_funded") || "Marcar FUNDED — banco pagou"))
                                    }
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 border transition-colors text-xs font-display font-black uppercase tracking-wider ${
                                      r.funded
                                        ? "border-success bg-success text-white hover:bg-success/80"
                                        : rmvMissing
                                          ? "border-warning/60 text-warning hover:border-warning hover:bg-warning/10"
                                          : "border-border text-text-secondary hover:border-success hover:text-success"
                                    } ${isSalesperson ? "cursor-default" : ""}`}
                                  >
                                    {r.funded ? <CheckCircle2 size={14} /> : (rmvMissing ? <AlertTriangle size={14} /> : <DollarSign size={14} />)}
                                    {r.funded ? "FUNDED" : (rmvMissing ? "RMV?" : (t("not_funded") || "Não"))}
                                  </button>
                                  {rmvMissing && !isSalesperson && (
                                    <p className="text-[9px] uppercase tracking-widest text-warning mt-1">
                                      Falta emplacamento
                                    </p>
                                  )}
                                  {!r.funded && !rmvMissing && !isSalesperson && (
                                    <p data-testid={`rmv-ok-${r.vehicle_id}`} className="text-[9px] uppercase tracking-widest text-text-secondary mt-1">
                                      RMV {formatCurrency(r.registration_cost)} ✓
                                    </p>
                                  )}
                                  {r.funded && !(Number(r.registration_cost) > 0) && !isSalesperson && (
                                    <p data-testid={`rmv-pending-${r.vehicle_id}`} className="text-[9px] uppercase tracking-widest text-warning mt-1">
                                      Falta emplacamento
                                    </p>
                                  )}
                                  {r.funded && r.funded_at && (
                                    <p data-testid={`funded-at-${r.vehicle_id}`} className="text-[10px] text-text-secondary mt-1">
                                      {new Date(r.funded_at).toLocaleDateString()}
                                    </p>
                                  )}
                                </>
                              );
                            })()}
                          </td>
                          <td className="p-3 text-center">
                            <button
                              type="button"
                              data-testid={`toggle-paid-${r.vehicle_id}`}
                              onClick={() => !isSalesperson && togglePaid(r.vehicle_id, r.commission_paid)}
                              disabled={isSalesperson || (!r.funded && !r.commission_paid)}
                              title={
                                isSalesperson
                                  ? ""
                                  : !r.funded && !r.commission_paid
                                    ? (t("commission_blocked_until_funded") || "Marque FUNDED primeiro")
                                    : (r.commission_paid ? t("mark_unpaid") : t("mark_paid"))
                              }
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 border transition-colors text-xs font-display font-bold uppercase tracking-wider ${
                                r.commission_paid
                                  ? "border-success text-success hover:bg-success/10"
                                  : !r.funded
                                    ? "border-border text-text-secondary/50 cursor-not-allowed"
                                    : "border-warning text-warning hover:bg-warning/10"
                              } ${isSalesperson ? "cursor-default" : ""}`}
                            >
                              {r.commission_paid ? <CheckCircle2 size={14} /> : <Clock size={14} />}
                              {r.commission_paid ? t("paid") : t("pending")}
                            </button>
                            {r.commission_paid && r.commission_paid_at && (
                              <p data-testid={`paid-at-${r.vehicle_id}`} className="text-[10px] text-text-secondary mt-1">
                                {new Date(r.commission_paid_at).toLocaleDateString()}
                              </p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* Non-commissioned employees (mechanics, BDC, detailer, office, etc.)
          Owner-only — non-owner roles never reach this tab. */}
      {!isSalesperson && <EmployeesSection t={t} />}

      {editingSp && (
        <SalespersonForm sp={editingSp.id ? editingSp : null} t={t} onClose={() => setEditingSp(null)} onSaved={() => { setEditingSp(null); onReload(); }} />
      )}
      {payrollSp && (
        <WeeklyPayrollModal
          salesperson={payrollSp}
          onClose={() => setPayrollSp(null)}
          onSaved={() => { loadReport(); onReload(); }}
        />
      )}

      {credsFor && (
        <SalespersonCredentialsModal
          sp={credsFor}
          t={t}
          existing={credentialsMap[credsFor.id]}
          onClose={() => setCredsFor(null)}
          onSaved={() => { setCredsFor(null); loadCreds(); }}
        />
      )}

      {accessFor && (
        <SalespersonAccessModal
          sp={accessFor}
          stores={myDealerships}
          onClose={() => setAccessFor(null)}
          onSaved={() => { setAccessFor(null); onReload(); }}
        />
      )}

      {transferSp && (
        <SalespersonTransferModal
          sp={transferSp}
          stores={myDealerships}
          onClose={() => setTransferSp(null)}
          onSaved={() => { setTransferSp(null); onReload(); }}
        />
      )}

      {regPromptRow && (
        <RegistrationPromptModal
          row={regPromptRow}
          onClose={() => setRegPromptRow(null)}
          onSaved={async () => {
            // After the RMV is filled, immediately mark the vehicle as FUNDED
            // — that's what the owner was trying to do when the button flagged.
            const vid = regPromptRow.vehicle_id;
            setRegPromptRow(null);
            try {
              const ok = await putFundedWithOverride(vid, { funded: true }, t);
              if (ok) toast.success("Emplacamento salvo e FUNDED marcado");
              loadReport();
              onReload();
            } catch (e) {
              toast.error(e.response?.data?.detail || t("error_generic"));
              loadReport();
            }
          }}
          onSkip={async () => {
            // Commission sometimes needs to go out before the car is registered
            // — fund now, fill the RMV later (row keeps the amber reminder).
            const vid = regPromptRow.vehicle_id;
            setRegPromptRow(null);
            try {
              const ok = await putFundedWithOverride(vid, { funded: true }, t);
              if (ok) toast.success("FUNDED marcado — lembre de preencher o emplacamento depois");
              loadReport();
              onReload();
            } catch (e) {
              toast.error(e.response?.data?.detail || t("error_generic"));
              loadReport();
            }
          }}
        />
      )}
    </div>
  );
}

function SalespersonForm({ sp, t, onClose, onSaved }) {
  const [form, setForm] = useState(sp || { name: "", commission_amount: 0, salary_weekly: 0, phone: "", email: "", active: true });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      commission_amount: Number(form.commission_amount) || 0,
      salary_weekly: Number(form.salary_weekly) || 0,
    };
    try {
      if (sp) await api.put(`/salespeople/${sp.id}`, payload);
      else await api.post("/salespeople", payload);
      toast.success(t("saved"));
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || t("error_generic")); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-12 px-4">
      <form onSubmit={save} className="bg-background border border-border w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl uppercase tracking-tight">{sp ? t("edit") : t("add_salesperson")}</h2>
          <button type="button" onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>
        <Input label={t("salesperson_name")} value={form.name} set={(v) => set("name", v)} required testid="sp-name" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label={t("commission_amount")} type="number" value={form.commission_amount} set={(v) => set("commission_amount", v)} testid="sp-commission" />
          <Input label={t("phone")} value={form.phone} set={(v) => set("phone", v)} testid="sp-phone" />
        </div>
        <Input label={t("email")} type="email" value={form.email} set={(v) => set("email", v)} testid="sp-email" />
        <Input label="Salário semanal (US$)" type="number" value={form.salary_weekly ?? 0} set={(v) => set("salary_weekly", v)} testid="sp-salary-weekly" />
        <label className="flex items-center gap-2 cursor-pointer">
          <input data-testid="sp-active" type="checkbox" checked={form.active !== false} onChange={(e) => set("active", e.target.checked)} className="w-4 h-4 accent-primary" />
          <span className="label-eyebrow">{t("active")}</span>
        </label>
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-border hover:border-primary text-xs font-display font-bold uppercase tracking-widest transition-colors">{t("cancel")}</button>
          <button type="submit" data-testid="sp-submit" disabled={saving} className="bg-primary hover:bg-primary-hover disabled:opacity-50 px-5 py-2.5 text-xs font-display font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2">
            <Check size={14} /> {saving ? "..." : t("save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function SalespersonCredentialsModal({ sp, t, existing, onClose, onSaved }) {
  const [email, setEmail] = useState(existing?.login_email || sp.email || "");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t("error_generic"));
      return;
    }
    setSaving(true);
    try {
      await api.post(`/salespeople/${sp.id}/credentials`, { email, password });
      toast.success(t("saved"));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally {
      setSaving(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm(t("confirm_revoke_login"))) return;
    setSaving(true);
    try {
      await api.delete(`/salespeople/${sp.id}/credentials`);
      toast.success(t("saved"));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-12 px-4">
      <form onSubmit={save} className="bg-background border border-border w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="label-eyebrow text-primary mb-1">{sp.name}</p>
            <h2 className="font-display font-black text-xl uppercase tracking-tight">
              {existing?.has_login ? t("edit_login") : t("set_login")}
            </h2>
          </div>
          <button type="button" onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>
        <p className="text-xs text-text-secondary leading-relaxed border-l-2 border-primary pl-3">
          {t("salesperson_login_hint")}
        </p>
        <Input label={t("login_email")} type="email" value={email} set={setEmail} required testid="creds-email" />
        <Input label={t("new_password")} type="password" value={password} set={setPassword} required testid="creds-password" />
        <div className="flex justify-between gap-3 pt-4 border-t border-border">
          {existing?.has_login ? (
            <button type="button" data-testid="creds-revoke" onClick={revoke} disabled={saving} className="px-5 py-2.5 border border-primary text-primary hover:bg-primary/10 disabled:opacity-50 text-xs font-display font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2">
              <Trash2 size={14} /> {t("revoke_login")}
            </button>
          ) : <span />}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-border hover:border-primary text-xs font-display font-bold uppercase tracking-widest transition-colors">{t("cancel")}</button>
            <button type="submit" data-testid="creds-submit" disabled={saving} className="bg-primary hover:bg-primary-hover disabled:opacity-50 px-5 py-2.5 text-xs font-display font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2">
              <Check size={14} /> {saving ? "..." : t("save")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}


/**
 * DocumentChecklistSection — sub-UI for Step 3 (Documentações).
 *
 * Replaces the generic single-zone uploader with a checklist of 5 mandatory
 * document buckets. Each bucket has its own upload zone + delete + preview,
 * and renders a green check when at least one file is present (visual cue
 * that the document is "captured").
 *
 * Files are stored on the existing `step_files["3"]` array. Each file gets a
 * `subcategory` field that maps to the bucket id. Files without a subcategory
 * (uploaded before this UI existed) fall into the "other" bucket so nothing
 * is lost.
 */
function DocumentChecklistSection({ files, uploading, onUpload, onPreview, onDelete, t }) {
  const BUCKETS = [
    { id: "drive",      labelKey: "doc_drive" },
    { id: "insurance",  labelKey: "doc_insurance" },
    { id: "income",     labelKey: "doc_income" },
    { id: "address",    labelKey: "doc_address" },
    { id: "other",      labelKey: "doc_other" },
  ];
  // Partition existing files into the right buckets (files without subcategory
  // — i.e. uploaded before this UI shipped — go into "other" so nothing leaks).
  const byBucket = Object.fromEntries(BUCKETS.map((b) => [b.id, []]));
  for (const f of files) {
    const sub = (f.subcategory || "").toLowerCase();
    if (byBucket[sub]) byBucket[sub].push(f);
    else byBucket.other.push(f);
  }

  return (
    <div className="p-6 max-h-[calc(90vh-220px)] overflow-y-auto space-y-3">
      {BUCKETS.map((b) => {
        const items = byBucket[b.id];
        const done = items.length > 0;
        const inputId = `doc-upload-${b.id}`;
        return (
          <div
            key={b.id}
            data-testid={`doc-bucket-${b.id}`}
            className={`border ${done ? "border-success/50 bg-success/[0.04]" : "border-border"} p-4`}
          >
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="inline-flex items-center gap-2">
                <span
                  className={`w-5 h-5 border-2 inline-flex items-center justify-center shrink-0 ${
                    done ? "bg-success border-success text-white" : "border-border text-text-secondary"
                  }`}
                >
                  {done ? <Check size={12} /> : null}
                </span>
                <p className="font-display font-bold text-sm uppercase tracking-widest">
                  {t(b.labelKey)}
                </p>
                {done && (
                  <span className="text-[10px] uppercase tracking-widest text-success font-display font-bold">
                    {items.length} {t("doc_uploaded")}
                  </span>
                )}
              </div>
              <label
                htmlFor={inputId}
                data-testid={`doc-add-${b.id}`}
                className={`inline-flex items-center gap-1.5 px-3 h-9 border text-[10px] uppercase tracking-widest font-display font-bold cursor-pointer transition-colors ${
                  uploading
                    ? "border-border opacity-50 cursor-not-allowed"
                    : "border-primary text-primary hover:bg-primary hover:text-white"
                }`}
              >
                <Upload size={11} /> {t("doc_add_file")}
              </label>
              <input
                id={inputId}
                type="file"
                multiple
                accept="image/*,application/pdf,.pdf,.doc,.docx"
                disabled={uploading}
                onChange={(e) => {
                  const list = Array.from(e.target.files || []);
                  e.target.value = ""; // reset so re-selecting same file fires onChange
                  onUpload(list, b.id);
                }}
                className="hidden"
              />
            </div>

            {items.length === 0 ? (
              <p className="text-[11px] text-text-secondary italic">
                {t("doc_no_files_yet")}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {items.map((f) => {
                  const isImage = (f.type || "").startsWith("image/");
                  // Backwards-compat: new files use `url` (Cloudinary), legacy
                  // ones use `data_url` (base64). Fall back gracefully.
                  const src = f.url || f.data_url;
                  return (
                    <div
                      key={f.id}
                      data-testid={`doc-file-${f.id}`}
                      className="border border-border bg-background p-2 flex items-center gap-2"
                    >
                      <button
                        type="button"
                        onClick={() => isImage && onPreview(f)}
                        className={`w-12 h-12 bg-surface flex items-center justify-center shrink-0 ${
                          isImage ? "cursor-zoom-in hover:opacity-80" : ""
                        }`}
                      >
                        {isImage ? (
                          <img src={src} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <FileText size={20} className="text-text-secondary" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate" title={f.name}>{f.name}</p>
                        <div className="flex gap-1 mt-1">
                          <button
                            type="button"
                            data-testid={`doc-download-${f.id}`}
                            onClick={() => openStoredFile(f, t)}
                            className="text-[9px] px-1.5 py-0.5 border border-border hover:border-primary hover:text-primary uppercase tracking-widest"
                          >
                            <Download size={10} />
                          </button>
                          <button
                            type="button"
                            onClick={() => onDelete(f.id)}
                            className="text-[9px] px-1.5 py-0.5 border border-border hover:border-primary hover:text-primary uppercase tracking-widest"
                          >
                            <Trash2 size={10} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function StepFilesModal({ vehicle, step, t, onClose, onChanged }) {
  const [files, setFiles] = useState(vehicle.step_files?.[String(step)] || []);
  const [notes, setNotes] = useState(vehicle.step_notes?.[String(step)] || "");
  const [savedNotes, setSavedNotes] = useState(vehicle.step_notes?.[String(step)] || "");
  const [uploading, setUploading] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [previewing, setPreviewing] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.get(`/vehicles/${vehicle.id}`);
      setFiles(r.data.step_files?.[String(step)] || []);
      const n = r.data.step_notes?.[String(step)] || "";
      setNotes(n);
      setSavedNotes(n);
    } catch { /* noop */ }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      // Merge with existing step_notes from vehicle (other steps untouched)
      const existing = { ...(vehicle.step_notes || {}) };
      existing[String(step)] = notes;
      await api.put(`/vehicles/${vehicle.id}`, { step_notes: existing });
      setSavedNotes(notes);
      toast.success(t("saved"));
      onChanged();
    } catch { toast.error(t("error_generic")); }
    finally { setSavingNotes(false); }
  };

  const handleUpload = async (fileList, subcategory = "") => {
    if (!fileList?.length) return;
    setUploading(true);
    for (const file of fileList) {
      // Cloudinary free tier allows up to 20MB per file; keep a safe margin.
      if (file.size > 20 * 1024 * 1024) {
        toast.error(t("file_too_large"));
        continue;
      }
      try {
        // 1) Ask the backend for a signed upload URL scoped to this dealership.
        const sig = (await api.get("/cloudinary/signature", { params: { folder: "delivery/" } })).data;
        // 2) Upload the raw file directly to Cloudinary (bypasses the 8MB
        //    Mongo doc limit + keeps the base64 out of our DB).
        const fd = new FormData();
        fd.append("file", file);
        fd.append("api_key", sig.api_key);
        fd.append("timestamp", sig.timestamp);
        fd.append("signature", sig.signature);
        fd.append("folder", sig.folder);
        const cloudRes = await fetch(
          `https://api.cloudinary.com/v1_1/${sig.cloud_name}/auto/upload`,
          { method: "POST", body: fd },
        );
        const cloudJson = await cloudRes.json();
        if (!cloudJson.secure_url) {
          throw new Error(cloudJson.error?.message || "upload failed");
        }
        // 3) Persist the reference on the vehicle doc.
        await api.post(`/vehicles/${vehicle.id}/step-files-cloud/${step}`, {
          name: file.name,
          type: file.type || "application/octet-stream",
          url: cloudJson.secure_url,
          public_id: cloudJson.public_id,
          size: file.size,
          subcategory: subcategory || "",
        });
        toast.success(t("saved"));
      } catch (err) {
        toast.error(err.response?.data?.detail || err.message || t("error_generic"));
      }
    }
    setUploading(false);
    await refresh();
    onChanged();
  };

  const remove = async (fileId) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.delete(`/vehicles/${vehicle.id}/step-files/${step}/${fileId}`);
      toast.success(t("saved"));
      await refresh();
      onChanged();
    } catch { toast.error(t("error_generic")); }
  };

  const isImage = (f) => (f.type || "").startsWith("image/");
  const formatSize = (b) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil((b || 0) / 1024)} KB`;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-12 px-4">
      <div className="bg-background border border-border w-full max-w-2xl">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <p className="label-eyebrow text-primary mb-1">{step === 8 ? t("delivery_photos") : `${t("upload_for_step")} ${step}`}</p>
            <h2 className="font-display font-bold text-xl uppercase tracking-tight">{t(`step_${step}`)}</h2>
            <p className="text-xs text-text-secondary mt-1">{vehicle.year} {vehicle.make} {vehicle.model} · {vehicle.buyer_name}</p>
          </div>
          <button onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>

        {step === 8 && (
          <div className="px-6 pt-5 -mb-2">
            <p className="text-xs text-text-secondary leading-relaxed border-l-2 border-success pl-3">
              {t("delivery_photos_hint")}
            </p>
          </div>
        )}

        {/* Notes section */}
        <div className="px-6 pt-6">
          <div className="flex items-center justify-between mb-3">
            <label className="label-eyebrow text-primary">{t("step_notes_label")}</label>
            {notes !== savedNotes && (
              <button
                type="button"
                data-testid="save-step-notes"
                onClick={saveNotes}
                disabled={savingNotes}
                className="bg-primary hover:bg-primary-hover disabled:opacity-50 px-4 py-1.5 text-[10px] font-display font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2"
              >
                <Check size={12} /> {savingNotes ? "..." : t("save_notes")}
              </button>
            )}
          </div>
          <textarea
            data-testid="step-notes-input"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("step_notes_placeholder")}
            className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 py-2 text-sm resize-none"
          />
        </div>

        {/* Upload area — Step 3 (Documentações) uses a checklist with 5 buckets,
            every other step uses the generic single-zone upload. */}
        {step === 3 ? (
          <DocumentChecklistSection
            files={files}
            uploading={uploading}
            onUpload={handleUpload}
            onPreview={setPreviewing}
            onDelete={remove}
            t={t}
          />
        ) : (
        <>
        <div className="p-6">
          <label
            data-testid="upload-dropzone"
            htmlFor={`upload-${step}`}
            className="block border-2 border-dashed border-border hover:border-primary transition-colors p-8 text-center cursor-pointer"
          >
            <Upload size={28} className="mx-auto text-text-secondary mb-3" />
            <p className="text-sm text-text-secondary">{t("drag_drop")}</p>
            <p className="text-xs text-text-secondary mt-1">PNG, JPG, PDF · {t("file_too_large").replace("(", "").replace(")", "").toLowerCase()}</p>
          </label>
          <input
            id={`upload-${step}`}
            data-testid="upload-input"
            type="file"
            multiple
            accept="image/*,application/pdf,.pdf,.doc,.docx"
            onChange={(e) => handleUpload(Array.from(e.target.files || []))}
            className="hidden"
          />
          {uploading && <p className="text-center text-text-secondary text-sm mt-3">...</p>}
        </div>

        {/* Files list */}
        <div className="px-6 pb-6 max-h-96 overflow-y-auto">
          {files.length === 0 ? (
            <p className="text-text-secondary text-sm text-center py-8">{t("no_files_yet")}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {files.map((f) => {
                // Backwards-compat: legacy files use `data_url` (base64),
                // new files use `url` (Cloudinary). Always prefer the new one.
                const src = f.url || f.data_url;
                return (
                <div key={f.id} data-testid={`file-${f.id}`} className="border border-border bg-surface p-3 flex gap-3 items-start">
                  {isImage(f) && src ? (
                    <button
                      type="button"
                      onClick={() => setPreviewing(f)}
                      className="w-16 h-16 bg-background flex-shrink-0 overflow-hidden cursor-pointer"
                    >
                      <img src={src} alt={f.name} className="w-full h-full object-cover" />
                    </button>
                  ) : (
                    <div className="w-16 h-16 bg-background flex-shrink-0 flex items-center justify-center">
                      <FileIcon size={24} className="text-text-secondary" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" title={f.name}>{f.name}</p>
                    <p className="text-xs text-text-secondary">{formatSize(f.size)}</p>
                    <div className="flex gap-1 mt-2">
                      <button
                        type="button"
                        onClick={() => openStoredFile(f, t)}
                        data-testid={`download-${f.id}`}
                        className="text-[10px] px-2 py-1 border border-border hover:border-primary hover:text-primary uppercase tracking-wider transition-colors inline-flex items-center gap-1"
                      >
                        <Download size={11} /> {t("download")}
                      </button>
                      <button
                        data-testid={`del-file-${f.id}`}
                        onClick={() => remove(f.id)}
                        className="text-[10px] px-2 py-1 border border-border hover:border-primary hover:text-primary uppercase tracking-wider transition-colors"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
        </>
        )}

        <div className="border-t border-border p-4 flex justify-end">
          <button onClick={onClose} className="px-5 py-2.5 border border-border hover:border-primary text-xs font-display font-bold uppercase tracking-widest transition-colors">{t("cancel")}</button>
        </div>
      </div>

      {previewing && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-8" onClick={() => setPreviewing(null)}>
          <button onClick={() => setPreviewing(null)} className="absolute top-6 right-6 text-white"><X size={28} /></button>
          <img src={previewing.url || previewing.data_url} alt={previewing.name} className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}

function ImportUrlModal({ t, onClose, onImported }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetchUrl = async (e) => {
    e?.preventDefault();
    if (!url) return;
    setLoading(true);
    setPreview(null);
    try {
      const r = await api.post("/vehicles/import-url", { url });
      const data = r.data.extracted;
      if (!data.image && !data.title) {
        toast.error(t("import_failed"));
      } else {
        setPreview(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("import_failed"));
    } finally { setLoading(false); }
  };

  const useThis = () => {
    onImported(preview);
    toast.success(t("import_success"));
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-auto py-12 px-4">
      <div className="bg-background border border-border w-full max-w-xl">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="font-display font-bold text-xl uppercase tracking-tight">{t("import_from_url")}</h2>
          <button onClick={onClose}><X size={20} className="text-text-secondary hover:text-primary" /></button>
        </div>

        <form onSubmit={fetchUrl} className="p-6 space-y-4">
          <div>
            <label className="label-eyebrow block mb-2">URL</label>
            <input
              data-testid="import-url-input"
              type="url"
              required
              autoFocus
              placeholder={t("paste_url")}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-4 h-11 text-sm"
            />
            <p className="text-xs text-text-secondary mt-2">Ex: https://www.intercarautosales.com/details/used-2019-honda-civic/125084579</p>
          </div>

          <button
            type="submit"
            data-testid="import-url-fetch"
            disabled={loading || !url}
            className="w-full bg-primary hover:bg-primary-hover disabled:opacity-50 transition-colors py-3 font-display font-bold uppercase text-sm tracking-widest text-white"
          >
            {loading ? t("importing") : t("import")}
          </button>
        </form>

        {preview && (
          <div className="px-6 pb-6">
            <div className="border border-border bg-surface p-4">
              <p className="label-eyebrow text-primary mb-3">Preview</p>
              <div className="flex gap-4">
                {preview.image ? (
                  <img src={preview.image} alt="" className="w-32 h-24 object-cover bg-background flex-shrink-0" />
                ) : (
                  <div className="w-32 h-24 bg-background flex items-center justify-center"><Car size={28} className="text-text-secondary" /></div>
                )}
                <div className="flex-1 min-w-0 text-sm space-y-1">
                  <p className="font-display font-bold mb-1 truncate">{preview.title || "—"}</p>
                  {preview.year ? <p className="text-text-secondary">{preview.year} {preview.make} {preview.model}</p> : null}
                  {preview.price ? <p className="font-display font-bold text-primary">{formatCurrency(preview.price)}</p> : null}
                  {preview.mileage ? <p className="text-xs text-success font-display font-bold inline-flex items-center gap-1"><Gauge size={11} /> {preview.mileage.toLocaleString()} mi</p> : null}
                  {preview.vin ? <p className="text-xs text-text-secondary font-mono">VIN {preview.vin}</p> : null}
                  {preview.images?.length > 1 ? (
                    <p className="text-xs text-success">{preview.images.length} {t("photos_imported")}</p>
                  ) : null}
                </div>
              </div>
              {preview.images?.length > 1 && (
                <div className="grid grid-cols-6 gap-1.5 mt-3">
                  {preview.images.slice(0, 12).map((src, i) => (
                    <img key={i} src={src} alt="" className="w-full h-12 object-cover bg-background border border-border" />
                  ))}
                </div>
              )}
              <button
                type="button"
                data-testid="import-url-use"
                onClick={useThis}
                className="w-full mt-4 bg-success hover:opacity-80 transition-opacity py-2.5 font-display font-bold uppercase text-xs tracking-widest text-background"
              >
                <Check size={14} className="inline mr-2" /> {t("save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Input({ label, value, set, type = "text", required, testid }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">
        {label}
        {required && <span className="text-primary ml-1" aria-hidden="true">*</span>}
      </label>
      <input data-testid={testid} type={type} required={required} value={value} onChange={(e) => set(e.target.value)} className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm" />
    </div>
  );
}

function Select({ label, value, set, options, testid }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => set(e.target.value)} className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm cursor-pointer">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

/**
 * Registration cost field — plain manual input (the old MA tax auto-calculator
 * was removed at the owner's request for both stores).
 */
function RegistrationField({ t, value, salePrice, tradeInValue, onChange, inPrice = false, onInPriceChange = null }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">🧾 {t("registration_cost")}</label>
      <input
        data-testid="f-registration"
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm"
      />
      {onInPriceChange && (
        <label
          className={`mt-2 flex items-start gap-2 p-2 border cursor-pointer transition-colors text-[11px] ${
            inPrice
              ? "border-success/60 bg-success/[0.06] text-white"
              : "border-border bg-surface/40 text-text-secondary hover:border-success/40"
          }`}
        >
          <input
            type="checkbox"
            data-testid="f-reg-in-price"
            checked={!!inPrice}
            onChange={(e) => onInPriceChange(e.target.checked)}
            className="mt-0.5 accent-success"
          />
          <span>
            <span className={`font-display font-bold uppercase tracking-wider ${inPrice ? "text-success" : ""}`}>
              {t("reg_in_price_label") || "Incluir no preço do carro"}
            </span>
            <br />
            <span className="text-text-secondary text-[10px] leading-tight">
              {inPrice
                ? (t("reg_in_price_on_hint") || "Loja absorveu o emplacamento → será lançado como despesa do veículo.")
                : (t("reg_in_price_off_hint") || "Cliente paga separado → não conta como despesa (passthrough).")}
            </span>
          </span>
        </label>
      )}
    </div>
  );
}

/**
 * Select with a non-selectable placeholder ("Selecione...") shown when value
 * is empty. Used for payment_method / bank_name so the user has to make an
 * explicit choice instead of silently inheriting the first option.
 */
function SelectWithPlaceholder({ label, value, set, options, testid, placeholder }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <select
        data-testid={testid}
        value={value || ""}
        onChange={(e) => set(e.target.value)}
        className={`w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm cursor-pointer ${value ? "" : "text-text-secondary"}`}
      >
        <option value="" disabled>{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
